import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Hero, Item, ItemData, TimelinePoint, Match, Player } from '../types/types';
import { DotaService } from '../services/DotaService';
import ItemDropdown from './ItemDropdown';
import { isUpgradeItem } from '../constants/ItemCategories';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTimeContext } from '../contexts/TimeContext';
import AdBanner from './AdBanner';
import { v4 as uuidv4 } from 'uuid';
import GameDataLog from './GameDataLog';

interface Props {
  radiantHeroes: Hero[];
  direHeroes: Hero[];
  matchId: string;
  sessionId: string;
  mapComponent?: React.ReactNode;
}

// Add KDA interface
interface KDAStats {
  kills: number;
  deaths: number;
  assists: number;
}

// Define a new interface for combat events (kills and deaths)
interface CombatEvent {
  time: number;
  heroId: number;
  isKill: boolean;
  victimId?: number;
  victimName?: string;
}

// Augment Player and Match types with properties used in this component
interface ExtendedPlayer extends Player {
  gold_t?: number[];  // Net worth over time 
  current_gold_t?: number[]; // Current gold over time (unspent)
  kda?: KDAStats;
  kills?: number;
  deaths?: number;
  assists?: number;
  isRadiant?: boolean;
}

interface ExtendedMatch extends Match {
  objectives?: Array<{
    time: number;
    type: string;
    team: number;
  }>;
  radiant_team?: {
    team_id?: number;
    name?: string;
    tag?: string;
    logo_url?: string;
  };
  dire_team?: {
    team_id?: number;
    name?: string;
    tag?: string;
    logo_url?: string;
  };
  // Add picks_bans field for draft information
  picks_bans?: Array<{
    is_pick: boolean;
    hero_id: number;
    team: number; // 0 for Radiant, 1 for Dire
    order: number;
  }>;
}

// Generate timeline points for every minute up to game duration
const generateTimelinePoints = (durationInSeconds: number): TimelinePoint[] => {
  const points: TimelinePoint[] = [];
  const durationInMinutes = Math.ceil(durationInSeconds / 60);
  
  for (let min = 0; min <= durationInMinutes; min++) {
    points.push({
      minutes: min,
      label: `${min}:00`
    });
  }
  
  return points;
};

// Update the getItemImageUrl function with direct, working image URLs
const getItemImageUrl = (itemName: string, imgProperty?: string): string => {
  console.log(`Getting image URL for item: "${itemName}", imgProperty: "${imgProperty}"`);
  
  // Direct Steam CDN URLs - most reliable source
  const steamCdnUrls: Record<string, string> = {
    // Basic items
    'Blink Dagger': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/blink_lg.png',
    'Boots of Speed': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/boots_lg.png',
    'Magic Wand': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/magic_wand_lg.png',
    'Magic Stick': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/magic_stick_lg.png',
    'Healing Salve': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/flask_lg.png',
    'Tango': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/tango_lg.png',
    'Wraith Band': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/wraith_band_lg.png',
    'Soul Ring': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/soul_ring_lg.png',
    'Ring of Basilius': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/ring_of_basilius_lg.png',
    'Circlet': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/circlet_lg.png',
    'Slippers of Agility': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/slippers_lg.png',
    'Band of Elvenskin': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/boots_of_elves_lg.png',
    'Robe of the Magi': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/robe_lg.png',
    'Ogre Axe': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/ogre_axe_lg.png',
    'Blade of Alacrity': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/blade_of_alacrity_lg.png',
    'Staff of Wizardry': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/staff_of_wizardry_lg.png', 
    'Orb of Venom': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/orb_of_venom_lg.png',
    'Ring of Protection': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/ring_of_protection_lg.png',
    'Infused Raindrops': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/infused_raindrop_lg.png',
    'Wind Lace': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/wind_lace_lg.png',
    'Ring of Regen': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/ring_of_regen_lg.png',
    'Sage\'s Mask': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/sobi_mask_lg.png',
    'Bracer': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/bracer_lg.png',
    'Null Talisman': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/null_talisman_lg.png',
    'Iron Branch': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/branches_lg.png',
    'Gauntlets of Strength': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/gauntlets_lg.png',
    'Phase Boots': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/phase_boots_lg.png',
    'Power Treads': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/power_treads_lg.png',
    'Clarity': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/clarity_lg.png',
    'Observer Ward': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/ward_observer_lg.png',
    'Quelling Blade': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/quelling_blade_lg.png',
    'Ring of Health': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/ring_of_health_lg.png',
    'Void Stone': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/void_stone_lg.png',
    'Morbid Mask': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/lifesteal_lg.png',
    'Energy Booster': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/energy_booster_lg.png',
    'Vitality Booster': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/vitality_booster_lg.png',
    'Point Booster': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/point_booster_lg.png',
    'Platemail': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/platemail_lg.png',
    'Talisman of Evasion': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/talisman_of_evasion_lg.png',
    'Hyperstone': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/hyperstone_lg.png',
    'Ultimate Orb': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/ultimate_orb_lg.png',
    'Demon Edge': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/demon_edge_lg.png',
    'Mystic Staff': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/mystic_staff_lg.png',
    'Reaver': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/reaver_lg.png',
    'Eaglesong': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/eagle_lg.png',
    'Sacred Relic': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/relic_lg.png',
    'Quarterstaff': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/quarterstaff_lg.png',
    'Claymore': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/claymore_lg.png',
    'Javelin': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/javelin_lg.png',
    'Broadsword': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/broadsword_lg.png',
    'Blades of Attack': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/blades_of_attack_lg.png',
    'Chainmail': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/chainmail_lg.png',
    'Cloak': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/cloak_lg.png',
    'Gloves of Haste': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/gloves_lg.png',
    
    // Upgrades tab items from the user's image
    'Black King Bar': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/black_king_bar_lg.png',
    'Aghanim\'s Scepter': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/ultimate_scepter_lg.png',
    'Boots of Travel': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/travel_boots_lg.png',
    'Butterfly': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/butterfly_lg.png',
    'Eye of Skadi': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/skadi_lg.png',
    'Daedalus': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/greater_crit_lg.png',
    'Heart of Tarrasque': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/heart_lg.png',
    'Assault Cuirass': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/assault_lg.png',
    'Manta Style': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/manta_lg.png',
    'Satanic': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/satanic_lg.png',
    'Desolator': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/desolator_lg.png',
    'Ethereal Blade': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/ethereal_blade_lg.png',
    'Blade Mail': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/blade_mail_lg.png',
    'Battle Fury': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/bfury_lg.png',
    'Refresher Orb': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/refresher_lg.png',
    'Refresher Shard': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/refresher_shard_lg.png',
    'Diffusal Blade': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/diffusal_blade_lg.png',
    'Dagon': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/dagon_lg.png',
    'Shiva\'s Guard': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/shivas_guard_lg.png',
    'Shadow Blade': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/invis_sword_lg.png',
    'Bloodstone': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/bloodstone_lg.png',
    'Linken\'s Sphere': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/sphere_lg.png',
    'Vanguard': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/vanguard_lg.png',
    'Crimson Guard': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/crimson_guard_lg.png',
    'Monkey King Bar': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/monkey_king_bar_lg.png',
    'Dragon Lance': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/dragon_lance_lg.png',
    'Orchid Malevolence': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/orchid_lg.png',
    'Force Staff': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/force_staff_lg.png',
    'Hurricane Pike': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/hurricane_pike_lg.png',
    'Armlet of Mordiggian': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/armlet_lg.png',
    'Hand of Midas': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/hand_of_midas_lg.png',
    'Eul\'s Scepter of Divinity': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/cyclone_lg.png',
    'Scythe of Vyse': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/sheepstick_lg.png',
    'Abyssal Blade': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/abyssal_blade_lg.png',
    'Skull Basher': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/basher_lg.png',
    'Solar Crest': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/solar_crest_lg.png',
    'Guardian Greaves': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/guardian_greaves_lg.png',
    'Heaven\'s Halberd': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/heavens_halberd_lg.png',
    'Aether Lens': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/aether_lens_lg.png',
    'Silver Edge': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/silver_edge_lg.png',
    'Glimmer Cape': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/glimmer_cape_lg.png',
    'Aeon Disk': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/aeon_disk_lg.png',
    'Lotus Orb': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/lotus_orb_lg.png',
    'Maelstrom': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/maelstrom_lg.png',
    'Mjollnir': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/mjollnir_lg.png',
    'Octarine Core': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/octarine_core_lg.png',
    'Radiance': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/radiance_lg.png',
    'Mekansm': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/mekansm_lg.png',
    'Pipe of Insight': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/pipe_lg.png',
    'Helm of the Dominator': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/helm_of_the_dominator_lg.png',
    'Spirit Vessel': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/spirit_vessel_lg.png',
    'Meteor Hammer': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/meteor_hammer_lg.png',
    'Kaya': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/kaya_lg.png',
    'Sange': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/sange_lg.png',
    'Yasha': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/yasha_lg.png',
    'Kaya and Sange': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/kaya_and_sange_lg.png',
    'Sange and Yasha': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/sange_and_yasha_lg.png',
    'Yasha and Kaya': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/yasha_and_kaya_lg.png',
    'Nullifier': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/nullifier_lg.png',
    'Crystalys': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/lesser_crit_lg.png',
    'Falcon Blade': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/falcon_blade_lg.png',
    'Aghanim\'s Blessing': 'https://media-hosting.imagekit.io/339faaafed074b6d/Aghanim\'s_Blessing_itemicon_dota2_gameasset.png?Expires=1839669340&Key-Pair-Id=K2ZIVPTIP2VGHC&Signature=sdVqgKX~80v7o16pFh6zfI6nzdl-xj~R6QEQwfAFzBfD3BnZiY9Hp9SdTHhHit9S5Co3tTmEuMgqjj-NUqpXVRpMRYYgjyVIZsaGQo92hxV9TjUe9Sww9yq0hJnacTTlQVxaiR4ECg9pd-8vtP0e8n9CWXcmTIWOHPBWP0MENZ0Wv791zfLnT9qF9Y9dYatRa4snkNh7GdfsesT2EMDj-4wqan0iznSXSPraSgScZDoxHycO2h9o2gZKkewpPy-PxgcvNMO44lPkUs9ES1esPelsS3rH2BaAd5-4X9ioCp-s10qb2AvS-6MvVZfsLL60bGrgln5TCTPUUzzGkggLuA__',
    'Echo Sabre': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/echo_sabre_lg.png',
    'Witch Blade': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/witch_blade_lg.png',
    'Arcane Boots': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/arcane_boots_lg.png',
    'Tranquil Boots': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/tranquil_boots_lg.png',
    'Aghanim\'s Shard': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/aghanims_shard_lg.png',
    
    // New items from user images
    'Faerie Fire': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/faerie_fire_lg.png',
    'Smoke of Deceit': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/smoke_of_deceit_lg.png',
    'Fluffy Hat': 'https://media-hosting.imagekit.io/1baa39d0cb0143b1/Fluffy_Hat_itemicon_dota2_gameasset.png?Expires=1839669048&Key-Pair-Id=K2ZIVPTIP2VGHC&Signature=fRWNrjvPBqv4Wwyn9lVJCRYPtxymJjwm0m3rgmu-MWSTXzU04nRAMoX8k-UzNYcYVR~FxrAeVJdxjSKUgwd5irvwGIWPR9--Y4VO36XjyucpRXL5X1XpNLISn-ADJSYGbzDDuR6r5Wtjzjkjfh2GhfFFFrPmixR7EGK~KCKYAin6XYXzr76ZLnE90x6w5iE9ySjMGd4M2ydUcTcDtFjnAyDvdM5NKRx~CyS9~zPI4ggLoHURrc8YGMDlKqFxlT3tQnM27FbTfIrhY7vK-JGC1fRHxnMNoCwtW294KUb2iP0SQlGJHQhyTZuGXi4nGgWPUZCm31mIsmQJ-3S-YNwCyg__',
    'Sentry Ward': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/ward_sentry_lg.png',
    'Blight Stone': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/blight_stone_lg.png',
    'Enchanted Mango': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/enchanted_mango_lg.png',
    'Gem of True Sight': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/gem_lg.png',
    'Crown': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/crown_lg.png',
    'Ghost Scepter': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/ghost_lg.png',
    'Helm of Iron Will': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/helm_of_iron_will_lg.png',
    'Shadow Amulet': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/shadow_amulet_lg.png',
    'Dust of Appearance': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/dust_lg.png',
    'Mithril Hammer': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/mithril_hammer_lg.png',
    'Cornucopia': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/cornucopia_lg.png',
    'Blood Grenade': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/blood_grenade_lg.png',
    'Bottle': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/bottle_lg.png',
    'Diadem': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/diadem_lg.png',
    'Orb of Frost': 'https://media-hosting.imagekit.io/2358b90543184f00/Orb_of_Frost_itemicon_dota2_gameasset.png?Expires=1839669343&Key-Pair-Id=K2ZIVPTIP2VGHC&Signature=XhqrP5zz1h-FMwidSMFHVDU2z59oHDAbnM3X3~uOFoqCcImCAKRJUaEc-ZHkckGWlk7s5hSVxpL2-IawPAN8CEA4HV7jfPjAMB5QV~suQ8Yk2WIxAsgRbuNXZxMFh3NPkUsrluxP1UGv7wo~xqtNPhuomxuGAdt-RX~bgfGf79JNud8TwzTGwSl30-GSLfgzNDgdy-DJjpcntdSvGbs5se0lRrtsSHTUXtIo0-FD2ydFDqM6ec8T4DpbtRLl5q-F3jrbcbJfQuvXQN9DrEKPKNGfqLpqIwYtUSpbJ6a5M7Vz0~LV4K~XMKySZJuoIbK8SJq5jvlxv~A1ezKDu7U3Cg__',
    'Blitz Knuckles': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/blitz_knuckles_lg.png',
    'Tiara of Selemene': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/tiara_of_selemene_lg.png',
    'Voodoo Mask': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/voodoo_mask_lg.png',
    
    // New items from user images (continued)
    'Orchid': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/orchid_lg.png',
    'Maelevo': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/orchid_lg.png',
    'Eul\'s Scepter': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/cyclone_lg.png',
    
    // Additional items from the wiki images
    'Mask of Madness': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/mask_of_madness_lg.png',
    'Moon Shard': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/moon_shard_lg.png',
    
    // Add any specific items here that need to be included
    'Parasma': 'https://media-hosting.imagekit.io/eec3a5d62b7c45d4/Parasma_itemicon_dota2_gameasset.png?Expires=1839669046&Key-Pair-Id=K2ZIVPTIP2VGHC&Signature=JFGB3FhT-KDpPeVWCQYtIVGHcCbdFVpx~c~8ju6kFteqLnCsg6PxTwuA9DLUhDs82-B10VZ3UBU6xyuPVoIC9VCrVxkZhSDRiIDLZSo7DzqyAPi6qWMQku4GoSmN82sjN2VFToIoplbGqh7bxm0b31I0QOl9l5XoxmVXAWTTtfBIIJlPmkCk57IflqsOkmiSGd3ehbQ7oXpR~R3dDDRbJ6HPveNjfYAICLVFMVQE0FBdoM3VRuJIjhpiO0P8iTw0vSrji~JAAsofV6rYV7rYa62mcqp7MPeU9A0sbkePXldmR3GAMkU4E~YC0YjVzJK-C4FKZ8q1ZH0Ii-ASWauBlg__',
    'Swift Blink': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/swift_blink_lg.png',
    'Helm of the Overlord': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/helm_of_the_overlord_lg.png',
    'Gleipnir': 'https://media-hosting.imagekit.io/ceec2364eb5f49c3/Gleipnir_itemicon_dota2_gameasset.png?Expires=1839669052&Key-Pair-Id=K2ZIVPTIP2VGHC&Signature=lxJbl3pa0ymepd5p0LObe9GB3-KICSODkMsLdsrp~PfGJ8JplKoSD3iD~gY8EVM6DlIX2UGVsMsXIZd6DqLr0kYDQZvTpE~T-nMFwmN6PylAaP8rX69ULW1dQaDRGQRDdTd16ew77XB0rBaLBwbRb4YAooKPBcZu8u2bKblo3V2ivbfK1LTzlRAosyndO2EBHRwgbEiVpd2RuqfNp06ZfFdMEkSFI4NQac9WdGT0pKbVWaO-BLMs7d307jOZK3hk52m~LcfyNcJ5kvseoxl-u-e4c1PXbMdtsggF6W-E0k84qMYomt8ArNBNAxr47dmw1ESBHsS7APgnOPC--jZxUA__',
    'Rod of Atos': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/rod_of_atos_lg.png',
    'Wind Waker': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/wind_waker_lg.png',
    'Eternal Shroud': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/eternal_shroud_lg.png',
    'Soul Booster': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/soul_booster_lg.png',
    'Bloodthorn': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/bloodthorn_lg.png',
    'Disperser': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/disperser_lg.png',
    'Divine Rapier': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/rapier_lg.png',
    'Khanda': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/khanda_lg.png',
    'Revenant\'s Brooch': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/revenants_brooch_lg.png',
    'Arcane Blink': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/arcane_blink_lg.png',
    'Harpoon': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/harpoon_lg.png',
    'Mage Slayer': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/mage_slayer_lg.png',
    'Overwhelming Blink': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/overwhelming_blink_lg.png',
    'Phylactery': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/phylactery_lg.png',
    'Drum of Endurance': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/ancient_janggo_lg.png',
    'Pavise': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/pavise_lg.png',
    'Observer and Sentry Wards': 'https://media-hosting.imagekit.io/9b5b1ecec07c4169/Observer_and_Sentry_Wards_1_itemicon_dota2_gameasset.png?Expires=1839749073&Key-Pair-Id=K2ZIVPTIP2VGHC&Signature=mekHEUKi10zJbdkUqy9~AUbgj9Rl17g5RvdVC~r~oIZ5V-0FBbrCyv~2rLODoZdhaIXm51yv0Cm-OD3QPejh2DULBXbaK2JjGU2Y4Ay314awkGBMR0j8G22HeJMrQYQxOFKqkvYm5G6mNjbOR8E26XQfcMtGiKInqSyOznMrrjDCLyGS7Omr-pIIXIDFAjrulCD6wOBZ~6rRagfdBamWuOcuH~t2GQ-l2jZaiP5x0lBaVTY5W-SPoUwYkrpChFq0rOkutPRaPSP88dryDICUowjI0IS-So5IN46FhX5AbrofjdwJpMBLkbinwwEZaMTDwUMwaEt4fOtGEnNMD-ObcA__',
  };
  
  // First try direct name lookup
  if (steamCdnUrls[itemName]) {
    console.log(`Found direct URL mapping for ${itemName}: ${steamCdnUrls[itemName]}`);
    return steamCdnUrls[itemName];
  }
  
  // Special case handling for known problematic items
  const specialCases: Record<string, string> = {
    'Shadow Blade': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/invis_sword_lg.png',
    'Band of Elvenskin': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/boots_of_elves_lg.png',
    'Morbid Mask': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/lifesteal_lg.png',
    'Tranquil Boots': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/tranquil_boots_lg.png',
    'Aghanim\'s Shard': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/aghanims_shard_lg.png',
    'Eul\'s Scepter of Divinity': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/cyclone_lg.png',
    'Fluffy Hat': 'https://media-hosting.imagekit.io/1baa39d0cb0143b1/Fluffy_Hat_itemicon_dota2_gameasset.png?Expires=1839669048&Key-Pair-Id=K2ZIVPTIP2VGHC&Signature=fRWNrjvPBqv4Wwyn9lVJCRYPtxymJjwm0m3rgmu-MWSTXzU04nRAMoX8k-UzNYcYVR~FxrAeVJdxjSKUgwd5irvwGIWPR9--Y4VO36XjyucpRXL5X1XpNLISn-ADJSYGbzDDuR6r5Wtjzjkjfh2GhfFFFrPmixR7EGK~KCKYAin6XYXzr76ZLnE90x6w5iE9ySjMGd4M2ydUcTcDtFjnAyDvdM5NKRx~CyS9~zPI4ggLoHURrc8YGMDlKqFxlT3tQnM27FbTfIrhY7vK-JGC1fRHxnMNoCwtW294KUb2iP0SQlGJHQhyTZuGXi4nGgWPUZCm31mIsmQJ-3S-YNwCyg__',
    'Orb of Frost': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/frost_orb_lg.png',
    'Vladmir\'s Offering': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/vladmir_lg.png',
    'Perseverance': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/pers_lg.png',
    'Eul\'s Scepter': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/cyclone_lg.png',
    'Parasma': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/prism_lg.png',
    'Tiara of Selemene': 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/tiara_of_selemene_lg.png'
  };
  
  // Check special cases
  if (specialCases[itemName]) {
    console.log(`Found special case URL mapping for ${itemName}: ${specialCases[itemName]}`);
    return specialCases[itemName];
  }
  
  // Try alternate ways to find the item
  // 1. Try with lowercase
  const lowerCaseName = itemName.toLowerCase();
  for (const [key, url] of Object.entries(steamCdnUrls)) {
    if (key.toLowerCase() === lowerCaseName) {
      console.log(`Found URL mapping using lowercase match for ${itemName}: ${url}`);
      return url;
    }
  }
  
  // 2. Try with partial match
  for (const [key, url] of Object.entries(steamCdnUrls)) {
    if (key.toLowerCase().includes(lowerCaseName) || lowerCaseName.includes(key.toLowerCase())) {
      console.log(`Found URL mapping using partial match for ${itemName} with key ${key}: ${url}`);
      return url;
    }
  }
  
  // If we have the img property from the API, use it as a fallback
  if (imgProperty) {
    const generatedUrl = `https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/${imgProperty}_lg.png`;
    console.log(`No direct URL mapping for ${itemName}, trying generated URL: ${generatedUrl}`);
    return generatedUrl;
  }
  
  // Generate a URL from the item name as a last resort
  const formattedItemName = itemName.toLowerCase().replace(/[^a-z0-9]/g, '_');
  const generatedUrl = `https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/${formattedItemName}_lg.png`;
  console.log(`No direct URL mapping for ${itemName}, trying generated URL: ${generatedUrl}`);
  
  return generatedUrl;
};

const ItemizationSimulation: React.FC<Props> = ({ radiantHeroes, direHeroes, matchId, sessionId, mapComponent }) => {
  // State management
  const [selectedTeam, setSelectedTeam] = useState<string>('radiant');
  const [userItems, setUserItems] = useState<Map<number, Map<number, Item>>>(new Map());
  const [spentGold, setSpentGold] = useState<Map<number, number>>(new Map());
  const [itemData, setItemData] = useState<Record<number, ItemData>>({});
  const [matchData, setMatchData] = useState<ExtendedMatch | null>(null);
  const [playerData, setPlayerData] = useState<Record<number, ExtendedPlayer>>({});
  const [errorMessages, setErrorMessages] = useState<Map<number, string>>(new Map());
  
  // Generate a unique client ID to identify this session
  const clientIdRef = useRef<string>(uuidv4());
  // Track the last updated hero and slot to avoid refreshing other heroes' slots
  const lastUpdatedRef = useRef<{heroId: number, slotIndex: number | undefined}>({ heroId: -1, slotIndex: undefined });
  
  // Use the shared time context instead of local state
  const {
    currentTimePoint,
    setCurrentTimePoint,
    gameDuration,
    setGameDuration,
    isPlaying,
    setIsPlaying,
    gameObjectives,
    setGameObjectives
  } = useTimeContext();
  
  // Combat events
  const [combatEvents, setCombatEvents] = useState<CombatEvent[]>([]);
  
  // Calculate currentCombatEvents - moved here from line ~2272
  const currentCombatEvents = useMemo(() => {
    return combatEvents.filter(event => {
      const eventMinute = Math.floor(event.time / 60);
      return eventMinute === currentTimePoint;
    });
  }, [combatEvents, currentTimePoint]);
  
  // Derived state
  const heroes = useMemo(() => {
    return selectedTeam === 'radiant' ? radiantHeroes : direHeroes;
  }, [radiantHeroes, direHeroes, selectedTeam]);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isComparing, setIsComparing] = useState(false);
  const [timelinePoints, setTimelinePoints] = useState<TimelinePoint[]>([]);
  
  // Add state variables for team names
  const [radiantTeamName, setRadiantTeamName] = useState<string>('Radiant');
  const [direTeamName, setDireTeamName] = useState<string>('Dire');
  
  // Add state variables for banned heroes
  const [radiantBannedHeroes, setRadiantBannedHeroes] = useState<Hero[]>([]);
  const [direBannedHeroes, setDireBannedHeroes] = useState<Hero[]>([]);
  
  // Draft sequence mapping
  const [draftSequence, setDraftSequence] = useState<Record<number, Hero>>({});
  
  // Track spent gold (negative for purchased items, positive for sold items)
  const [playerNetWorth, setPlayerNetWorth] = useState<Map<number, number[]>>(new Map());
  const [playerCurrentGold, setPlayerCurrentGold] = useState<Map<number, number[]>>(new Map());
  
  // Rename these variables to use them in the UI
  const [actualItems, setActualItems] = useState<Map<number, Map<number, Item>>>(new Map());

  // Store all purchased items for each hero (for item progression display)
  const [heroPurchaseHistory, setHeroPurchaseHistory] = useState<Map<number, Array<{item: Item, time: number}>>>(new Map());

  // Which heroes have their "Final Build" answer revealed (collapsed by default so it's not spoiled)
  const [revealedFinalBuilds, setRevealedFinalBuilds] = useState<Set<number>>(new Set());

  const [_playerKDA, setPlayerKDA] = useState<Map<number, KDAStats>>(new Map());
  const [_teamKills, setTeamKills] = useState({ radiant: 0, dire: 0 });
  
  // Add state variables to track special Aghanim's items for each hero
  const [heroesWithShard, setHeroesWithShard] = useState<Set<number>>(new Set());
  const [heroesWithBlessing, setHeroesWithBlessing] = useState<Set<number>>(new Set());
  
  const navigate = useNavigate();
  const location = useLocation();

  const loadMatchData = useCallback(async (itemData: Record<number, ItemData>) => {
    if (!matchId) return;
    
    try {
      const match = await DotaService.getMatch(matchId) as ExtendedMatch;
      console.log('Full match data:', match);
      const points = generateTimelinePoints(match.duration);
      setTimelinePoints(points);
      setGameDuration(match.duration || 2400);
      
      // Extract team names from match data if available
      if (match.radiant_team && match.radiant_team.name) {
        setRadiantTeamName(match.radiant_team.name);
      }
      
      if (match.dire_team && match.dire_team.name) {
        setDireTeamName(match.dire_team.name);
      }
      
      // Try to get match data from additional sources if needed
      const additionalData = await DotaService.getMatchItemsFromSources(matchId);
      if (additionalData) {
        console.log('Got additional match data:', additionalData);
        
        // Check additional data for team names if not found in primary data
        if ((!match.radiant_team || !match.radiant_team.name) && 
            additionalData.radiant_team && additionalData.radiant_team.name) {
          setRadiantTeamName(additionalData.radiant_team.name);
        }
        
        if ((!match.dire_team || !match.dire_team.name) && 
            additionalData.dire_team && additionalData.dire_team.name) {
          setDireTeamName(additionalData.dire_team.name);
        }
        
        // Get picks_bans data from additional source if not available in primary data
        if (!match.picks_bans && additionalData.picks_bans) {
          match.picks_bans = additionalData.picks_bans;
        }
      }
      
      // Process picks and bans data if available
      if (match.picks_bans && match.picks_bans.length > 0) {
        processDraftSequence(match.picks_bans);
      }
      
      // Extract game objectives data (towers, rosh, etc.)
      // Removed as per request - no longer needed
      
      // Extract player net worth over time
      const netWorthOverTime = new Map<number, number[]>();
      const currentGoldOverTime = new Map<number, number[]>();
      
      if (match.players) {
        match.players.forEach((player: ExtendedPlayer) => {
          if (player.hero_id) {
            const netWorths: number[] = [];
            const currentGolds: number[] = []; // Track current gold separately
            
            // Check if detailed gold data is available
            if (player.gold_t && Array.isArray(player.gold_t)) {
              // Ensure first value (minute 0) is at least 600 gold
              const adjustedNetWorth = [...player.gold_t];
              
              // If there's no value for minute 0 or it's less than 600, set it to 600
              if (!adjustedNetWorth[0] || adjustedNetWorth[0] < 600) {
                adjustedNetWorth[0] = 600;
              }
              
              netWorths.push(...adjustedNetWorth);
              
              // Generate current gold data - it's not directly provided by API
              // Estimate current gold as random percentage of net worth with logic for item purchases
              const purchaseTimes = player.purchase_log ? 
                player.purchase_log.map(p => Math.floor(p.time / 60)) : [];
                
              adjustedNetWorth.forEach((netWorth, minute) => {
                // Current gold is lower after item purchases
                const hasPurchase = purchaseTimes.includes(minute);
                const prevNetWorth = minute > 0 ? adjustedNetWorth[minute-1] || 0 : 0;
                const goldEarned = netWorth - prevNetWorth;
                
                // Estimate current gold based on purchases and gold earned
                let estimatedCurrentGold = 0;
                
                if (minute === 0) {
                  estimatedCurrentGold = 600; // Starting gold
                } else {
                  const prevCurrentGold = currentGolds[minute-1] || 0;
                  
                  if (hasPurchase) {
                    // If player bought an item, gold likely dropped to around 30-40% of previous amount
                    estimatedCurrentGold = Math.floor(prevCurrentGold * 0.35) + goldEarned;
                  } else {
                    // If no purchase, player accumulated gold
                    estimatedCurrentGold = prevCurrentGold + goldEarned;
                  }
                }
                
                currentGolds.push(Math.max(0, estimatedCurrentGold));
              });
            } else {
              // Generate synthetic data if not available
              const duration = match.duration || 2400;
              const minutes = Math.ceil(duration / 60);
              
              // Create a realistic gold progression curve
              let cumulativeGold = 600; // Starting gold
              for (let i = 0; i <= minutes; i++) {
                // Gold roughly increases ~500 per minute, with randomness
                const goldPerMinute = 500 + (Math.random() * 100 - 50);
                
                if (i === 0) {
                  netWorths.push(cumulativeGold);
                  currentGolds.push(cumulativeGold);
                } else {
                  cumulativeGold += goldPerMinute;
                  netWorths.push(Math.floor(cumulativeGold));
                  
                  // For current gold, simulate purchases
                  if (i % 5 === 0) { // Major purchase every 5 minutes
                    currentGolds.push(Math.floor(goldPerMinute * 0.4)); // Spent most gold
                  } else if (i % 2 === 0) { // Small items every 2 minutes
                    currentGolds.push(Math.floor(goldPerMinute * 0.7)); // Spent some gold
                  } else {
                    // Accumulating gold
                    currentGolds.push(Math.floor(
                      (currentGolds[currentGolds.length-1] || 0) + goldPerMinute * 0.8
                    ));
                  }
                }
              }
            }
            
            netWorthOverTime.set(player.hero_id, netWorths);
            currentGoldOverTime.set(player.hero_id, currentGolds);
          }
        });
      }
      
      setPlayerNetWorth(netWorthOverTime);
      setPlayerCurrentGold(currentGoldOverTime);
      
      // Create synthetic purchase data if none exists (for testing)
      const createSyntheticPurchaseData = (player: any) => {
        if (!player.purchase_log || player.purchase_log.length === 0) {
          console.log(`Creating synthetic purchase data for hero ${player.hero_id}`);
          player.purchase_log = [];
          
          // Get some random items for testing
          const earlyItems = Object.values(itemData)
            .filter(i => i.cost > 500 && i.cost < 2000)
            .sort(() => 0.5 - Math.random())
            .slice(0, 3);
            
          const midItems = Object.values(itemData)
            .filter(i => i.cost >= 2000 && i.cost < 4000)
            .sort(() => 0.5 - Math.random())
            .slice(0, 2);
            
          const lateItems = Object.values(itemData)
            .filter(i => i.cost >= 4000)
            .sort(() => 0.5 - Math.random())
            .slice(0, 1);
            
          const allItems = [...earlyItems, ...midItems, ...lateItems];
          
          // Distribute purchases throughout the game
          const gameDuration = match.duration || 2400;
          allItems.forEach((item, index) => {
            const timePoint = Math.min(
              gameDuration - 120,
              300 + index * Math.floor(gameDuration / (allItems.length + 1))
            );
            
            player.purchase_log.push({
              time: timePoint,
              item_id: item.id
            });
          });
        }
      };
      
      // Apply synthetic data creation to all players
      if (match.players) {
        match.players.forEach(createSyntheticPurchaseData);
      }
      
      const newActualItems = new Map<number, Map<number, Item>>();
      const newPurchaseHistory = new Map<number, Array<{item: Item, time: number}>>();

      // Create a map of all our heroes for quick lookup
      const allHeroes = [...radiantHeroes, ...direHeroes];
      
      // Debug for hero mapping
      console.log("Heroes from API:", match.players.map(p => p.hero_id));
      console.log("Our hero IDs:", allHeroes.map(h => h.id));
      
      // Process each player
      match.players.forEach(player => {
        console.log(`Processing player with hero_id ${player.hero_id}`);
        
        // Find our corresponding hero by name if IDs don't match directly
        // Some APIs use different hero ID formats
        const heroId = player.hero_id;
        
        // Try direct ID match first (most reliable)
        let matchingHero = allHeroes.find(h => h.id === heroId);
        
        // If no match, try to match by name (fallback)
        if (!matchingHero && match.heroes_data) {
          const apiHeroName = match.heroes_data[heroId]?.name;
          if (apiHeroName) {
            matchingHero = allHeroes.find(h => 
              h.name.toLowerCase() === apiHeroName.toLowerCase()
            );
          }
        }
        
        if (!matchingHero) {
          console.warn(`No matching hero found for API hero_id ${heroId} - using first hero as fallback`);
          // Use the first hero from the appropriate team as a fallback
          matchingHero = player.team_number === 0 ? radiantHeroes[0] : direHeroes[0];
          if (!matchingHero) {
            return; // Skip if no heroes available
          }
        }
        
        console.log(`Found matching hero: ${matchingHero.name} (${matchingHero.id})`);
        const heroTimeline = new Map<number, Item>();
        const heroPurchases: Array<{item: Item, time: number}> = [];

        // Function to add an item to the timeline
        const addItemToTimeline = (
          minute: number,
          itemId: number,
          timestamp: number,
          itemInfo: ItemData
        ) => {
          // Construct full image URL from relative path
          let fullImageUrl = itemInfo.img || '';
          if (fullImageUrl.startsWith('/')) {
            fullImageUrl = `https://cdn.cloudflare.steamstatic.com${fullImageUrl}`;
          } else if (!fullImageUrl.startsWith('http')) {
            fullImageUrl = `https://cdn.cloudflare.steamstatic.com/apps/dota2/images/dota_react/items/${fullImageUrl}`;
          }

          const itemObj: Item = {
            id: itemId,
            timestamp,
            name: itemInfo.dname,
            imageUrl: fullImageUrl
          };
          heroTimeline.set(minute, itemObj);
          // Also add to purchase history for full item progression display
          heroPurchases.push({ item: itemObj, time: timestamp });
        };
        
        // Function to find item by string ID/name
        const findItemByString = (key: string): ItemData | undefined => {
          // First try direct ID lookup
          const numId = parseInt(key);
          if (!isNaN(numId) && itemData[numId]) {
            return itemData[numId];
          }

          const searchKey = key.toLowerCase().replace(/^item_/, '');

          // Then try matching item name
          return Object.values(itemData).find(item => {
            if (!item) return false;
            // Item name is like 'item_quelling_blade', key is like 'quelling_blade'
            const itemNameLower = typeof item.name === 'string' ? item.name.toLowerCase().replace(/^item_/, '') : '';
            const dnameNormalized = item.dname.toLowerCase().replace(/['\s]/g, '_');

            return itemNameLower === searchKey ||
                   itemNameLower.includes(searchKey) ||
                   searchKey.includes(itemNameLower) ||
                   dnameNormalized.includes(searchKey) ||
                   searchKey.includes(dnameNormalized);
          });
        };
        
        // Process purchase log if available
        if (player.purchase_log && Array.isArray(player.purchase_log) && player.purchase_log.length > 0) {
          console.log(`Player ${player.hero_id} has purchase_log data, length: ${player.purchase_log.length}`);

          // Sort purchases by time
          const sortedPurchases = [...player.purchase_log].sort((a, b) => a.time - b.time);

          sortedPurchases.forEach(purchase => {
            if (!purchase || typeof purchase.time !== 'number') return;

            let itemInfo: ItemData | undefined;

            // Handle numeric item ID
            if (typeof purchase.item_id === 'number') {
              itemInfo = itemData[purchase.item_id];
              if (itemInfo) {
                const minute = Math.floor(purchase.time / 60);
                addItemToTimeline(minute, purchase.item_id, purchase.time, itemInfo);
              }
            }
            // Handle string item ID/name
            else if (typeof purchase.item_id === 'string') {
              itemInfo = findItemByString(purchase.item_id);
              if (itemInfo) {
                const minute = Math.floor(purchase.time / 60);
                addItemToTimeline(minute, itemInfo.id, purchase.time, itemInfo);
              }
            }
            // Handle 'key' field (OpenDota API uses 'key' for item name)
            else if (typeof purchase.key === 'string') {
              // Try to find item by the key (e.g., 'quelling_blade', 'magic_stick')
              itemInfo = findItemByString(purchase.key);
              if (itemInfo) {
                const minute = Math.floor(purchase.time / 60);
                addItemToTimeline(minute, itemInfo.id, purchase.time, itemInfo);
              }
            }
          });
        }
        // Use purchase field as fallback
        else if (player.purchase && typeof player.purchase === 'object') {
          console.log(`Player ${player.hero_id} has purchase object data`);
          
          const gameLength = match.duration || 2400; // Default to 40min
          let timeOffset = 300; // Start at 5 minutes
          
          Object.entries(player.purchase).forEach(([itemKey, count]) => {
            // Skip invalid entries
            if (typeof count !== 'number' || count <= 0) return;
            
            const itemInfo = findItemByString(itemKey);
            if (!itemInfo) return;
            
            // Distribute purchases throughout the game
            for (let i = 0; i < count; i++) {
              const estTime = Math.min(
                gameLength - 60,
                timeOffset + i * Math.floor(gameLength / (count + 2))
              );
              
              const minute = Math.floor(estTime / 60);
              addItemToTimeline(minute, itemInfo.id, estTime, itemInfo);
            }
          });
        }
        
        console.log(`Hero ${matchingHero.name} (${matchingHero.id}) timeline has ${heroTimeline.size} items`);
        if (heroTimeline.size > 0) {
          // Store using our local hero ID which is what the rendering uses
          newActualItems.set(matchingHero.id, heroTimeline);
        }

        // Only surface the 6 final inventory slots (item_0..item_5), not every
        // purchase ever made (consumables, upgraded-away components, etc).
        // For each final item, keep its last purchase time from the log.
        const finalItemIds = [
          player.item_0, player.item_1, player.item_2,
          player.item_3, player.item_4, player.item_5
        ].filter((id): id is number => typeof id === 'number' && id > 0);

        if (finalItemIds.length > 0) {
          const latestPurchaseById = new Map<number, { item: Item, time: number }>();
          heroPurchases.forEach(purchase => {
            const existing = latestPurchaseById.get(purchase.item.id);
            if (!existing || purchase.time > existing.time) {
              latestPurchaseById.set(purchase.item.id, purchase);
            }
          });

          // Every final-slot item must appear even if we couldn't match it to a
          // purchase_log entry (e.g. name-matching miss, or an item bought via
          // a recipe combine that wasn't logged under the assembled item's key).
          // Items we can't time-stamp are marked unknown and only surface once
          // the timeline reaches the end, instead of being silently dropped.
          const finalHeroItems = finalItemIds
            .map(id => {
              const matched = latestPurchaseById.get(id);
              if (matched) return matched;

              const itemInfo = itemData[id];
              if (!itemInfo) {
                console.warn(`No itemData found for final-slot item id ${id}, cannot display it`);
                return null;
              }

              let fullImageUrl = itemInfo.img || '';
              if (fullImageUrl.startsWith('/')) {
                fullImageUrl = `https://cdn.cloudflare.steamstatic.com${fullImageUrl}`;
              } else if (!fullImageUrl.startsWith('http')) {
                fullImageUrl = `https://cdn.cloudflare.steamstatic.com/apps/dota2/images/dota_react/items/${fullImageUrl}`;
              }

              return {
                item: { id, timestamp: undefined, name: itemInfo.dname, imageUrl: fullImageUrl } as Item,
                time: Infinity // unknown purchase time - surfaces last, once timeline reaches the end
              };
            })
            .filter((p): p is { item: Item, time: number } => !!p)
            .sort((a, b) => a.time - b.time);

          if (finalHeroItems.length > 0) {
            newPurchaseHistory.set(matchingHero.id, finalHeroItems);
          }
        } else if (heroPurchases.length > 0) {
          // Fallback (e.g. no final-slot data available): show full log sorted by time
          heroPurchases.sort((a, b) => a.time - b.time);
          newPurchaseHistory.set(matchingHero.id, heroPurchases);
        }
      });

      console.log('Final actual items map:', newActualItems);
      console.log('Final purchase history:', newPurchaseHistory);
      setActualItems(newActualItems);
      setHeroPurchaseHistory(newPurchaseHistory);

      // Process KDA data
      const kdaMap = new Map<number, KDAStats>();
      let radiantKills = 0;
      let direKills = 0;

      if (match.players) {
        match.players.forEach((player: ExtendedPlayer) => {
          if (player.hero_id) {
            // Find the correct hero in our local array
            const allHeroes = [...radiantHeroes, ...direHeroes];
            const matchingHero = allHeroes.find(h => h.id === player.hero_id);
            
            if (!matchingHero) {
              console.warn(`Could not find matching hero for KDA data: ${player.hero_id}`);
              return;
            }
            
            // Use actual KDA data if available
            const kda: KDAStats = {
              kills: player.kills || 0,
              deaths: player.deaths || 0,
              assists: player.assists || 0
            };
            
            // Store using our local hero ID
            kdaMap.set(matchingHero.id, kda);

            // Add to team totals
            if (player.team_number === 0 || player.isRadiant) {
              radiantKills += kda.kills;
            } else {
              direKills += kda.kills;
            }
            
            console.log(`Hero ${matchingHero.name} (${matchingHero.id}) KDA: ${kda.kills}/${kda.deaths}/${kda.assists}`);
          }
        });
      }

      // If we didn't get KDA data for all heroes, add fallback data
      [...radiantHeroes, ...direHeroes].forEach(hero => {
        if (!kdaMap.has(hero.id)) {
          console.log(`Adding fallback KDA data for hero ${hero.name} (${hero.id})`);
          // Generate some realistic KDA stats if not available from API
          const kda = {
            kills: Math.floor(Math.random() * 12),
            deaths: Math.floor(Math.random() * 8),
            assists: Math.floor(Math.random() * 20)
          };
          kdaMap.set(hero.id, kda);
          
          // Add to team totals
          const isRadiant = radiantHeroes.some(h => h.id === hero.id);
          if (isRadiant) {
            radiantKills += kda.kills;
          } else {
            direKills += kda.kills;
          }
        }
      });

      setPlayerKDA(kdaMap);
      setTeamKills({ radiant: radiantKills, dire: direKills });
      
      // Process kills and deaths logs
      const allCombatEvents: CombatEvent[] = [];
      const heroIdMap = new Map<string, number>();
      
      // Create a map of hero names to hero IDs for lookups
      [...radiantHeroes, ...direHeroes].forEach(hero => {
        // Use the hero name without npc_dota_hero_ prefix for mapping
        const heroKey = hero.name.replace('npc_dota_hero_', '');
        heroIdMap.set(heroKey, hero.id);
        // Also add the full name for matches that use it
        heroIdMap.set(hero.name, hero.id);
      });
      
      // Process combat events from each player
      if (match.players) {
        match.players.forEach(player => {
          const heroId = player.hero_id;
          
          // Process kills
          if (player.kills_log && Array.isArray(player.kills_log)) {
            player.kills_log.forEach(kill => {
              if (typeof kill.time === 'number') {
                const victimKey = kill.key.replace('npc_dota_hero_', '');
                const victimId = heroIdMap.get(victimKey) || heroIdMap.get(kill.key);
                
                allCombatEvents.push({
                  time: kill.time,
                  heroId: heroId,
                  isKill: true,
                  victimId: victimId,
                  victimName: kill.key
                });
              }
            });
          }
          
          // Process deaths
          if (player.deaths_log && Array.isArray(player.deaths_log)) {
            player.deaths_log.forEach(death => {
              if (typeof death.time === 'number') {
                const killerId = heroIdMap.get(death.key) || heroIdMap.get(death.key.replace('npc_dota_hero_', ''));
                
                allCombatEvents.push({
                  time: death.time,
                  heroId: heroId,
                  isKill: false,
                  victimId: heroId, // The victim is this player
                  victimName: death.key // The killer's name/key
                });
              }
            });
          }
        });
      }
      
      // Sort combat events by time
      allCombatEvents.sort((a, b) => a.time - b.time);
      setCombatEvents(allCombatEvents);
      console.log('Processed combat events:', allCombatEvents);
    } catch (error) {
      console.error('Error loading match data:', error);
    }
  }, [matchId, radiantHeroes, direHeroes]);

  // Add a function to process draft sequence data
  const processDraftSequence = async (picks_bans: ExtendedMatch['picks_bans']) => {
    if (!picks_bans || picks_bans.length === 0) return;
    
    // Get all heroes data to look up hero info by ID
    try {
      const allHeroes = await DotaService.getHeroes();
      const heroesById = new Map<number, Hero>();
      allHeroes.forEach(hero => heroesById.set(hero.id, hero));
      
      // Create a draft sequence map based on the actual draft order
      const draftMap: Record<number, Hero> = {};
      
      // First create a temporary map for pick/ban data
      const picksByIndex = new Map<number, Hero>();
      const bansByIndex = new Map<number, Hero>();
      
      // Extract picks and bans data
      picks_bans.forEach((item, index) => {
        // Find the hero by ID
        const hero = heroesById.get(item.hero_id);
        if (!hero) return;
        
        if (item.is_pick) {
          picksByIndex.set(index, hero);
        } else {
          bansByIndex.set(index, hero);
        }
      });
      
      // Define the pick slots order (standard CM mode)
      const pickSlots = [8, 9, 13, 14, 15, 16, 17, 18, 23, 24];
      
      // Map picks to their slots
      let pickIndex = 0;
      for (const [index, hero] of picksByIndex.entries()) {
        if (pickIndex < pickSlots.length) {
          draftMap[pickSlots[pickIndex]] = hero;
          pickIndex++;
        }
      }
      
      // Now map ban positions to standard ban slots based on the specific instructions
      
      // Create a mapping from ban index to ban slot based on the requested swaps:
      // 2nd ban to 1st ban (slot 2 → slot 1)
      // 6th ban to 4th ban (slot 6 → slot 4)
      // 21st ban to 7th ban (slot 21 → slot 7)
      // 3rd ban to 2nd ban (slot 3 → slot 2)
      // 5th ban to 3rd ban (slot 5 → slot 3)
      // 12th ban to 5th ban (slot 12 → slot 5)
      // 20th ban to 6th ban (slot 20 → slot 6)
      // 1st ban to 10th ban (slot 1 → slot 10)
      // 4th ban to 11th ban (slot 4 → slot 11)
      // 7th ban to 12th ban (slot 7 → slot 12)
      // 11th ban to 20th ban (slot 11 → slot 20)
      // 19th ban to 21st ban (slot 19 → slot 21)
      // 10th ban to 19th ban (slot 10 → slot 19)
      // 22nd ban stays (slot 22)
      
      const banSlotMapping: Record<number, number> = {
        // Map source ban index to target ban slot
        0: 1,  // 1st ban index (0) → 2nd ban slot
        1: 2,  // 2nd ban index (1) → 3rd ban slot
        2: 3,  // 3rd ban index (2) → 5th ban slot
        3: 4,  // 4th ban index (3) → 6th ban slot
        4: 5, // 5th ban index (4) → 20th ban slot
        5: 6, // 6th ban index (5) → 21st ban slot
        6: 7, // 7th ban index (6) → 22nd ban slot (stays)
        7: 8,  // 8th ban index (7) → 1st ban slot
        8: 9,  // 9th ban index (8) → 4th ban slot
        9: 10,  // 10th ban index (9) → 7th ban slot
        10: 11, // 11th ban index (10) → 10th ban slot
        11: 12, // 12th ban index (11) → 11th ban slot
        19: 20,
        20: 21, // 13th ban index (12) → 12th ban slot
        13: 14,  // 14th ban index (13) → 19th ban slot
        18: 19,
        21: 22
      };
      
      // Map bans to their slots based on the custom mapping
      for (const [index, hero] of bansByIndex.entries()) {
        const slot = banSlotMapping[index];
        if (slot) {
          draftMap[slot] = hero;
        }
      }
      
      setDraftSequence(draftMap);
    } catch (error) {
      console.error('Error processing draft sequence:', error);
    }
  };

  // Single useEffect for initialization
  useEffect(() => {
    let mounted = true;
    let unsubscribeFromFirebase: null | (() => void) = null;

    const init = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const items = await DotaService.getItems();
        if (!mounted) return;
        
        setItemData(items);
        
        // Debug log heroes before loading match data
        console.log("Radiant heroes:", radiantHeroes.map(h => ({ id: h.id, name: h.name })));
        console.log("Dire heroes:", direHeroes.map(h => ({ id: h.id, name: h.name })));
        
        // Create initial draft sequence with just the picks (this will be replaced by actual data if available)
        const initialDraftMap: Record<number, Hero> = {};
        
        // Radiant picks
        if (radiantHeroes.length >= 5) {
          initialDraftMap[9] = radiantHeroes[1];  // 1st pick - Swapped with 13
          initialDraftMap[13] = radiantHeroes[0]; // 2nd pick - Swapped with 9
          initialDraftMap[16] = radiantHeroes[4]; // 3rd pick - Swapped with 24
          initialDraftMap[17] = radiantHeroes[3]; // 4th pick
          initialDraftMap[24] = radiantHeroes[2]; // 5th pick - Swapped with 16
        }
        
        // Dire picks
        if (direHeroes.length >= 5) {
          initialDraftMap[8] = direHeroes[0];  // 1st pick
          initialDraftMap[14] = direHeroes[1]; // 2nd pick
          initialDraftMap[15] = direHeroes[3]; // 3rd pick - Swapped with 18
          initialDraftMap[18] = direHeroes[2]; // 4th pick - Swapped with 15
          initialDraftMap[23] = direHeroes[4]; // 5th pick
        }
        
        setDraftSequence(initialDraftMap);
        
        // First create synthetic data for all heroes
        // This ensures we always have something to display
        const syntheticItems = new Map<number, Map<number, Item>>();
        [...radiantHeroes, ...direHeroes].forEach(hero => {
          const syntheticItemTimeline = createSyntheticItemsForHero(hero, items);
          syntheticItems.set(hero.id, syntheticItemTimeline);
        });
        
        // Set synthetic data immediately
        setActualItems(syntheticItems);
        
        // Then try to load real data from API
        await loadMatchData(items);
        
        // Set up Firebase listener for real-time updates if we have a session ID
        if (sessionId) {
          console.log(`Setting up Firebase listener for session ${sessionId}`);
          
          // Reset the initial data loaded flag for new session
          initialDataLoadedFromFirebaseRef.current = false;
          
          // First check if there are any existing items in Firebase
          try {
            const sessionData = await DotaService.getSessionData(sessionId);
            if (sessionData && sessionData.items && Object.keys(sessionData.items).length > 0) {
              console.log('Found existing items in Firebase:', sessionData.items);
              
              // Convert Firebase format to our Map format
              const newUserItems = new Map<number, Map<number, Item>>();
              
              // Process each hero's items
              Object.entries(sessionData.items).forEach(([heroIdStr, heroItemsObj]) => {
                const heroId = parseInt(heroIdStr);
                const heroItems = new Map<number, Item>();
                
                // Process each slot's item
                Object.entries(heroItemsObj as Record<string, any>).forEach(([slotIndexStr, item]) => {
                  const slotIndex = parseInt(slotIndexStr);
                  heroItems.set(slotIndex, item as Item);
                });
                
                newUserItems.set(heroId, heroItems);
              });
              
              // Update the timestamp to prevent immediate re-sync
              lastUpdateTimeRef.current = Date.now();
              
              // Update user items
              setUserItems(newUserItems);
              
              // Update spent gold based on these items
              const newSpentGold = new Map<number, number>();
              newUserItems.forEach((heroItems, heroId) => {
                let totalCost = 0;
                heroItems.forEach(item => {
                  if (item.cost) {
                    totalCost += item.cost;
                  }
                });
                newSpentGold.set(heroId, totalCost);
              });
              
              setSpentGold(newSpentGold);
              console.log('Updated user items and spent gold from Firebase (initial load)');
            }
            
            // Mark that we've checked initial data
            initialDataLoadedFromFirebaseRef.current = true;
          } catch (err) {
            console.error('Error checking initial Firebase data:', err);
            // Still mark as loaded, since we'll get updates via listener anyway
            initialDataLoadedFromFirebaseRef.current = true;
          }
          
          // Set up real-time listener with improved handling for multi-user edits
          unsubscribeFromFirebase = DotaService.listenForSessionItems(sessionId, (firebaseItems) => {
            console.log('Received item update from Firebase:', firebaseItems);
            
            // Skip if we're already syncing
            if (pendingUpdatesRef.current) {
              console.log('Skipping Firebase update because we are currently syncing');
              return;
            }
            
            // Check if there are any items in the Firebase data
            const hasItems = Object.keys(firebaseItems).length > 0;
            
            // Process each hero's items if they exist
            if (hasItems) {
              // Track which updates are coming from the current client
              const updatedByThisClient: Set<string> = new Set();
              
              // Convert Firebase format to our Map format while identifying the source client
              // of each updated item
              const firebaseItemsMap = new Map<number, Map<number, Item & { _fromCurrentClient?: boolean }>>();
              
              // First pass: identify which items were updated by the current client
              Object.entries(firebaseItems).forEach(([heroIdStr, heroItemsObj]) => {
                const heroId = parseInt(heroIdStr);
                const heroItems = new Map<number, Item & { _fromCurrentClient?: boolean }>();
                
                Object.entries(heroItemsObj as Record<string, any>).forEach(([slotIndexStr, item]) => {
                  const slotIndex = parseInt(slotIndexStr);
                  const itemObj = item as Item;
                  const fromCurrentClient = itemObj._clientId === clientIdRef.current;
                  
                  // Mark if this item was updated by the current client
                  if (fromCurrentClient) {
                    updatedByThisClient.add(`${heroId}-${slotIndex}`);
                  }
                  
                  heroItems.set(slotIndex, { 
                    ...itemObj,
                    _fromCurrentClient: fromCurrentClient
                  });
                });
                
                firebaseItemsMap.set(heroId, heroItems);
              });
              
              // Update user items with a merging strategy
              setUserItems(prevUserItems => {
                const mergedItems = new Map<number, Map<number, Item>>();
                
                // Get all hero IDs from both current and incoming state
                const allHeroIds = new Set<number>();
                prevUserItems.forEach((_, heroId) => allHeroIds.add(heroId));
                firebaseItemsMap.forEach((_, heroId) => allHeroIds.add(heroId));
                
                // Process each hero
                allHeroIds.forEach(heroId => {
                  const prevHeroItems = prevUserItems.get(heroId) || new Map<number, Item>();
                  const incomingHeroItems = firebaseItemsMap.get(heroId) || new Map<number, Item>();
                  const mergedHeroItems = new Map<number, Item>();
                  
                  // Get all slot indices for this hero
                  const allSlotIndices = new Set<number>();
                  prevHeroItems.forEach((_, slotIndex) => allSlotIndices.add(slotIndex));
                  incomingHeroItems.forEach((_, slotIndex) => allSlotIndices.add(slotIndex));
                  
                  // For each slot, decide which version to keep
                  allSlotIndices.forEach(slotIndex => {
                    const prevItem = prevHeroItems.get(slotIndex);
                    const incomingItem = incomingHeroItems.get(slotIndex);
                    
                    // Decision logic:
                    // 1. If incoming item doesn't exist, use previous
                    // 2. If incoming item was updated by this client, use previous (this preserves local state)
                    // 3. Otherwise, use incoming (updates from other clients)
                    const itemKey = `${heroId}-${slotIndex}`;
                    
                    if (!incomingItem) {
                      // Keep previous item if it exists
                      if (prevItem) {
                        mergedHeroItems.set(slotIndex, prevItem);
                      }
                    } else if (updatedByThisClient.has(itemKey)) {
                      // For items updated by this client, prefer the local state
                      if (prevItem) {
                        mergedHeroItems.set(slotIndex, prevItem);
                      } else {
                        // If local state doesn't have this item (unlikely), use incoming
                        mergedHeroItems.set(slotIndex, incomingItem);
                      }
                    } else {
                      // For items updated by other clients, use the incoming state
                      mergedHeroItems.set(slotIndex, incomingItem);
                    }
                  });
                  
                  if (mergedHeroItems.size > 0) {
                    mergedItems.set(heroId, mergedHeroItems);
                  }
                });
                
                return mergedItems;
              });
              
              // Update spent gold based on the merged items
              setSpentGold(prevSpentGold => {
                // Need to get the current items after merging
                // This is a bit of a hack since we don't have access to the merged items yet
                // Will trigger a re-calculation after a slight delay
                setTimeout(() => {
                  setUserItems(current => {
                    const newSpentGold = new Map<number, number>();
                    
                    current.forEach((heroItems, heroId) => {
                      let totalCost = 0;
                      heroItems.forEach(item => {
                        if (item.cost) {
                          totalCost += item.cost;
                        }
                      });
                      newSpentGold.set(heroId, totalCost);
                    });
                    
                    setSpentGold(newSpentGold);
                    return current;
                  });
                }, 50);
                
                // Return the current state for now
                return prevSpentGold;
              });
              
              console.log('Updated user items from Firebase with merge strategy to preserve local changes');
            } else if (userItems.size > 0) {
              // If Firebase data is empty but we have local items,
              // then we should push our data to Firebase rather than clearing it
              console.log('Firebase data is empty but we have local items. Will sync our data to Firebase.');
              pendingUpdatesRef.current = true;
              syncToFirebase(userItems, sessionId);
            }
          });
        }
      } catch (err) {
        console.error('Error during initialization:', err);
        if (mounted) {
          setError(err instanceof Error ? err.message : 'Failed to load data');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    init();
    
    return () => { 
      mounted = false; 
      // Clean up Firebase listener on unmount
      if (unsubscribeFromFirebase) {
        console.log('Cleaning up Firebase listener');
        unsubscribeFromFirebase();
      }
    };
  }, [radiantHeroes, direHeroes, matchId, sessionId]);

  // Initialize spent gold for heroes in useEffect
  useEffect(() => {
    const newSpentGold = new Map<number, number>();
    [...radiantHeroes, ...direHeroes].forEach(hero => {
      if (!spentGold.has(hero.id)) {
        newSpentGold.set(hero.id, 0);
      }
    });
    
    // Only update if we have new heroes
    if (newSpentGold.size > 0) {
      setSpentGold(prevSpentGold => {
        const mergedMap = new Map(prevSpentGold);
        newSpentGold.forEach((gold, heroId) => {
          mergedMap.set(heroId, gold);
        });
        return mergedMap;
      });
    }
  }, [radiantHeroes, direHeroes, spentGold]);
  
  // Use refs to track the last update time and changes waiting to be synced
  const lastUpdateTimeRef = useRef<number>(Date.now());
  const pendingUpdatesRef = useRef<boolean>(false);
  const initialDataLoadedFromFirebaseRef = useRef<boolean>(false);
  
  // Add debounce function implementation
  function debounce<T extends (...args: any[]) => any>(func: T, wait: number) {
    let timeout: ReturnType<typeof setTimeout> | null = null;
    
    return function(...args: Parameters<T>) {
      if (timeout) clearTimeout(timeout);
      
      timeout = setTimeout(() => {
        func(...args);
        timeout = null;
      }, wait);
    };
  }
  
  // Debounced function to sync changes to Firebase
  const syncToFirebase = useCallback(debounce(async (items: Map<number, Map<number, Item>>, id: string) => {
    console.log("Syncing changes to Firebase...");
    
    // Convert our Map format to Firebase-friendly object format
    const firebaseItems: Record<string, any> = {};
    
    items.forEach((heroItems, heroId) => {
      if (heroItems.size === 0) return;
      
      // Create an object for each hero's items
      const heroItemsObj: Record<string, any> = {};
      
      heroItems.forEach((item, slotIndex) => {
        heroItemsObj[slotIndex.toString()] = item;
      });
      
      firebaseItems[heroId.toString()] = heroItemsObj;
    });
    
    // Skip if empty
    if (Object.keys(firebaseItems).length === 0) {
      console.log("No items to sync");
      pendingUpdatesRef.current = false;
      return;
    }
    
    try {
      // Update the entire items object at once to reduce conflicts
      await DotaService.updateAllSessionItems(id, firebaseItems);
      console.log("Successfully synced items to Firebase");
      
      // Reset pending updates flag
      pendingUpdatesRef.current = false;
      lastUpdateTimeRef.current = Date.now();
    } catch (error) {
      console.error('Error syncing items to Firebase:', error);
      // Will try again on next update
    }
  }, 300), []);  // 300ms debounce
  
  // Sync local changes to Firebase whenever user items change
  useEffect(() => {
    if (!sessionId || userItems.size === 0) return;
    
    // Don't sync if we haven't loaded initial data from Firebase
    if (!initialDataLoadedFromFirebaseRef.current) {
      console.log("Skipping sync because initial data hasn't been loaded from Firebase yet");
      return;
    }
    
    // Don't trigger update if we've just received data from Firebase (within last second)
    const now = Date.now();
    if (now - lastUpdateTimeRef.current < 1000) {
      console.log("Skipping sync because we just received an update");
      return;
    }
    
    // Mark that we have pending updates
    pendingUpdatesRef.current = true;
    
    // Trigger the debounced update
    syncToFirebase(userItems, sessionId);
    
  }, [userItems, sessionId, syncToFirebase]);

  // Function to create synthetic data for a specific hero
  const createSyntheticItemsForHero = (hero: Hero, _itemData: Record<number, ItemData>): Map<number, Item> => {
    console.log(`Creating direct synthetic items for ${hero.name}`);
    const itemTimeline = new Map<number, Item>();
    
    // Define well-known item URLs that definitely work
    const knownGoodItems = [
      {
        id: 1,
        name: "Blink Dagger",
        imageUrl: "https://cdn.dota2.com/apps/dota2/images/items/blink_lg.png",
        minute: 5
      },
      {
        id: 2,
        name: "Black King Bar",
        imageUrl: "https://cdn.dota2.com/apps/dota2/images/items/black_king_bar_lg.png",
        minute: 12
      },
      {
        id: 3,
        name: "Aghanim's Scepter",
        imageUrl: "https://cdn.dota2.com/apps/dota2/images/items/ultimate_scepter_lg.png",
        minute: 20
      },
      {
        id: 4, 
        name: "Daedalus",
        imageUrl: "https://cdn.dota2.com/apps/dota2/images/items/greater_crit_lg.png",
        minute: 30
      }
    ];
    
    // Add the known good items
    knownGoodItems.forEach(item => {
      itemTimeline.set(item.minute, {
        id: item.id,
        name: item.name,
        imageUrl: item.imageUrl,
        timestamp: item.minute * 60
      });
    });
    
    return itemTimeline;
  };

  const calculateAvailableCurrency = (heroId: number, currentMinute: number) => {
    // Calculate total cost of all existing items
    const currentHeroItems = userItems.get(heroId) || new Map<number, Item>();
    let totalCost = 0;
    currentHeroItems.forEach(item => {
      if (item.cost) {
        totalCost += item.cost;
      }
    });
    
    // Calculate available currency based on net worth
    const netWorthData = playerNetWorth.get(heroId);
    const currentNetWorth = netWorthData && netWorthData.length > currentMinute
      ? netWorthData[currentMinute]
      : 0;
    
    return {
      availableCurrency: currentNetWorth - totalCost,
      totalItemsCost: totalCost,
      currentNetWorth
    };
  };

  const handleItemSelect = (heroId: number, slotIndex: number, itemId: number) => {
    // Find the selected item in our predefined item lists
    const { basicItems, upgradeItems, neutralItems } = getDotaItems();
    const allItems = [...basicItems, ...upgradeItems, ...neutralItems];
    const selectedItem = allItems.find(item => item.id === itemId);
    
    if (selectedItem) {
      // Get current timeline minute
      const currentMinute = Math.floor(currentTimePoint / 60);
      
      // Calculate currency and costs
      const { availableCurrency, totalItemsCost, currentNetWorth } = calculateAvailableCurrency(heroId, currentMinute);
      const itemCost = selectedItem.cost || 0;
      
      // Check if there's enough currency to buy the item
      if (itemCost > availableCurrency) {
        console.log(`Not enough currency to buy ${selectedItem.name}. Need ${itemCost}, have ${availableCurrency}`);
        
        // Set error message for this hero instead of using alert
        const moneyNeeded = itemCost - availableCurrency;
        const errorMsg = `Need ${moneyNeeded} more for ${selectedItem.name}`;
        const newErrorMessages = new Map(errorMessages);
        newErrorMessages.set(heroId, errorMsg);
        setErrorMessages(newErrorMessages);
        
        // Clear the error message after 3 seconds
        setTimeout(() => {
          const updatedErrorMessages = new Map(errorMessages);
          updatedErrorMessages.delete(heroId);
          setErrorMessages(updatedErrorMessages);
        }, 3000);
        
        return;
      }

      // Special handling for Aghanim's Shard and Aghanim's Blessing
      if (selectedItem.name === 'Aghanim\'s Shard' || selectedItem.name === 'Aghanim\'s Blessing') {
        // Update the special item state
        if (selectedItem.name === 'Aghanim\'s Shard') {
          setHeroesWithShard(prev => {
            const newSet = new Set(prev);
            newSet.add(heroId);
            return newSet;
          });
        } else { // Aghanim's Blessing
          setHeroesWithBlessing(prev => {
            const newSet = new Set(prev);
            newSet.add(heroId);
            return newSet;
          });
        }
        
        // Update the hero's spent gold
        const currentSpentGold = spentGold.get(heroId) || 0;
        const newSpentGold = currentSpentGold + itemCost;
        
        const newSpentGoldMap = new Map(spentGold);
        newSpentGoldMap.set(heroId, newSpentGold);
        setSpentGold(newSpentGoldMap);
        
        // Log for debugging
        console.log(`Selected ${selectedItem.name} for hero ${heroId} at minute ${currentMinute}, costing ${itemCost}. Net Worth: ${currentNetWorth}, Spent: ${newSpentGold}, Available: ${currentNetWorth - newSpentGold}`);
        
        // Close the modal immediately
        const modal = document.querySelector('.item-selection-modal');
        if (modal && document.body.contains(modal)) {
          document.body.removeChild(modal);
          
          // Find the backdrop and remove it too
          const backdropOverlay = document.querySelector('.modal-backdrop-blur');
          if (backdropOverlay && document.body.contains(backdropOverlay)) {
            document.body.removeChild(backdropOverlay);
          }
        }

        // Format data for Firebase to track aghanim's purchases
        const firebaseItems: Record<string, Record<string, any>> = {};
        // Include a special marker for Aghanim's items in Firebase
        const heroItemsObj: Record<string, any> = {};
        
        // Create a pseudo-item to track in Firebase
        const specialItem = {
          id: selectedItem.id,
          name: selectedItem.name,
          imageUrl: selectedItem.name === 'Aghanim\'s Shard' 
            ? 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/aghanims_shard_lg.png'
            : 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/ultimate_scepter_2_lg.png',
          cost: itemCost,
          timestamp: currentMinute,
          time: currentMinute * 60,
          isSpecial: true, // Mark as special item
          sessionId,
          _clientId: clientIdRef.current,
          _lastUpdated: Date.now()
        };
        
        // Use a special slot (e.g., 100 for shard, 101 for blessing) to store these items
        const specialSlot = selectedItem.name === 'Aghanim\'s Shard' ? 100 : 101;
        heroItemsObj[specialSlot.toString()] = specialItem;
        
        // Add to Firebase
        firebaseItems[heroId.toString()] = heroItemsObj;
        
        // Directly sync to Firebase bypassing the debounce
        console.log(`Added special item ${selectedItem.name} for hero ${heroId}. Syncing immediately to Firebase...`);
        DotaService.updateSessionItems(sessionId, String(heroId), heroItemsObj)
          .then(() => {
            console.log("Successfully synced special item addition to Firebase");
            lastUpdateTimeRef.current = Date.now();
          })
          .catch(error => {
            console.error('Error syncing special item addition to Firebase:', error);
          });
        
        return;
      }
      
      // Special handling for combined Observer and Sentry Wards
      if (selectedItem.name === 'Observer and Sentry Wards') {
        console.log(`Selected combined Observer and Sentry Wards for hero ${heroId} in slot ${slotIndex}`);
        
        // Create a structured item object with multiple image sources for reliability
        const primaryImgUrl = getItemImageUrl(selectedItem.name);
        const alternativeImgUrl = `https://media-hosting.imagekit.io/9b5b1ecec07c4169/Observer_and_Sentry_Wards_1_itemicon_dota2_gameasset.png?Expires=1839749073&Key-Pair-Id=K2ZIVPTIP2VGHC&Signature=mekHEUKi10zJbdkUqy9~AUbgj9Rl17g5RvdVC~r~oIZ5V-0FBbrCyv~2rLODoZdhaIXm51yv0Cm-OD3QPejh2DULBXbaK2JjGU2Y4Ay314awkGBMR0j8G22HeJMrQYQxOFKqkvYm5G6mNjbOR8E26XQfcMtGiKInqSyOznMrrjDCLyGS7Omr-pIIXIDFAjrulCD6wOBZ~6rRagfdBamWuOcuH~t2GQ-l2jZaiP5x0lBaVTY5W-SPoUwYkrpChFq0rOkutPRaPSP88dryDICUowjI0IS-So5IN46FhX5AbrofjdwJpMBLkbinwwEZaMTDwUMwaEt4fOtGEnNMD-ObcA__`;
        
        const newItem = {
          id: selectedItem.id,
          name: selectedItem.name,
          img: selectedItem.img,
          imageUrl: primaryImgUrl,
          alternativeImageUrl: alternativeImgUrl,
          slotIndex: slotIndex,
          timestamp: currentMinute,
          time: currentMinute * 60,
          cost: selectedItem.cost,
          sessionId: sessionId,
          _clientId: clientIdRef.current,
          _lastUpdated: Date.now(),
          isCombinedWards: true // Mark this as a special combined item
        };
        
        // Keep track of which slot we just updated to prevent other player edits from affecting it
        lastUpdatedRef.current = { heroId, slotIndex };
        
        // Update the user items Map - store items by slot index instead of time
        const heroItems = userItems.get(heroId) || new Map<number, Item>();
        
        // Check if there's an existing item in this slot to refund its cost
        const existingItem = heroItems.get(slotIndex);
        let refundAmount = 0;
        
        if (existingItem && existingItem.cost) {
          refundAmount = existingItem.cost;
        }
        
        // Set the new item
        heroItems.set(slotIndex, newItem);
        
        // Create a completely new map to ensure React detects the change
        const newUserItems = new Map(userItems);
        newUserItems.set(heroId, new Map(heroItems));
        
        // Update the hero's spent gold
        const currentSpentGold = spentGold.get(heroId) || 0;
        const newSpentGold = currentSpentGold + itemCost - refundAmount;
        
        const newSpentGoldMap = new Map(spentGold);
        newSpentGoldMap.set(heroId, newSpentGold);
        
        // Set states immediately to ensure UI updates right away
        setUserItems(newUserItems);
        setSpentGold(newSpentGoldMap);
        
        // ... existing modal handling code ...
        
        // Format data for Firebase
        const firebaseItems: Record<string, Record<string, any>> = {};
        newUserItems.forEach((items, heroId) => {
          const heroItemsObj: Record<string, any> = {};
          items.forEach((item, slotIndex) => {
            heroItemsObj[slotIndex.toString()] = item;
          });
          
          // Include an empty object for heroes with no items to explicitly clear them
          firebaseItems[heroId.toString()] = items.size > 0 ? heroItemsObj : {};
        });
        
        // Directly sync to Firebase bypassing the debounce
        console.log(`Added combined wards for hero ${heroId}. Syncing immediately to Firebase...`);
        DotaService.updateAllSessionItems(sessionId, firebaseItems)
          .then(() => {
            console.log("Successfully synced combined wards to Firebase");
            lastUpdateTimeRef.current = Date.now();
          })
          .catch(error => {
            console.error('Error syncing combined wards to Firebase:', error);
          });
          
        return;
      }
      
      // Create a structured item object with multiple image sources for reliability
      const primaryImgUrl = getItemImageUrl(selectedItem.name);
      const alternativeImgUrl = `https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/${selectedItem.img}_lg.png`;
      
      const newItem = {
        id: selectedItem.id,
        name: selectedItem.name,
        img: selectedItem.img,
        imageUrl: primaryImgUrl,
        alternativeImageUrl: alternativeImgUrl,
        slotIndex: slotIndex,
        timestamp: currentMinute,
        time: currentMinute * 60,
        cost: selectedItem.cost,
        sessionId: sessionId,
        _clientId: clientIdRef.current,
        _lastUpdated: Date.now()
      };
      
      // Keep track of which slot we just updated to prevent other player edits from affecting it
      lastUpdatedRef.current = { heroId, slotIndex };
      
      // Update the user items Map - store items by slot index instead of time
      const heroItems = userItems.get(heroId) || new Map<number, Item>();
      
      // Check if there's an existing item in this slot to refund its cost
      const existingItem = heroItems.get(slotIndex);
      let refundAmount = 0;
      
      if (existingItem && existingItem.cost) {
        refundAmount = existingItem.cost;
      }
      
      // Set the new item
      heroItems.set(slotIndex, newItem);
      
      // Create a completely new map to ensure React detects the change
      const newUserItems = new Map(userItems);
      newUserItems.set(heroId, new Map(heroItems));
      
      // Update the hero's spent gold
      const currentSpentGold = spentGold.get(heroId) || 0;
      const newSpentGold = currentSpentGold + itemCost - refundAmount;
      
      const newSpentGoldMap = new Map(spentGold);
      newSpentGoldMap.set(heroId, newSpentGold);
      
      // Set states immediately to ensure UI updates right away
      setUserItems(newUserItems);
      setSpentGold(newSpentGoldMap);
      
      // Update the modal UI if it's open
      const modal = document.querySelector('.item-selection-modal');
      if (modal) {
        const heroRow = modal.querySelector(`.hero-row[data-hero-id="${heroId}"]`);
        if (heroRow) {
          // Find the hero object
          const allHeroes = [...radiantHeroes, ...direHeroes];
          const hero = allHeroes.find(h => h.id === heroId);
          if (hero) {
            // Instead of calling createHeroRow directly, trigger a full refresh of the modal
            // This ensures all internal functions are correctly available
            const modalElement = document.querySelector('.item-selection-modal');
            if (modalElement && document.body.contains(modalElement)) {
              document.body.removeChild(modalElement);
              
              // Find the backdrop and remove it too
              const backdropOverlay = document.querySelector('.modal-backdrop-blur');
              if (backdropOverlay && document.body.contains(backdropOverlay)) {
                document.body.removeChild(backdropOverlay);
              }
              
              // Reopen the modal
              const dummyEvent = new MouseEvent('click');
              Object.defineProperty(dummyEvent, 'target', { value: document.createElement('div') });
              showItemSelection(dummyEvent as unknown as React.MouseEvent<HTMLDivElement>, heroId, slotIndex);
            }
          }
        }
      }
      
      // Log for debugging
      console.log(`Selected ${selectedItem.name} for hero ${heroId} in slot ${slotIndex} at minute ${currentMinute}, costing ${itemCost}. Net Worth: ${currentNetWorth}, Spent: ${newSpentGold}, Available: ${currentNetWorth - newSpentGold}`);
      
      // Format data for Firebase
      const firebaseItems: Record<string, Record<string, any>> = {};
      newUserItems.forEach((items, heroId) => {
        const heroItemsObj: Record<string, any> = {};
        items.forEach((item, slotIndex) => {
          heroItemsObj[slotIndex.toString()] = item;
        });
        
        // Include an empty object for heroes with no items to explicitly clear them
        firebaseItems[heroId.toString()] = items.size > 0 ? heroItemsObj : {};
      });
      
      // Directly sync to Firebase bypassing the debounce
      console.log(`Added item ${selectedItem.name} for hero ${heroId}. Syncing immediately to Firebase...`);
      DotaService.updateAllSessionItems(sessionId, firebaseItems)
        .then(() => {
          console.log("Successfully synced item addition to Firebase");
          lastUpdateTimeRef.current = Date.now();
        })
        .catch(error => {
          console.error('Error syncing item addition to Firebase:', error);
        });
    }
  };
  
  // Add a function to handle removing items and refunding gold
  const removeHeroItem = (heroId: number, slotIndex: number) => {
    // Keep track of which slot we just updated
    lastUpdatedRef.current = { heroId, slotIndex: undefined };
    
    const heroItems = userItems.get(heroId);
    
    if (heroItems && heroItems.has(slotIndex)) {
      const item = heroItems.get(slotIndex)!;
      const itemCost = item.cost || 0;
      
      // Remove the item
      const newHeroItems = new Map(heroItems);
      newHeroItems.delete(slotIndex);
      
      // Create a completely new map to ensure React detects the change
      const newUserItems = new Map(userItems);
      newUserItems.set(heroId, newHeroItems);
      
      // Refund the gold by reducing spent gold
      const heroSpentGold = spentGold.get(heroId) || 0;
      const newSpentGold = heroSpentGold - itemCost;
      
      const newSpentGoldMap = new Map(spentGold);
      newSpentGoldMap.set(heroId, newSpentGold);
      
      // Set states immediately to ensure UI updates right away
      setUserItems(newUserItems);
      setSpentGold(newSpentGoldMap);
      
      // Update the modal UI if it's open
      const modal = document.querySelector('.item-selection-modal');
      if (modal) {
        const heroRow = modal.querySelector(`.hero-row[data-hero-id="${heroId}"]`);
        if (heroRow) {
          // Find the hero object
          const allHeroes = [...radiantHeroes, ...direHeroes];
          const hero = allHeroes.find(h => h.id === heroId);
          if (hero) {
            // Refresh the whole modal to ensure all internal functions are correctly available
            const modalElement = document.querySelector('.item-selection-modal');
            if (modalElement && document.body.contains(modalElement)) {
              document.body.removeChild(modalElement);
              
              // Find the backdrop and remove it too
              const backdropOverlay = document.querySelector('.modal-backdrop-blur');
              if (backdropOverlay && document.body.contains(backdropOverlay)) {
                document.body.removeChild(backdropOverlay);
              }
              
              // Reopen the modal with the current active slot
              const dummyEvent = new MouseEvent('click');
              Object.defineProperty(dummyEvent, 'target', { value: document.createElement('div') });
              showItemSelection(dummyEvent as unknown as React.MouseEvent<HTMLDivElement>, heroId, slotIndex);
            }
          }
        }
      }
      
      // Format data for Firebase
      const firebaseItems: Record<string, Record<string, any>> = {};
      newUserItems.forEach((items, heroId) => {
        const heroItemsObj: Record<string, any> = {};
        items.forEach((item, slotIndex) => {
          // Add client tracking info to the item
          const itemWithClientInfo = {
            ...item,
            _clientId: clientIdRef.current,
            _lastUpdated: Date.now()
          };
          heroItemsObj[slotIndex.toString()] = itemWithClientInfo;
        });
        
        // Include an empty object for heroes with no items to explicitly clear them
        firebaseItems[heroId.toString()] = items.size > 0 ? heroItemsObj : {};
      });
      
      // Log for debugging
      console.log(`Removed item from hero ${heroId} slot ${slotIndex}. Syncing to Firebase...`);
      DotaService.updateAllSessionItems(sessionId, firebaseItems)
        .then(() => {
          console.log("Successfully synced item removal to Firebase");
          lastUpdateTimeRef.current = Date.now();
        })
        .catch(error => {
          console.error('Error syncing item removal to Firebase:', error);
        });
    }
  };
  
  // Update the clear all items function
  const clearAllHeroItems = (heroId: number) => {
    const heroItems = userItems.get(heroId);
    const hasShard = heroesWithShard.has(heroId);
    const hasBlessing = heroesWithBlessing.has(heroId);
    
    if ((heroItems && heroItems.size > 0) || hasShard || hasBlessing) {
      // Calculate the total cost of all items to refund
      let totalRefund = 0;
      
      // Calculate refund for regular items
      if (heroItems) {
        heroItems.forEach(item => {
          if (item.cost) {
            totalRefund += item.cost;
          }
        });
      }
      
      // Add refund for shard and blessing if present
      if (hasShard) {
        totalRefund += 1400; // Shard cost
      }
      if (hasBlessing) {
        totalRefund += 5800; // Blessing cost
      }
      
      // Clear all items
      const newUserItems = new Map(userItems);
      newUserItems.set(heroId, new Map());
      
      // Clear special items for this hero
      if (hasShard) {
        setHeroesWithShard(prev => {
          const newSet = new Set(prev);
          newSet.delete(heroId);
          return newSet;
        });
      }
      
      if (hasBlessing) {
        setHeroesWithBlessing(prev => {
          const newSet = new Set(prev);
          newSet.delete(heroId);
          return newSet;
        });
      }
      
      // Reset spent gold to zero
      const newSpentGoldMap = new Map(spentGold);
      newSpentGoldMap.set(heroId, 0);
      
      // Set states immediately to ensure UI updates right away
      setUserItems(newUserItems);
      setSpentGold(newSpentGoldMap);
      
      // Format data for Firebase with explicit handling for special slots
      const firebaseItems: Record<string, Record<string, any>> = {};
      const heroItemsObj: Record<string, any> = {};
      
      // Explicitly set special slots to null
      heroItemsObj["100"] = null; // Clear Aghanim's Shard 
      heroItemsObj["101"] = null; // Clear Aghanim's Blessing
      
      firebaseItems[heroId.toString()] = heroItemsObj;
      
      // Directly sync to Firebase bypassing the debounce
      console.log(`Cleared all items for hero ${heroId}, refunding ${totalRefund} gold. Syncing immediately to Firebase...`);
      DotaService.updateSessionItems(sessionId, String(heroId), heroItemsObj)
        .then(() => {
          console.log("Successfully synced cleared items to Firebase");
          lastUpdateTimeRef.current = Date.now();
        })
        .catch(error => {
          console.error('Error syncing cleared items to Firebase:', error);
        });
    }
  };

  // Add new function to clear all items for all heroes
  const clearAllItemsForAllHeroes = () => {
    // Check if there are any items to clear
    let hasItems = false;
    [...radiantHeroes, ...direHeroes].forEach(hero => {
      const heroItems = userItems.get(hero.id);
      if (heroItems && heroItems.size > 0) {
        hasItems = true;
      }
    });

    if (!hasItems && heroesWithShard.size === 0 && heroesWithBlessing.size === 0) {
      console.log("No items to clear for any heroes");
      return;
    }

    // Create new maps for userItems and spentGold
    const newUserItems = new Map<number, Map<number, Item>>();
    const newSpentGoldMap = new Map<number, number>();

    // Initialize empty maps for all heroes
    [...radiantHeroes, ...direHeroes].forEach(hero => {
      newUserItems.set(hero.id, new Map());
      newSpentGoldMap.set(hero.id, 0);
    });

    // Clear special items
    setHeroesWithShard(new Set());
    setHeroesWithBlessing(new Set());

    // Update state immediately
    setUserItems(newUserItems);
    setSpentGold(newSpentGoldMap);

    // Format data for Firebase
    const firebaseItems: Record<string, Record<string, any>> = {};
    [...radiantHeroes, ...direHeroes].forEach(hero => {
      // Include empty objects for all heroes to explicitly clear them
      firebaseItems[hero.id.toString()] = {
        "100": null,  // Clear Aghanim's Shard
        "101": null   // Clear Aghanim's Blessing
      };
    });

    // Directly sync to Firebase
    console.log("Clearing all items for all heroes. Syncing immediately to Firebase...");
    DotaService.updateAllSessionItems(sessionId, firebaseItems)
      .then(() => {
        console.log("Successfully synced cleared items for all heroes to Firebase");
        lastUpdateTimeRef.current = Date.now();
      })
      .catch(error => {
        console.error('Error syncing cleared items for all heroes to Firebase:', error);
      });
  };

  // Add function to compare user itemization with actual match data
  const _compareItemization = () => {
    // Debug output
    console.log('Comparing itemization. Current state:', isComparing);
    console.log('Heroes:', selectedTeam === 'radiant' ? radiantHeroes : direHeroes);
    
    // Check if we have actual data
    const hasActualData = (selectedTeam === 'radiant' ? radiantHeroes : direHeroes).some(hero => {
      const heroItems = actualItems.get(hero.id);
      return heroItems && heroItems.size > 0;
    });
    
    if (!hasActualData) {
      console.log('No actual data available - using demo items');
    }
    
    // Debug log for each hero's items
    if (selectedTeam === 'radiant') {
      radiantHeroes.forEach(hero => {
        const heroItems = actualItems.get(hero.id);
        console.log(`Hero ${hero.name} (${hero.id}) actual items:`, heroItems);
      });
    } else {
      direHeroes.forEach(hero => {
        const heroItems = actualItems.get(hero.id);
        console.log(`Hero ${hero.name} (${hero.id}) actual items:`, heroItems);
      });
    }
    
    setIsComparing(!isComparing);
  };

  // Add an effect to handle timeline-based item management
  useEffect(() => {
    // Skip if no session or no items
    if (!sessionId || userItems.size === 0) return;
    
    // Get the current minute
    const currentMinute = Math.floor(currentTimePoint / 60);
    
    // Check all hero items to see if we need to remove any based on timeline
    let itemsChanged = false;
    const newUserItems = new Map<number, Map<number, Item>>();
    const newSpentGoldMap = new Map<number, number>();
    
    userItems.forEach((heroItems, heroId) => {
      const updatedHeroItems = new Map<number, Item>();
      let totalHeroCost = 0;
      
      heroItems.forEach((item, slotIndex) => {
        // Check if this item was purchased in the future based on the timestamp
        // Use a default value of 0 if timestamp is undefined
        const itemMinute = Math.floor((item.timestamp || 0) / 60);
        if (itemMinute <= currentMinute) {
          // Keep the item if it was purchased in the past or present
          updatedHeroItems.set(slotIndex, item);
          if (item.cost) {
            totalHeroCost += item.cost;
          }
        } else {
          // Item was in the future, so it should be removed
          itemsChanged = true;
          console.log(`Removing future item ${item.name} from hero ${heroId} at minute ${itemMinute} because timeline is at ${currentMinute}`);
        }
      });
      
      newUserItems.set(heroId, updatedHeroItems);
      newSpentGoldMap.set(heroId, totalHeroCost);
    });
    
    // Update state if items were removed
    if (itemsChanged) {
      console.log("Timeline moved - removing future items and updating spent gold");
      
      // Force all client updates to be immediate, don't wait for next tick
      // This ensures both clients stay in sync immediately
      setUserItems(newUserItems);
      setSpentGold(newSpentGoldMap);
      
      // Force immediate sync to Firebase to notify other sessions
      lastUpdateTimeRef.current = 0;
      syncToFirebase(newUserItems, sessionId);
    }
  }, [currentTimePoint, sessionId, userItems, syncToFirebase]);

  // Update hero positions to be better distributed
  const _getHeroPosition = (isRadiant: boolean, role: number, phase: string) => {
    if (phase === 'early') {
      if (isRadiant) {
        switch(role) {
          case 0: // Mid
            return { x: 190, y: 190 };
          case 1: // Safe carry
            return { x: 280, y: 80 };
          case 2: // Safe support
            return { x: 260, y: 100 };
          case 3: // Off lane
            return { x: 80, y: 280 };
          case 4: // Roaming support
            return { x: 160, y: 160 };
        }
      } else {
        switch(role) {
          case 0: // Mid
            return { x: 210, y: 210 };
          case 1: // Safe carry
            return { x: 320, y: 320 };
          case 2: // Safe support
            return { x: 300, y: 300 };
          case 3: // Off lane
            return { x: 120, y: 120 };
          case 4: // Roaming support
            return { x: 240, y: 240 };
        }
      }
    } else if (phase === 'mid') {
      if (isRadiant) {
        switch(role) {
          case 0: return { x: 220, y: 180 };
          case 1: return { x: 300, y: 120 };
          case 2: return { x: 260, y: 160 };
          case 3: return { x: 140, y: 260 };
          case 4: return { x: 200, y: 200 };
        }
      } else {
        switch(role) {
          case 0: return { x: 180, y: 220 };
          case 1: return { x: 280, y: 280 };
          case 2: return { x: 240, y: 240 };
          case 3: return { x: 140, y: 140 };
          case 4: return { x: 200, y: 200 };
        }
      }
    } else { // late game
      if (isRadiant) {
        switch(role) {
          case 0: return { x: 240, y: 200 };
          case 1: return { x: 280, y: 160 };
          case 2: return { x: 260, y: 180 };
          case 3: return { x: 220, y: 220 };
          case 4: return { x: 200, y: 240 };
        }
      } else {
        switch(role) {
          case 0: return { x: 160, y: 200 };
          case 1: return { x: 120, y: 240 };
          case 2: return { x: 140, y: 220 };
          case 3: return { x: 180, y: 180 };
          case 4: return { x: 200, y: 160 };
        }
      }
    }
    return { x: 200, y: 200 }; // fallback
  };

  // Define a more comprehensive item list with proper image paths
  const getDotaItems = () => {
    // Basic Items - First three rows from the screenshot
    const basicItems = [
      // First row
      { id: 1, name: 'Blink Dagger', img: 'blink', cost: 2250 },
      { id: 2, name: 'Boots of Speed', img: 'boots', cost: 500 },
      { id: 3, name: 'Magic Stick', img: 'magic_stick', cost: 200 },
      { id: 4, name: 'Quelling Blade', img: 'quelling_blade', cost: 100 },
      { id: 5, name: 'Clarity', img: 'clarity', cost: 50 },
      // Second row
      { id: 14, name: 'Iron Branch', img: 'branches', cost: 50 },
      { id: 6, name: 'Healing Salve', img: 'flask', cost: 100 },
      { id: 7, name: 'Tango', img: 'tango', cost: 90 },
      { id: 11, name: 'Wraith Band', img: 'wraith_band', cost: 505 },
      { id: 12, name: 'Soul Ring', img: 'soul_ring', cost: 805 },
      // Third row
      { id: 56, name: 'Magic Wand', img: 'magic_wand', cost: 450 },
      { id: 13, name: 'Ring of Basilius', img: 'ring_of_basilius', cost: 425 },
      { id: 8, name: 'Observer Ward', img: 'ward_observer', cost: 0 },
      { id: 9, name: 'Bracer', img: 'bracer', cost: 505 },
      { id: 10, name: 'Null Talisman', img: 'null_talisman', cost: 505 },
      // Fourth row
      { id: 15, name: 'Gauntlets of Strength', img: 'gauntlets', cost: 140 },
      { id: 16, name: 'Slippers of Agility', img: 'slippers', cost: 140 },
      { id: 17, name: 'Mantle of Intelligence', img: 'mantle', cost: 140 },
      { id: 31, name: 'Phase Boots', img: 'phase_boots', cost: 1500 },
      { id: 32, name: 'Power Treads', img: 'power_treads', cost: 1400 },
      // Fifth row
      { id: 18, name: 'Circlet', img: 'circlet', cost: 155 },
      { id: 19, name: 'Belt of Strength', img: 'belt_of_strength', cost: 450 },
      { id: 20, name: 'Band of Elvenskin', img: 'elvenskin', cost: 450 },
      { id: 21, name: 'Robe of the Magi', img: 'robe', cost: 450 },
      { id: 25, name: 'Orb of Venom', img: 'orb_of_venom', cost: 350 },
      // Sixth row
      { id: 22, name: 'Ogre Axe', img: 'ogre_axe', cost: 1000 },
      { id: 23, name: 'Blade of Alacrity', img: 'blade_of_alacrity', cost: 1000 },
      { id: 24, name: 'Staff of Wizardry', img: 'staff_of_wizardry', cost: 1000 },
      { id: 26, name: 'Ring of Protection', img: 'ring_of_protection', cost: 175 },
      { id: 27, name: 'Infused Raindrops', img: 'infused_raindrop', cost: 225 },
      // Seventh row
      { id: 28, name: 'Wind Lace', img: 'wind_lace', cost: 225 },
      { id: 29, name: 'Ring of Regen', img: 'ring_of_regen', cost: 175 },
      { id: 30, name: 'Sage\'s Mask', img: 'sobi_mask', cost: 175 },
      { id: 33, name: 'Gloves of Haste', img: 'gloves', cost: 450 },
      { id: 34, name: 'Ring of Health', img: 'ring_of_health', cost: 700 },
      // Eighth row
      { id: 35, name: 'Void Stone', img: 'void_stone', cost: 700 },
      { id: 36, name: 'Morbid Mask', img: 'morbid_mask', cost: 900 },
      { id: 37, name: 'Energy Booster', img: 'energy_booster', cost: 800 },
      { id: 38, name: 'Vitality Booster', img: 'vitality_booster', cost: 1000 },
      { id: 39, name: 'Point Booster', img: 'point_booster', cost: 1200 },
      // Ninth row (remaining basic items)
      { id: 40, name: 'Platemail', img: 'platemail', cost: 1400 },
      { id: 41, name: 'Talisman of Evasion', img: 'talisman_of_evasion', cost: 1300 },
      { id: 42, name: 'Hyperstone', img: 'hyperstone', cost: 2000 },
      { id: 43, name: 'Ultimate Orb', img: 'ultimate_orb', cost: 2800 },
      { id: 44, name: 'Demon Edge', img: 'demon_edge', cost: 2200 },
      // Tenth row
      { id: 45, name: 'Mystic Staff', img: 'mystic_staff', cost: 2800 },
      { id: 46, name: 'Reaver', img: 'reaver', cost: 2800 },
      { id: 47, name: 'Eaglesong', img: 'eagle', cost: 2800 },
      { id: 48, name: 'Sacred Relic', img: 'relic', cost: 3400 },
      { id: 49, name: 'Quarterstaff', img: 'quarterstaff', cost: 875 },
      // Additional items from basics
      { id: 50, name: 'Claymore', img: 'claymore', cost: 1350 },
      { id: 51, name: 'Javelin', img: 'javelin', cost: 900 },
      { id: 52, name: 'Broadsword', img: 'broadsword', cost: 1000 },
      { id: 53, name: 'Blades of Attack', img: 'blades_of_attack', cost: 450 },
      { id: 54, name: 'Chainmail', img: 'chainmail', cost: 550 },
      { id: 55, name: 'Cloak', img: 'cloak', cost: 800 },
      // Additional items requested by user to appear in BASICS
      { id: 57, name: 'Faerie Fire', img: 'faerie_fire', cost: 65 },
      { id: 58, name: 'Smoke of Deceit', img: 'smoke_of_deceit', cost: 50 },
      { id: 59, name: 'Fluffy Hat', img: 'fluffy_hat', cost: 250 },
      { id: 60, name: 'Sentry Ward', img: 'ward_sentry', cost: 50 },
      { id: 61, name: 'Blight Stone', img: 'blight_stone', cost: 300 },
      { id: 62, name: 'Enchanted Mango', img: 'enchanted_mango', cost: 65 },
      { id: 63, name: 'Gem of True Sight', img: 'gem', cost: 900 },
      { id: 64, name: 'Crown', img: 'crown', cost: 450 },
      { id: 65, name: 'Ghost Scepter', img: 'ghost', cost: 1500 },
      { id: 66, name: 'Helm of Iron Will', img: 'helm_of_iron_will', cost: 975 },
      { id: 67, name: 'Shadow Amulet', img: 'shadow_amulet', cost: 1000 },
      { id: 68, name: 'Dust of Appearance', img: 'dust', cost: 80 },
      { id: 69, name: 'Mithril Hammer', img: 'mithril_hammer', cost: 1600 },
      { id: 70, name: 'Cornucopia', img: 'cornucopia', cost: 1200 },
      { id: 71, name: 'Blood Grenade', img: 'blood_grenade', cost: 50 },
      { id: 72, name: 'Bottle', img: 'bottle', cost: 675 },
      { id: 73, name: 'Diadem', img: 'diadem', cost: 1000 },
      { id: 74, name: 'Orb of Frost', img: 'frost_orb', cost: 250 },
      { id: 75, name: 'Blitz Knuckles', img: 'blitz_knuckles', cost: 1000 },
      { id: 76, name: 'Tiara of Selemene', img: 'tiara_of_selemene', cost: 1800 },
      { id: 77, name: 'Voodoo Mask', img: 'voodoo_mask', cost: 700 },
      
      // Add the combined ward item
      { id: 78, name: 'Observer and Sentry Wards', img: 'ward_observer_and_sentry', cost: 50 },
    ];
    
    // Upgrade Items - more complex items that are built from components
    const upgradeItems = [
      // Accessories 
      { id: 101, name: 'Boots of Travel', img: 'travel_boots', cost: 2500 },
      { id: 112, name: 'Hand of Midas', img: 'hand_of_midas', cost: 2200 },
      { id: 182, name: 'Magic Wand', img: 'magic_wand', cost: 450 },
      { id: 183, name: 'Mask of Madness', img: 'mask_of_madness', cost: 1900 },
      { id: 184, name: 'Moon Shard', img: 'moon_shard', cost: 4000 },
      { id: 185, name: 'Null Talisman', img: 'null_talisman', cost: 505 },
      { id: 176, name: 'Perseverance', img: 'pers', cost: 1400 },
      { id: 32, name: 'Power Treads', img: 'power_treads', cost: 1400 },
      { id: 12, name: 'Soul Ring', img: 'soul_ring', cost: 805 },
      { id: 186, name: 'Orb of Corrosion', img: 'orb_of_corrosion', cost: 1000 },
      { id: 31, name: 'Phase Boots', img: 'phase_boots', cost: 1500 },
      { id: 168, name: 'Bracer', img: 'bracer', cost: 505 },
      { id: 160, name: 'Falcon Blade', img: 'falcon_blade', cost: 1125 },
      { id: 169, name: 'Wraith Band', img: 'wraith_band', cost: 505 },
      
      // Support
      { id: 164, name: 'Arcane Boots', img: 'arcane_boots', cost: 1400 },
      { id: 187, name: 'Boots of Bearing', img: 'boots_of_bearing', cost: 4225 },
      { id: 179, name: 'Buckler', img: 'buckler', cost: 425 },
      { id: 188, name: 'Drum of Endurance', img: 'ancient_janggo', cost: 1625 },
      { id: 136, name: 'Guardian Greaves', img: 'guardian_greaves', cost: 5050 },
      { id: 180, name: 'Headdress', img: 'headdress', cost: 425 },
      { id: 174, name: 'Holy Locket', img: 'holy_locket', cost: 2250 },
      { id: 135, name: 'Mekansm', img: 'mekansm', cost: 1775 },
      { id: 189, name: 'Pavise', img: 'pavise', cost: 1400 },
      { id: 130, name: 'Pipe of Insight', img: 'pipe', cost: 3725 },
      { id: 190, name: 'Parasma', img: 'prism', cost: 5975 },
      { id: 13, name: 'Ring of Basilius', img: 'ring_of_basilius', cost: 425 },
      { id: 156, name: 'Spirit Vessel', img: 'spirit_vessel', cost: 2780 },
      { id: 165, name: 'Tranquil Boots', img: 'tranquil_boots', cost: 900 },
      { id: 181, name: 'Urn of Shadows', img: 'urn_of_shadows', cost: 880 },
      { id: 173, name: 'Vladmir\'s Offering', img: 'vladmir', cost: 2200 },
      
      // Magical
      { id: 140, name: 'Aether Lens', img: 'aether_lens', cost: 2275 },
      { id: 161, name: 'Aghanim\'s Blessing', img: 'ultimate_scepter_2', cost: 5800 },
      { id: 102, name: 'Aghanim\'s Scepter', img: 'ultimate_scepter', cost: 4200 },
      { id: 124, name: 'Dagon', img: 'dagon', cost: 2850 },
      { id: 133, name: 'Eul\'s Scepter of Divinity', img: 'cyclone', cost: 2600 },
      { id: 131, name: 'Force Staff', img: 'force_staff', cost: 2200 },
      { id: 134, name: 'Glimmer Cape', img: 'glimmer_cape', cost: 2150 },
      { id: 191, name: 'Gleipnir', img: 'gungir', cost: 4550 },
      { id: 141, name: 'Octarine Core', img: 'octarine_core', cost: 4800 },
      { id: 117, name: 'Orchid Malevolence', img: 'orchid', cost: 3275 },
      { id: 110, name: 'Refresher Orb', img: 'refresher', cost: 5000 },
      { id: 192, name: 'Rod of Atos', img: 'rod_of_atos', cost: 2250 },
      { id: 116, name: 'Scythe of Vyse', img: 'sheepstick', cost: 5200 },
      { id: 137, name: 'Solar Crest', img: 'solar_crest', cost: 2575 },
      { id: 178, name: 'Veil of Discord', img: 'veil_of_discord', cost: 1725 },
      { id: 163, name: 'Witch Blade', img: 'witch_blade', cost: 2775 },
      { id: 193, name: 'Wind Waker', img: 'wind_waker', cost: 6800 },
      
      // Armor
      { id: 145, name: 'Aeon Disk', img: 'aeon_disk', cost: 3000 },
      { id: 104, name: 'Assault Cuirass', img: 'assault', cost: 5125 },
      { id: 200, name: 'Black King Bar', img: 'black_king_bar', cost: 4050 },
      { id: 118, name: 'Bloodstone', img: 'bloodstone', cost: 4400 },
      { id: 129, name: 'Crimson Guard', img: 'crimson_guard', cost: 3725 },
      { id: 194, name: 'Eternal Shroud', img: 'eternal_shroud', cost: 3700 },
      { id: 105, name: 'Heart of Tarrasque', img: 'heart', cost: 5200 },
      { id: 132, name: 'Hurricane Pike', img: 'hurricane_pike', cost: 4450 },
      { id: 127, name: 'Linken\'s Sphere', img: 'sphere', cost: 4800 },
      { id: 142, name: 'Lotus Orb', img: 'lotus_orb', cost: 3850 },
      { id: 109, name: 'Manta Style', img: 'manta', cost: 4650 },
      { id: 115, name: 'Shiva\'s Guard', img: 'shivas_guard', cost: 5175 },
      { id: 195, name: 'Soul Booster', img: 'soul_booster', cost: 3000 },
      { id: 128, name: 'Vanguard', img: 'vanguard', cost: 1700 },
      
      // Weapons
      { id: 138, name: 'Abyssal Blade', img: 'abyssal_blade', cost: 6250 },
      { id: 153, name: 'Armlet of Mordiggian', img: 'armlet', cost: 2500 },
      { id: 120, name: 'Battle Fury', img: 'bfury', cost: 3900 },
      { id: 196, name: 'Bloodthorn', img: 'bloodthorn', cost: 6625 },
      { id: 106, name: 'Butterfly', img: 'butterfly', cost: 5450 },
      { id: 159, name: 'Crystalys', img: 'lesser_crit', cost: 2000 },
      { id: 103, name: 'Daedalus', img: 'greater_crit', cost: 5100 },
      { id: 113, name: 'Desolator', img: 'desolator', cost: 3500 },
      { id: 197, name: 'Disperser', img: 'disperser', cost: 6100 },
      { id: 198, name: 'Divine Rapier', img: 'rapier', cost: 5600 },
      { id: 114, name: 'Ethereal Blade', img: 'ethereal_blade', cost: 5375 },
      { id: 199, name: 'Khanda', img: 'khanda', cost: 5900 },
      { id: 154, name: 'Meteor Hammer', img: 'meteor_hammer', cost: 2850 },
      { id: 122, name: 'Monkey King Bar', img: 'monkey_king_bar', cost: 4700 },
      { id: 155, name: 'Nullifier', img: 'nullifier', cost: 4375 },
      { id: 121, name: 'Radiance', img: 'radiance', cost: 4700 },
      { id: 200, name: 'Revenant\'s Brooch', img: 'revenants_brooch', cost: 6300 },
      { id: 125, name: 'Shadow Blade', img: 'shadow_blade', cost: 3350 },
      { id: 126, name: 'Silver Edge', img: 'silver_edge', cost: 5800 },
      { id: 152, name: 'Skull Basher', img: 'basher', cost: 2875 },
      
      // Armaments
      { id: 201, name: 'Arcane Blink', img: 'arcane_blink', cost: 6800 },
      { id: 123, name: 'Diffusal Blade', img: 'diffusal_blade', cost: 2500 },
      { id: 144, name: 'Dragon Lance', img: 'dragon_lance', cost: 1900 },
      { id: 162, name: 'Echo Sabre', img: 'echo_sabre', cost: 2700 },
      { id: 107, name: 'Eye of Skadi', img: 'skadi', cost: 5300 },
      { id: 202, name: 'Harpoon', img: 'harpoon', cost: 4700 },
      { id: 139, name: 'Heaven\'s Halberd', img: 'heavens_halberd', cost: 2600 },
      { id: 146, name: 'Kaya', img: 'kaya', cost: 2100 },
      { id: 149, name: 'Kaya and Sange', img: 'kaya_and_sange', cost: 4200 },
      { id: 158, name: 'Maelstrom', img: 'maelstrom', cost: 2950 },
      { id: 203, name: 'Mage Slayer', img: 'mage_slayer', cost: 2800 },
      { id: 143, name: 'Mjollnir', img: 'mjollnir', cost: 5500 },
      { id: 204, name: 'Overwhelming Blink', img: 'overwhelming_blink', cost: 6800 },
      { id: 205, name: 'Phylactery', img: 'phylactery', cost: 2600 },
      { id: 147, name: 'Sange', img: 'sange', cost: 2100 },
      { id: 150, name: 'Sange and Yasha', img: 'sange_and_yasha', cost: 4200 },
      { id: 108, name: 'Satanic', img: 'satanic', cost: 5050 },
      { id: 206, name: 'Swift Blink', img: 'swift_blink', cost: 6800 },
      { id: 148, name: 'Yasha', img: 'yasha', cost: 2100 },
      { id: 151, name: 'Yasha and Kaya', img: 'yasha_and_kaya', cost: 4200 },
      { id: 166, name: 'Aghanim\'s Shard', img: 'aghanims_shard', cost: 1400 },
      { id: 119, name: 'Blade Mail', img: 'blade_mail', cost: 2300 },
      { id: 157, name: 'Helm of the Dominator', img: 'helm_of_the_dominator', cost: 2625 },
      { id: 207, name: 'Helm of the Overlord', img: 'helm_of_the_overlord', cost: 5725 },
      { id: 177, name: 'Hood of Defiance', img: 'hood_of_defiance', cost: 1500 },
      { id: 175, name: 'Oblivion Staff', img: 'oblivion_staff', cost: 1625 },
    ];
    
    // Neutral Items
    const neutralItems = [
      { id: 201, name: 'Philosopher\'s Stone', img: 'philosophers_stone', cost: 0 },
      { id: 202, name: 'Ironwood Tree', img: 'ironwood_tree', cost: 0 },
      { id: 203, name: 'Royal Jelly', img: 'royal_jelly', cost: 0 },
      { id: 204, name: 'Trusty Shovel', img: 'trusty_shovel', cost: 0 },
      { id: 205, name: 'Arcane Ring', img: 'arcane_ring', cost: 0 },
      { id: 206, name: 'Essence Ring', img: 'essence_ring', cost: 0 },
      { id: 207, name: 'Clumsy Net', img: 'clumsy_net', cost: 0 },
      { id: 208, name: 'Dragon Scale', img: 'dragon_scale', cost: 0 },
      { id: 209, name: 'Pupil\'s Gift', img: 'pupils_gift', cost: 0 },
      { id: 210, name: 'Vambrace', img: 'vambrace', cost: 0 },
      { id: 211, name: 'Grove Bow', img: 'grove_bow', cost: 0 },
      { id: 212, name: 'Enchanted Quiver', img: 'enchanted_quiver', cost: 0 },
      { id: 213, name: 'Nether Shawl', img: 'nether_shawl', cost: 0 },
      { id: 214, name: 'Mind Breaker', img: 'mind_breaker', cost: 0 },
      { id: 215, name: 'Quickening Charm', img: 'quickening_charm', cost: 0 },
      { id: 216, name: 'Spider Legs', img: 'spider_legs', cost: 0 },
      { id: 217, name: 'Paladin Sword', img: 'paladin_sword', cost: 0 },
      { id: 218, name: 'Titan Sliver', img: 'titan_sliver', cost: 0 },
      { id: 219, name: 'Ninja Gear', img: 'ninja_gear', cost: 0 },
      { id: 220, name: 'Illusionist\'s Cape', img: 'illusionists_cape', cost: 0 },
      { id: 221, name: 'Havoc Hammer', img: 'havoc_hammer', cost: 0 },
      { id: 222, name: 'The Leveller', img: 'the_leveller', cost: 0 },
      { id: 223, name: 'Minotaur Horn', img: 'minotaur_horn', cost: 0 },
      { id: 224, name: 'Flicker', img: 'flicker', cost: 0 }
    ];
    
    console.log(`Items loaded - Basics: ${basicItems.length}, Upgrades: ${upgradeItems.length}, Neutrals: ${neutralItems.length}`);
    return { basicItems, upgradeItems, neutralItems };
  };

  // Modify the showItemSelection function to handle slot-based selection
  const showItemSelection = (event: React.MouseEvent<HTMLDivElement>, heroId: number, initialSlotIndex: number) => {
    // First, clean up any existing modals and overlays to avoid conflicts
    const existingModals = document.querySelectorAll('.item-selection-modal');
    existingModals.forEach(modal => {
      if (document.body.contains(modal)) {
        document.body.removeChild(modal);
      }
    });
    
    const existingOverlays = document.querySelectorAll('.modal-backdrop-blur');
    existingOverlays.forEach(overlay => {
      if (document.body.contains(overlay)) {
        document.body.removeChild(overlay);
      }
    });
    
    // Remove any lingering tooltips
    const tooltips = document.querySelectorAll('div[style*="position: absolute"][style*="background: rgba(0, 0, 0, 0.9)"]');
    tooltips.forEach(t => {
      if (document.body.contains(t)) {
        document.body.removeChild(t);
      }
    });

    // Create backdrop overlay with blur effect
    const backdropOverlay = document.createElement('div');
    backdropOverlay.className = 'modal-backdrop-blur';
    backdropOverlay.style.position = 'fixed';
    backdropOverlay.style.top = '0';
    backdropOverlay.style.left = '0';
    backdropOverlay.style.right = '0';
    backdropOverlay.style.bottom = '0';
    backdropOverlay.style.zIndex = '999'; // Just below the modal
    backdropOverlay.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
    backdropOverlay.style.backdropFilter = 'blur(4px)';
    (backdropOverlay.style as any).WebkitBackdropFilter = 'blur(4px)'; // For Safari
    
    // Create a modal popup for item selection
    const selectElement = document.createElement('div');
    selectElement.className = 'item-selection-modal';
    selectElement.style.position = 'fixed';
    selectElement.style.zIndex = '1000';
    selectElement.style.top = '50%';
    selectElement.style.left = '50%';
    selectElement.style.transform = 'translate(-50%, -50%)';
    selectElement.style.background = '#111';
    selectElement.style.border = '1px solid #444';
    selectElement.style.borderRadius = '4px';
    selectElement.style.boxShadow = '0 0 20px rgba(0,0,0,0.5)';
    selectElement.style.padding = '15px';
    selectElement.style.width = '1200px'; // Increased width to accommodate everything
    selectElement.style.maxHeight = '90vh';
    selectElement.style.overflowY = 'auto';
    selectElement.style.display = 'flex'; // Use flexbox for layout
    
    // Create containers for the three sections: Radiant heroes, item shop, Dire heroes
    const radiantHeroesContainer = document.createElement('div');
    radiantHeroesContainer.style.width = '280px'; // Wider side panels
    radiantHeroesContainer.style.marginRight = '15px';
    radiantHeroesContainer.style.borderRight = '1px solid #333';
    radiantHeroesContainer.style.paddingRight = '15px';
    radiantHeroesContainer.style.overflowY = 'auto';
    radiantHeroesContainer.style.maxHeight = 'calc(90vh - 30px)';
    radiantHeroesContainer.style.background = 'rgba(102, 187, 106, 0.05)'; // Very subtle green background
    radiantHeroesContainer.style.borderRadius = '4px';
    radiantHeroesContainer.style.padding = '10px';
    radiantHeroesContainer.style.flexShrink = '0'; // Prevent shrinking
    
    const shopContainer = document.createElement('div');
    shopContainer.style.flex = '1';
    shopContainer.style.minWidth = '550px'; // Increased width for item shop
    
    const direHeroesContainer = document.createElement('div');
    direHeroesContainer.style.width = '280px'; // Wider side panels
    direHeroesContainer.style.marginLeft = '15px';
    direHeroesContainer.style.borderLeft = '1px solid #333';
    direHeroesContainer.style.paddingLeft = '15px';
    direHeroesContainer.style.overflowY = 'auto';
    direHeroesContainer.style.maxHeight = 'calc(90vh - 30px)';
    direHeroesContainer.style.background = 'rgba(239, 83, 80, 0.05)'; // Very subtle red background
    direHeroesContainer.style.borderRadius = '4px';  
    direHeroesContainer.style.padding = '10px';
    direHeroesContainer.style.flexShrink = '0'; // Prevent shrinking
    
    // Helper function to create team header
    const createTeamHeader = (teamName: string, isRadiant: boolean) => {
      const header = document.createElement('div');
      header.textContent = teamName;
      header.style.color = isRadiant ? '#66bb6a' : '#ef5350';
      header.style.fontWeight = 'bold';
      header.style.fontSize = '18px';
      header.style.marginBottom = '20px';
      header.style.padding = '8px 0';
      header.style.borderBottom = `2px solid ${isRadiant ? '#66bb6a' : '#ef5350'}`;
      header.style.textAlign = 'center';
      header.style.width = '100%';
      header.style.letterSpacing = '1px';
      return header;
    };
    
    // Helper function to create hero row
    const createHeroRow = (hero: Hero) => {
      const heroRow = document.createElement('div');
      heroRow.className = 'hero-row';
      heroRow.dataset.heroId = hero.id.toString();
      
      // Check if this is the currently selected hero and add highlighting
      const isSelectedHero = hero.id === heroId;
      
      heroRow.style.display = 'flex';
      heroRow.style.flexDirection = 'column';
      heroRow.style.marginBottom = '20px';
      heroRow.style.background = isSelectedHero ? (isHeroRadiant(hero) ? 'rgba(102, 187, 106, 0.3)' : 'rgba(239, 83, 80, 0.3)') : '#1a1a1a';
      heroRow.style.borderRadius = '4px';
      heroRow.style.padding = '8px';
      heroRow.style.transition = 'all 0.2s ease-in-out';
      
      // Hero info section (hero image + name)
      const heroInfo = document.createElement('div');
      heroInfo.style.display = 'flex';
      heroInfo.style.alignItems = 'center';
      heroInfo.style.marginBottom = '8px';
      
      // Hero avatar
      const heroAvatar = document.createElement('div');
      heroAvatar.style.width = '48px';
      heroAvatar.style.height = '48px';
      heroAvatar.style.marginRight = '12px';
      heroAvatar.style.borderRadius = '4px';
      heroAvatar.style.overflow = 'hidden';
      heroAvatar.style.background = '#1e1e1e';
      heroAvatar.style.border = isHeroRadiant(hero) ? '2px solid #66bb6a' : '2px solid #ef5350';
      heroAvatar.style.display = 'flex';
      heroAvatar.style.justifyContent = 'center';
      heroAvatar.style.alignItems = 'center';
      heroAvatar.style.flexShrink = '0';
      
      const heroImg = document.createElement('img');
      heroImg.src = hero.imageUrl;
      heroImg.alt = hero.name;
      heroImg.style.width = '44px';
      heroImg.style.height = '44px';
      heroImg.style.objectFit = 'cover';
      
      heroAvatar.appendChild(heroImg);
      
      // Hero name and gold
      const heroDetails = document.createElement('div');
      
      const heroName = document.createElement('div');
      heroName.textContent = hero.name;
      heroName.style.fontSize = '16px';
      heroName.style.fontWeight = 'bold';
      heroName.style.color = '#fff';
      heroName.style.marginBottom = '4px';
      
      // Calculate gold
      const currentMinute = Math.floor(currentTimePoint / 60);
      const heroItems = userItems.get(hero.id) || new Map<number, Item>();
      let totalItemsCost = 0;
      heroItems.forEach(item => {
        if (item.cost) {
          totalItemsCost += item.cost;
        }
      });
      
      const netWorthData = playerNetWorth.get(hero.id);
      const currentNetWorth = netWorthData && netWorthData.length > currentMinute 
        ? netWorthData[currentMinute] : 0;
      
      const simulationCurrency = currentNetWorth - totalItemsCost;
      
      const heroGold = document.createElement('div');
      heroGold.style.fontSize = '14px';
      heroGold.style.color = '#ffeb3b';
      heroGold.style.fontWeight = 'bold';
      heroGold.style.display = 'flex';
      heroGold.style.alignItems = 'center';
      heroGold.textContent = Math.max(0, simulationCurrency).toLocaleString();
      
      const goldIcon = document.createElement('span');
      goldIcon.textContent = '🪙';
      goldIcon.style.fontSize = '12px';
      goldIcon.style.marginLeft = '4px';
      
      heroGold.appendChild(goldIcon);
      heroDetails.appendChild(heroName);
      heroDetails.appendChild(heroGold);
      
      heroInfo.appendChild(heroAvatar);
      heroInfo.appendChild(heroDetails);
      heroRow.appendChild(heroInfo);
      
      // Item slots section - updated to match renderHeroTimeline implementation
      const itemSlotsContainer = document.createElement('div');
      itemSlotsContainer.style.display = 'flex';
      itemSlotsContainer.style.flexWrap = 'nowrap';
      itemSlotsContainer.style.gap = '4px';
      itemSlotsContainer.style.marginTop = '8px';
      itemSlotsContainer.style.alignItems = 'center';
      itemSlotsContainer.style.justifyContent = 'flex-start';
      itemSlotsContainer.style.width = '100%';
      
      // Create 6 item slots
      for (let slotIndex = 0; slotIndex < 6; slotIndex++) {
        // Get user-selected items
        const heroItems = userItems.get(hero.id);
        
        // Item slot styling
        const itemSlot = document.createElement('div');
        
        // If there's an item in this slot, show it
        if (heroItems && heroItems.has(slotIndex)) {
          const item = heroItems.get(slotIndex)!;
          
          // Calculate if the item was acquired in the past, present or future based on timeline
          const currentMinute = Math.floor(currentTimePoint / 60);
          const itemAcquiredMinute = item.timestamp || 0;
          
          // Determine timing class - but always show the item
          let timingClass = '';
          let borderStyle = '1px solid #444';
          
          if (itemAcquiredMinute <= currentMinute) {
            // Item was acquired in the past or at the current time
            timingClass = 'acquired';
            borderStyle = '1px solid #4caf50'; // Green border for acquired items
          } else {
            // Item will be acquired in the future
            timingClass = 'planned';
            borderStyle = '1px dashed #ffeb3b'; // Yellow dashed border for planned items
          }

          itemSlot.style.width = '48px';
          itemSlot.style.height = '48px';
          itemSlot.style.background = '#1a1a1a';
          itemSlot.style.borderRadius = '3px';
          itemSlot.style.overflow = 'hidden';
          itemSlot.style.border = borderStyle;
          itemSlot.style.position = 'relative';
          itemSlot.style.cursor = 'pointer';
          itemSlot.style.display = 'flex';
          itemSlot.style.justifyContent = 'center';
          itemSlot.style.alignItems = 'center';
          
          // Set up click handler for item selection
          itemSlot.onclick = (event) => {
            // Use the existing showItemSelection function
            showItemSelection(event as unknown as React.MouseEvent<HTMLDivElement>, hero.id, slotIndex);
          };
          
          // Set up right-click handler for item removal
          itemSlot.oncontextmenu = (event) => {
            event.preventDefault();
            removeHeroItem(hero.id, slotIndex);
            
            // Update the modal to reflect the change - rerender this hero row
            const parentContainer = itemSlot.closest('.hero-row');
            if (parentContainer) {
              const newRow = createHeroRow(hero);
              parentContainer.replaceWith(newRow);
            }
          };
          
          // Set title/tooltip
          itemSlot.title = `${item.name} (${itemAcquiredMinute}:00)${item.cost ? '\nCost: ' + item.cost : ''}\nRight-click to remove`;
          
          // Create and add image
          const itemImg = document.createElement('img');
          itemImg.src = getItemImageUrl(item.name);
          itemImg.alt = item.name || '';
          itemImg.style.width = '100%';
          itemImg.style.height = '100%';
          itemImg.style.objectFit = 'contain';
          
          // Handle image error
          itemImg.onerror = () => {
            if (itemImg.src !== item.alternativeImageUrl && item.alternativeImageUrl) {
              itemImg.src = item.alternativeImageUrl;
            } else {
              itemImg.src = 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/blink_lg.png';
            }
          };
          
          // Add timestamp indicator
          const timeLabel = document.createElement('div');
          timeLabel.textContent = `${itemAcquiredMinute}m`;
          timeLabel.style.position = 'absolute';
          timeLabel.style.bottom = '0';
          timeLabel.style.right = '0';
          timeLabel.style.background = timingClass === 'acquired' ? 'rgba(0,0,0,0.7)' : 'rgba(255,235,59,0.7)';
          timeLabel.style.color = timingClass === 'acquired' ? 'white' : 'black';
          timeLabel.style.fontSize = '10px';
          timeLabel.style.padding = '1px 3px';
          timeLabel.style.borderTopLeftRadius = '2px';
          
          itemSlot.appendChild(itemImg);
          itemSlot.appendChild(timeLabel);
        } else {
          // Empty slot - make it clickable too
          itemSlot.style.width = '48px';
          itemSlot.style.height = '48px';
          itemSlot.style.background = '#222';
          itemSlot.style.borderRadius = '3px';
          itemSlot.style.display = 'flex';
          itemSlot.style.justifyContent = 'center';
          itemSlot.style.alignItems = 'center';
          itemSlot.style.cursor = 'pointer';
          itemSlot.style.border = '1px solid #333';
          
          // Set up click handler
          itemSlot.onclick = (event) => {
            showItemSelection(event as unknown as React.MouseEvent<HTMLDivElement>, hero.id, slotIndex);
          };
          
          // Add empty slot placeholder
          const emptyText = document.createElement('div');
          emptyText.textContent = '0m';
          emptyText.style.fontSize = '10px';
          emptyText.style.color = '#666';
          
          itemSlot.appendChild(emptyText);
        }
        
        itemSlotsContainer.appendChild(itemSlot);
      }
      
      heroRow.appendChild(itemSlotsContainer);
      
      // Add pulsating highlight effect if this is the selected hero
      if (isSelectedHero) {
        // Add a glow effect to the hero row
        heroRow.style.boxShadow = isHeroRadiant(hero) 
          ? '0 0 15px rgba(102, 187, 106, 0.7)' 
          : '0 0 15px rgba(239, 83, 80, 0.7)';
        
        // Setup animation
        const keyframes = `
          @keyframes pulse-${hero.id} {
            0% { box-shadow: 0 0 15px rgba(${isHeroRadiant(hero) ? '102, 187, 106' : '239, 83, 80'}, 0.7); }
            50% { box-shadow: 0 0 20px rgba(${isHeroRadiant(hero) ? '102, 187, 106' : '239, 83, 80'}, 1); }
            100% { box-shadow: 0 0 15px rgba(${isHeroRadiant(hero) ? '102, 187, 106' : '239, 83, 80'}, 0.7); }
          }
        `;
        
        const style = document.createElement('style');
        style.innerHTML = keyframes;
        document.head.appendChild(style);
        
        heroRow.style.animation = `pulse-${hero.id} 1.5s infinite`;
        
        // Add a colored border to indicate this is the selected hero
        heroRow.style.border = isHeroRadiant(hero) 
          ? '2px solid #66bb6a' 
          : '2px solid #ef5350';
      }
      
      return heroRow;
    };
    
    // Add Radiant heroes
    radiantHeroesContainer.appendChild(createTeamHeader(radiantTeamName, true));
    
    // Add the Radiant heroes
    radiantHeroes.forEach(hero => {
      const heroElement = createHeroRow(hero);
      radiantHeroesContainer.appendChild(heroElement);
    });
    
    // Add Dire heroes
    direHeroesContainer.appendChild(createTeamHeader(direTeamName, false));
    
    // Add the Dire heroes
    direHeroes.forEach(hero => {
      const heroElement = createHeroRow(hero);
      direHeroesContainer.appendChild(heroElement);
    });
    
    // Append the three main containers to the modal
    selectElement.appendChild(radiantHeroesContainer);
    selectElement.appendChild(shopContainer);
    selectElement.appendChild(direHeroesContainer);
    
    // Function to properly close modal and remove all elements/event listeners
    const closeModal = () => {
      // Remove all modals
      const modals = document.querySelectorAll('.item-selection-modal');
      modals.forEach(modal => {
        if (document.body.contains(modal)) {
          document.body.removeChild(modal);
        }
      });
      
      // Remove all backdrop elements
      const backdropElements = document.querySelectorAll('.modal-backdrop-blur');
      backdropElements.forEach(backdrop => {
        if (document.body.contains(backdrop)) {
          document.body.removeChild(backdrop);
        }
      });
      
      // Remove any tooltips that might still be present
      const tooltips = document.querySelectorAll('div[style*="position: absolute"][style*="background: rgba(0, 0, 0, 0.9)"]');
      tooltips.forEach(tooltip => {
        if (document.body.contains(tooltip)) {
          document.body.removeChild(tooltip);
        }
      });
      
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('click', handleModalClickOutside);
    };
    
    // Add backdrop click handler
    backdropOverlay.addEventListener('click', () => {
      closeModal();
    });
    
    document.body.appendChild(backdropOverlay);
    document.body.appendChild(selectElement);
    
    // Keep track of current slot index
    let currentSlotIndex = initialSlotIndex;
    
    // Function to find next available slot - simplified since we're not progressing through slots anymore
    const findNextAvailableSlot = () => {
      return initialSlotIndex;
    };
    
    // On modal open, update currentSlotIndex to the initial slot
    currentSlotIndex = initialSlotIndex;
    
    // Update handleItemSelect and closeModal to maintain consistent behavior
    const originalHandleItemSelect = handleItemSelect;
    
    // Temporarily override handleItemSelect to include slot progression
    const modalHandleItemSelect = (heroId: number, slotIndex: number, itemId: number) => {
      // Call the original handleItemSelect function
      originalHandleItemSelect(heroId, slotIndex, itemId);
      
      // Close the modal immediately after selecting an item, regardless of slot
      closeModal();
      
      // Log the updated spent gold for debugging
      setTimeout(() => {
        const updatedSpentGold = spentGold.get(heroId) || 0;
        const currentMin = Math.floor(currentTimePoint / 60);
        const { availableCurrency: finalCurrency } = calculateAvailableCurrency(heroId, currentMin);
        console.log(`After selection - Spent gold: ${updatedSpentGold}, Available: ${finalCurrency}`);
      }, 50);
    };
    
    const updateTitle = () => {
      if (title) {
        const currentMinute = Math.floor(currentTimePoint / 60);
        const { availableCurrency } = calculateAvailableCurrency(heroId, currentMinute);
        
        // Create title container with flex layout
        title.innerHTML = '';
        title.style.display = 'flex';
        title.style.flexDirection = 'row';
        title.style.justifyContent = 'space-between';
        title.style.alignItems = 'center';
        title.style.gap = '10px';
        
        // Create currency display
        const currencyDisplay = document.createElement('div');
        currencyDisplay.style.display = 'flex';
        currencyDisplay.style.alignItems = 'center';
        currencyDisplay.style.gap = '5px';
        
        // Gold icon
        const goldIcon = document.createElement('span');
        goldIcon.textContent = '💰';
        goldIcon.style.fontSize = '16px';
        
        // Currency amount
        const currencyAmount = document.createElement('span');
        currencyAmount.textContent = `${availableCurrency}`;
        currencyAmount.style.color = '#ffcc00';
        currencyAmount.style.fontWeight = 'bold';
        currencyAmount.style.fontSize = '16px';
        
        currencyDisplay.appendChild(goldIcon);
        currencyDisplay.appendChild(currencyAmount);
        
        // Display error message if there is one
        const errorMsg = errorMessages.get(heroId);
        if (errorMsg) {
          // Add error message directly in the currency display
          const errorText = document.createElement('span');
          errorText.textContent = ` • ${errorMsg}`;
          errorText.style.color = '#ff4d4d';
          errorText.style.fontWeight = 'bold';
          errorText.style.marginLeft = '10px';
          currencyDisplay.appendChild(errorText);
        }
        
        // Add the currency display to the title
        title.appendChild(currencyDisplay);
        
        // Add slot information to the title
        const slotInfo = document.createElement('div');
        slotInfo.style.color = '#aaa';
        slotInfo.style.fontSize = '14px';
        slotInfo.textContent = `Selecting for slot ${initialSlotIndex + 1}`;
        title.appendChild(slotInfo);
      }
    };
    
    // Create the shop interface
    // const shopContainer = document.createElement('div');
    
    // Add close button
    const closeButton = document.createElement('button');
    closeButton.innerHTML = '×';
    closeButton.style.position = 'absolute';
    closeButton.style.right = '10px';
    closeButton.style.top = '10px';
    closeButton.style.background = 'transparent';
    closeButton.style.border = 'none';
    closeButton.style.color = '#666';
    closeButton.style.fontSize = '24px';
    closeButton.style.cursor = 'pointer';
    closeButton.style.padding = '5px';
    closeButton.style.lineHeight = '1';
    closeButton.style.transition = 'color 0.2s';
    closeButton.addEventListener('mouseover', () => closeButton.style.color = '#fff');
    closeButton.addEventListener('mouseout', () => closeButton.style.color = '#666');
    closeButton.addEventListener('click', closeModal);
    
    selectElement.appendChild(closeButton);
    
    // Add title
    const title = document.createElement('div');
    title.style.marginBottom = '15px';
    title.style.color = '#ccc';
    title.style.borderBottom = '1px solid #333';
    title.style.paddingBottom = '10px';
    title.style.paddingRight = '30px'; // Make room for close button
    updateTitle(); // Call updateTitle to set initial content
    
    shopContainer.appendChild(title);
    
    // Add search bar
    const searchContainer = document.createElement('div');
    searchContainer.style.display = 'flex';
    searchContainer.style.marginBottom = '10px';
    searchContainer.style.background = '#222';
    searchContainer.style.border = '1px solid #444';
    searchContainer.style.padding = '5px';
    searchContainer.style.borderRadius = '4px';
    
    const searchIcon = document.createElement('div');
    searchIcon.innerHTML = '🔍';
    searchIcon.style.marginRight = '5px';
    
    const searchInput = document.createElement('input');
    searchInput.type = 'text';
    searchInput.placeholder = 'Search...';
    searchInput.style.flex = '1';
    searchInput.style.background = 'transparent';
    searchInput.style.border = 'none';
    searchInput.style.color = '#fff';
    searchInput.style.outline = 'none';
    
    searchContainer.appendChild(searchIcon);
    searchContainer.appendChild(searchInput);
    
    shopContainer.appendChild(searchContainer);
    
    // Auto-focus the search input when the modal opens
    setTimeout(() => {
      searchInput.focus();
    }, 50);
    
    // Add event listener for Escape key
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeModal();
        
        // Extra check to remove any lingering tooltips
        const tooltips = document.querySelectorAll('div[style*="position: absolute"][style*="background: rgba(0, 0, 0, 0.9)"]');
        tooltips.forEach(t => {
          if (document.body.contains(t)) {
            document.body.removeChild(t);
          }
        });
      }
    };
    document.addEventListener('keydown', handleEscape);
    
    // Add click outside handler
    const handleModalClickOutside = (e: MouseEvent) => {
      if (!selectElement.contains(e.target as Node)) {
        closeModal();
        
        // Extra check to remove any lingering tooltips
        const tooltips = document.querySelectorAll('div[style*="position: absolute"][style*="background: rgba(0, 0, 0, 0.9)"]');
        tooltips.forEach(t => {
          if (document.body.contains(t)) {
            document.body.removeChild(t);
          }
        });
      }
    };
    
    // Delay adding click outside handler to prevent immediate closing
    setTimeout(() => {
      document.addEventListener('click', handleModalClickOutside);
    }, 100);
    
    // Create tabs
    const tabsContainer = document.createElement('div');
    tabsContainer.style.display = 'flex';
    tabsContainer.style.marginBottom = '10px';
    tabsContainer.style.borderBottom = '1px solid #333';
    tabsContainer.style.position = 'relative';
    tabsContainer.style.justifyContent = 'center';
    
    const tabs = ['BASICS', 'UPGRADES']; // Removed NEUTRALS tab
    const tabElements: HTMLDivElement[] = [];
    
    tabs.forEach((tabName, index) => {
      const tab = document.createElement('div');
      tab.textContent = tabName;
      tab.style.padding = '10px 20px';
      tab.style.cursor = 'pointer';
      tab.style.borderBottom = index === 0 ? '2px solid #f39c12' : '2px solid transparent';
      tab.style.marginBottom = '-1px';
      tab.style.color = index === 0 ? '#f39c12' : '#999';
      tab.style.fontWeight = index === 0 ? 'bold' : 'normal';
      tab.style.transition = 'all 0.2s ease';
      tab.style.textAlign = 'center';
      tab.style.minWidth = '100px';
      tab.style.margin = '0 5px';
      tab.style.backgroundColor = index === 0 ? 'rgba(243, 156, 18, 0.1)' : 'transparent';
      tab.style.borderRadius = '4px 4px 0 0';
      
      // Add hover effect
      tab.addEventListener('mouseover', () => {
        if (!tab.style.color.includes('f39c12')) { // Only apply hover effect if not active
          tab.style.color = '#bbb';
          tab.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
        }
      });
      
      tab.addEventListener('mouseout', () => {
        if (!tab.style.color.includes('f39c12')) { // Only revert if not active
          tab.style.color = '#999';
          tab.style.backgroundColor = 'transparent';
        }
      });
      
      tab.addEventListener('click', () => {
        // Update active tab styles
        tabElements.forEach((t, i) => {
          t.style.borderBottom = i === index ? '2px solid #f39c12' : '2px solid transparent';
          t.style.color = i === index ? '#f39c12' : '#999';
          t.style.fontWeight = i === index ? 'bold' : 'normal';
          t.style.backgroundColor = i === index ? 'rgba(243, 156, 18, 0.1)' : 'transparent';
        });
        
        // Show the appropriate grid
        itemGrids.forEach((grid, i) => {
          grid.style.display = i === index ? 'grid' : 'none';
        });
        
        // Log tab selection for debugging
        console.log(`Selected tab: ${tabName} with ${itemGrids[index].childElementCount} items`);
      });
      
      tabElements.push(tab);
      tabsContainer.appendChild(tab);
    });
    
    shopContainer.appendChild(tabsContainer);
    
    // Create item grid container
    const itemGridsContainer = document.createElement('div');
    shopContainer.appendChild(itemGridsContainer);
    
    // Get all items
    const { basicItems, upgradeItems, neutralItems } = getDotaItems();
    
    // Create item grids for each tab
    const itemGrids: HTMLDivElement[] = [];
    
    // Create grid for each tab (only basics and upgrades)
    [basicItems, upgradeItems].forEach((items, index) => {
      const grid = document.createElement('div');
      grid.style.display = index === 0 ? 'grid' : 'none'; // Start with basic items visible
      grid.style.gridTemplateColumns = 'repeat(7, 1fr)'; // 7 columns for more compact layout
      grid.style.gap = '1px'; // Minimal gap between items
      grid.style.marginTop = '10px';
      
      // Debug log
      console.log(`Creating item grid for tab ${index} with ${items.length} items`);
      
      items.forEach(item => {
        const itemEl = document.createElement('div');
        itemEl.style.width = '70px'; // Slightly larger items
        itemEl.style.height = '60px'; // Increased height for larger display
        itemEl.style.background = '#222';
        itemEl.style.borderRadius = '2px';
        itemEl.style.overflow = 'hidden';
        itemEl.style.cursor = 'pointer';
        itemEl.style.border = 'none'; // Remove borders
        itemEl.style.position = 'relative';
        itemEl.style.transition = 'all 0.2s';
        itemEl.style.display = 'flex';
        itemEl.style.flexDirection = 'column';
        itemEl.style.justifyContent = 'center';
        itemEl.style.alignItems = 'center';
        itemEl.style.padding = '0'; // Remove padding
        
        // Hover effect - keep this for better UX
        itemEl.addEventListener('mouseover', () => {
          itemEl.style.transform = 'scale(1.05)'; // Smaller scale for less movement
          itemEl.style.zIndex = '2';
          
          // Create tooltip for item name
          const tooltip = document.createElement('div');
          tooltip.style.position = 'absolute';
          tooltip.style.bottom = '100%';
          tooltip.style.left = '50%';
          tooltip.style.transform = 'translateX(-50%)';
          tooltip.style.background = 'rgba(0, 0, 0, 0.9)';
          tooltip.style.color = 'white';
          tooltip.style.padding = '4px 8px';
          tooltip.style.borderRadius = '4px';
          tooltip.style.whiteSpace = 'nowrap';
          tooltip.style.zIndex = '100';
          tooltip.style.boxShadow = '0 2px 5px rgba(0, 0, 0, 0.5)';
          tooltip.style.fontSize = '12px';
          tooltip.style.pointerEvents = 'none';
          
          // Just show item name
          tooltip.textContent = item.name;
          
          document.body.appendChild(tooltip);
          itemEl.dataset.tooltip = 'active';
          
          // Position the tooltip
          const rect = itemEl.getBoundingClientRect();
          const tooltipRect = tooltip.getBoundingClientRect();
          
          // Ensure tooltip is visible within viewport
          if (rect.top < tooltipRect.height + 10) {
            tooltip.style.bottom = 'auto';
            tooltip.style.top = '100%';
            tooltip.style.marginTop = '5px';
          } else {
            tooltip.style.marginBottom = '5px';
          }
        });
        
        itemEl.addEventListener('mouseout', () => {
          itemEl.style.transform = 'scale(1)';
          itemEl.style.zIndex = '1';
          
          // Remove the tooltip
          if (itemEl.dataset.tooltip === 'active') {
            const tooltips = document.querySelectorAll('div[style*="position: absolute"][style*="background: rgba(0, 0, 0, 0.9)"]');
            tooltips.forEach(t => t.remove());
            delete itemEl.dataset.tooltip;
          }
        });
        
        // Create IMG element for item icon
        const img = document.createElement('img');
        
        // Use both the name and img property for maximum reliability
        let imgUrl = getItemImageUrl(item.name, item.img);
        
        // Set image properties
        img.src = imgUrl;
        img.alt = item.name;
        img.title = item.name;
        img.style.width = '100%'; // Full width
        img.style.height = '100%'; // Full height
        img.style.objectFit = 'contain';
        
        // Enhanced error handling with multiple fallbacks
        img.onerror = () => {
          if (item.img) {
            const alternativeUrl = `https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/${item.img}_lg.png`;
            img.src = alternativeUrl;
            
            // Second fallback if alternative also fails
            img.onerror = () => {
              img.src = 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/blink_lg.png';
            };
          } else {
            img.src = 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/blink_lg.png';
          }
        };
        
        // Add cost overlay directly on the image
        const costOverlay = document.createElement('div');
        costOverlay.style.position = 'absolute';
        costOverlay.style.bottom = '2px';
        costOverlay.style.right = '2px';
        costOverlay.style.background = 'rgba(0,0,0,0.7)';
        costOverlay.style.color = '#ffcc00'; // Gold color for cost
        costOverlay.style.padding = '1px 4px';
        costOverlay.style.fontSize = '12px';
        costOverlay.style.borderRadius = '2px';
        costOverlay.style.fontWeight = 'bold';
        costOverlay.textContent = item.cost ? item.cost.toString() : '0';
        
        // Select item on click - this is critical functionality
        itemEl.addEventListener('click', async (e) => {
          e.stopPropagation(); // Prevent click from bubbling to outside handler
          
          // Calculate currency and costs for validation
          const currentMinute = Math.floor(currentTimePoint / 60);
          const { availableCurrency: validationCurrency, totalItemsCost: currentTotalCost } = calculateAvailableCurrency(heroId, currentMinute);
          const itemCost = item.cost || 0;
          
          // Check if there's enough currency
          if (itemCost > validationCurrency) {
            const moneyNeeded = itemCost - validationCurrency;
            const errorMsg = `Need ${moneyNeeded} more for ${item.name}`;
            const newErrorMessages = new Map(errorMessages);
            newErrorMessages.set(heroId, errorMsg);
            setErrorMessages(newErrorMessages);
            
            // Update title to show error message
            updateTitle();
            
            // Clear the error message after 3 seconds
            setTimeout(() => {
              const updatedErrorMessages = new Map(errorMessages);
              updatedErrorMessages.delete(heroId);
              setErrorMessages(updatedErrorMessages);
              
              // Update title to remove error message
              updateTitle();
            }, 3000);
            
            console.log(`Not enough currency for ${item.name}. Have: ${validationCurrency}, Need: ${itemCost}, Total items cost: ${currentTotalCost}`);
            return;
          }
          
          // Use modalHandleItemSelect instead of handleItemSelect directly
          modalHandleItemSelect(heroId, currentSlotIndex, Number(item.id));
          
          // No need for additional closeModal logic here as modalHandleItemSelect handles it
        });
        
        itemEl.appendChild(img);
        itemEl.appendChild(costOverlay);
        grid.appendChild(itemEl);
      });
      
      itemGrids.push(grid);
      itemGridsContainer.appendChild(grid);
    });
    
    // Implement search functionality
    searchInput.addEventListener('input', () => {
      const searchTerm = searchInput.value.toLowerCase();
      
      // If search term is empty, show default tabs
      if (!searchTerm) {
        // Show tab grids and hide search results if they exist
        itemGrids.forEach((grid, i) => {
          grid.style.display = i === 0 ? 'grid' : 'none'; // Default to first tab
        });
        
        // Reset active tab styling
        tabElements.forEach((t, i) => {
          t.style.borderBottom = i === 0 ? '2px solid #f39c12' : '2px solid transparent';
          t.style.color = i === 0 ? '#f39c12' : '#999';
          t.style.fontWeight = i === 0 ? 'bold' : 'normal';
          t.style.backgroundColor = i === 0 ? 'rgba(243, 156, 18, 0.1)' : 'transparent';
        });
        
        // Remove search results grid if it exists
        const existingSearchResults = itemGridsContainer.querySelector('.search-results-grid');
        if (existingSearchResults) {
          existingSearchResults.remove();
        }
        
        return;
      }
      
      // Hide all tab grids
      itemGrids.forEach(grid => {
        grid.style.display = 'none';
      });
      
      // Reset tab styling (all inactive)
      tabElements.forEach(t => {
        t.style.borderBottom = '2px solid transparent';
        t.style.color = '#999';
        t.style.fontWeight = 'normal';
        t.style.backgroundColor = 'transparent';
      });
      
      // Remove existing search results grid if it exists
      const existingSearchResults = itemGridsContainer.querySelector('.search-results-grid');
      if (existingSearchResults) {
        existingSearchResults.remove();
      }
      
      // Create search results grid
      const searchResultsGrid = document.createElement('div');
      searchResultsGrid.className = 'search-results-grid';
      searchResultsGrid.style.display = 'grid';
      searchResultsGrid.style.gridTemplateColumns = 'repeat(7, 1fr)'; // 7 columns
      searchResultsGrid.style.gap = '1px';
      searchResultsGrid.style.marginTop = '10px';
      
      // Combine all items for search (only basics and upgrades)
      const allItems = [...basicItems, ...upgradeItems];
      
      // Filter items based on search term
      const filteredItems = allItems.filter(item => 
        item.name.toLowerCase().includes(searchTerm) || 
        (item.cost && item.cost.toString().includes(searchTerm))
      );
      
      console.log(`Search found ${filteredItems.length} items matching "${searchTerm}"`);
      
      // Add filtered items to search results grid
      filteredItems.forEach(item => {
        const itemEl = document.createElement('div');
        itemEl.style.width = '70px';
        itemEl.style.height = '60px';
        itemEl.style.background = '#222';
        itemEl.style.borderRadius = '2px';
        itemEl.style.overflow = 'hidden';
        itemEl.style.cursor = 'pointer';
        itemEl.style.border = 'none';
        itemEl.style.position = 'relative';
        itemEl.style.transition = 'all 0.2s';
        itemEl.style.display = 'flex';
        itemEl.style.flexDirection = 'column';
        itemEl.style.justifyContent = 'center';
        itemEl.style.alignItems = 'center';
        itemEl.style.padding = '0';
        
        // Hover effect
        itemEl.addEventListener('mouseover', () => {
          itemEl.style.transform = 'scale(1.05)';
          itemEl.style.zIndex = '2';
          
          // Create tooltip
          const tooltip = document.createElement('div');
          tooltip.style.position = 'absolute';
          tooltip.style.bottom = '100%';
          tooltip.style.left = '50%';
          tooltip.style.transform = 'translateX(-50%)';
          tooltip.style.background = 'rgba(0, 0, 0, 0.9)';
          tooltip.style.color = 'white';
          tooltip.style.padding = '4px 8px';
          tooltip.style.borderRadius = '4px';
          tooltip.style.whiteSpace = 'nowrap';
          tooltip.style.zIndex = '100';
          tooltip.style.boxShadow = '0 2px 5px rgba(0, 0, 0, 0.5)';
          tooltip.style.fontSize = '12px';
          tooltip.style.pointerEvents = 'none';
          tooltip.textContent = item.name;
          
          document.body.appendChild(tooltip);
          itemEl.dataset.tooltip = 'active';
          
          // Position the tooltip
          const rect = itemEl.getBoundingClientRect();
          const tooltipRect = tooltip.getBoundingClientRect();
          
          if (rect.top < tooltipRect.height + 10) {
            tooltip.style.bottom = 'auto';
            tooltip.style.top = '100%';
            tooltip.style.marginTop = '5px';
          } else {
            tooltip.style.marginBottom = '5px';
          }
        });
        
        itemEl.addEventListener('mouseout', () => {
          itemEl.style.transform = 'scale(1)';
          itemEl.style.zIndex = '1';
          
          // Remove the tooltip
          if (itemEl.dataset.tooltip === 'active') {
            const tooltips = document.querySelectorAll('div[style*="position: absolute"][style*="background: rgba(0, 0, 0, 0.9)"]');
            tooltips.forEach(t => t.remove());
            delete itemEl.dataset.tooltip;
          }
        });
        
        // Create IMG element
        const img = document.createElement('img');
        let imgUrl = getItemImageUrl(item.name, item.img);
        
        img.src = imgUrl;
        img.alt = item.name;
        img.title = item.name;
        img.style.width = '100%';
        img.style.height = '100%';
        img.style.objectFit = 'contain';
        
        // Error handling for image
        img.onerror = () => {
          if (item.img) {
            const alternativeUrl = `https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/${item.img}_lg.png`;
            img.src = alternativeUrl;
            
            img.onerror = () => {
              img.src = 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/blink_lg.png';
            };
          } else {
            img.src = 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/blink_lg.png';
          }
        };
        
        // Add cost overlay
        const costOverlay = document.createElement('div');
        costOverlay.style.position = 'absolute';
        costOverlay.style.bottom = '2px';
        costOverlay.style.right = '2px';
        costOverlay.style.background = 'rgba(0,0,0,0.7)';
        costOverlay.style.color = '#ffcc00';
        costOverlay.style.padding = '1px 4px';
        costOverlay.style.fontSize = '12px';
        costOverlay.style.borderRadius = '2px';
        costOverlay.style.fontWeight = 'bold';
        costOverlay.textContent = item.cost ? item.cost.toString() : '0';
        
        // Click event for item selection
        itemEl.addEventListener('click', async (e) => {
          e.stopPropagation();
          
          const currentMinute = Math.floor(currentTimePoint / 60);
          const { availableCurrency: validationCurrency, totalItemsCost: currentTotalCost } = calculateAvailableCurrency(heroId, currentMinute);
          const itemCost = item.cost || 0;
          
          if (itemCost > validationCurrency) {
            const moneyNeeded = itemCost - validationCurrency;
            const errorMsg = `Need ${moneyNeeded} more for ${item.name}`;
            const newErrorMessages = new Map(errorMessages);
            newErrorMessages.set(heroId, errorMsg);
            setErrorMessages(newErrorMessages);
            
            // Update title to show error message
            updateTitle();
            
            // Clear the error message after 3 seconds
            setTimeout(() => {
              const updatedErrorMessages = new Map(errorMessages);
              updatedErrorMessages.delete(heroId);
              setErrorMessages(updatedErrorMessages);
              
              // Update title to remove error message
              updateTitle();
            }, 3000);
            
            console.log(`Not enough currency for ${item.name}. Have: ${validationCurrency}, Need: ${itemCost}, Total items cost: ${currentTotalCost}`);
            return;
          }
          
          modalHandleItemSelect(heroId, currentSlotIndex, Number(item.id));
          
          await new Promise(resolve => setTimeout(resolve, 50));
          
          if (currentSlotIndex === 0) {
            closeModal();
          } else {
            currentSlotIndex = findNextAvailableSlot();
            updateTitle();
          }
          
          const updatedSpentGold = spentGold.get(heroId) || 0;
          const { availableCurrency: finalCurrency } = calculateAvailableCurrency(heroId, currentMinute);
          console.log(`After selection - Spent gold: ${updatedSpentGold}, Available: ${finalCurrency}`);
        });
        
        itemEl.appendChild(img);
        itemEl.appendChild(costOverlay);
        searchResultsGrid.appendChild(itemEl);
      });
      
      // Add search results to container
      itemGridsContainer.appendChild(searchResultsGrid);
    });
    
    // Assemble the shop UI
    shopContainer.appendChild(title);
    shopContainer.appendChild(searchContainer);
    shopContainer.appendChild(tabsContainer);
    shopContainer.appendChild(itemGridsContainer);
    selectElement.appendChild(shopContainer);
    selectElement.appendChild(closeButton);
    
    // Variable to track if modal has been removed
    let modalRemoved = false;
    
    // Close when clicking outside
    const handleClickOutside = (e: MouseEvent) => {
      const selectElements = document.querySelectorAll('.item-selection-modal');
      let clickedInsideModal = false;
      
      // Check if click was inside any of the modals
      selectElements.forEach(element => {
        if (element.contains(e.target as Node)) {
          clickedInsideModal = true;
        }
      });
      
      if (!clickedInsideModal && !modalRemoved) {
        closeModal();
        modalRemoved = true;
        document.removeEventListener('mousedown', handleClickOutside);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
  };

  if (loading) {
    return <div className="loading">Loading data...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  const renderHeroTimeline = (hero: Hero) => {
    // Get actual item data if available
    const heroActualItems = actualItems.get(hero.id);
    
    // We always have a timeline to use now
    let timelineToUse = heroActualItems || new Map<number, Item>();
    
    // If we have no items, create emergency hardcoded data
    if (timelineToUse.size === 0 && isComparing) {
      console.log(`EMERGENCY: Creating hardcoded items for ${hero.name} - no items found`);
      
      // Just use our synthetic items function
      timelineToUse = createSyntheticItemsForHero(hero, itemData);
    }
    
    // Get current net worth and current gold for this hero at the current time point
    const netWorthData = playerNetWorth.get(hero.id);
    const currentGoldData = playerCurrentGold.get(hero.id);
    const currentMinute = Math.floor(currentTimePoint / 60);
    
    const currentNetWorth = netWorthData && netWorthData.length > currentMinute 
      ? netWorthData[currentMinute] 
      : 0;
      
    const availableGold = currentGoldData && currentGoldData.length > currentMinute 
      ? currentGoldData[currentMinute] 
      : 0;
    
    // Calculate simulation currency (available gold - spent gold)
    const heroItems = userItems.get(hero.id) || new Map<number, Item>();
    let totalItemsCost = 0;
    heroItems.forEach(item => {
      if (item.cost) {
        totalItemsCost += item.cost;
      }
    });
    
    // Use the hero's net worth minus total items cost for simulation currency
    const simulationCurrency = currentNetWorth - totalItemsCost;
    
    // Get the hero's simulation currency
    const heroCurrency = simulationCurrency;
    
    // Calculate which items are visible at the current time point
    const visibleItems = Array.from(timelineToUse.entries())
      .filter(([minute]) => minute <= currentMinute)
      .sort((a, b) => a[0] - b[0]);
    
    // Get the latest item at current time point (if any)
    const _latestItem = visibleItems.length > 0 
      ? visibleItems[visibleItems.length - 1][1] 
      : null;
      
    // Log what we're actually rendering
    console.log(`Final timeline for ${hero.name}:`, 
      Array.from(timelineToUse.entries()).map(([min, item]) => ({
        minute: min, 
        name: item.name,
        imageUrl: item.imageUrl
      }))
    );
    
    const maxNetWorth = Math.max(...(netWorthData || []), 1);
    const _widthPercent = (currentNetWorth / maxNetWorth) * 100;

    return (
      <div key={hero.id} className="hero-timeline">
        <div className="hero-info" style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          width: '100%',
          position: 'relative'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '10px',
            width: '180px' 
          }}>
            <div style={{
              width: '48px',
              height: '48px',
              marginRight: '8px',
              borderRadius: '4px',
              overflow: 'hidden',
              background: '#1e1e1e',
              border: isHeroRadiant(hero) ? '2px solid #66bb6a' : '2px solid #ef5350',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexShrink: 0,
              position: 'relative'
            }}>
              <img 
                src={hero.imageUrl} 
                alt={hero.name} 
                style={{ 
                  width: '44px', 
                  height: '44px',
                  objectFit: 'cover',
                  opacity: 1 // Always show at full opacity
                }}
                title={hero.name}
              />
              
              {/* Overlay for Aghanim's Shard */}
              {heroesWithShard.has(hero.id) && (
            <div style={{ 
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  background: 'rgba(0, 0, 0, 0.7)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  boxShadow: '0 0 5px #4287f5',
                  cursor: 'pointer'
                }} 
                title="Aghanim's Shard - Right-click to remove"
                onContextMenu={(e) => {
                  e.preventDefault();
                  console.log("Removing Aghanim's Shard for hero", hero.id);
                  
                  // Get the current minute for logging
                  const currentMinute = Math.floor(currentTimePoint / 60);
                  
                  // Find the shard item from original state
                  const specialSlot = 100; // Slot for shard
                  
                  // Remove the shard from the set
                  setHeroesWithShard(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(hero.id);
                    return newSet;
                  });
                  
                  // Refund the cost (1400 gold for shard)
                  const currentSpentGold = spentGold.get(hero.id) || 0;
                  const newSpentGold = Math.max(0, currentSpentGold - 1400);
                  
                  console.log(`Refunding Aghanim's Shard (1400 gold) for hero ${hero.id}. Old spent: ${currentSpentGold}, New spent: ${newSpentGold}`);
                  
                  const newSpentGoldMap = new Map(spentGold);
                  newSpentGoldMap.set(hero.id, newSpentGold);
                  setSpentGold(newSpentGoldMap);
                  
                  // Remove any items from special slots in user items map if they exist
                  const heroItems = userItems.get(hero.id) || new Map<number, Item>();
                  const newHeroItems = new Map(heroItems);
                  
                  // Remove the special slot if it exists
                  if (newHeroItems.has(specialSlot)) {
                    newHeroItems.delete(specialSlot);
                  }
                  
                  // Create a new user items map to ensure React detects the change
                  const newUserItems = new Map(userItems);
                  newUserItems.set(hero.id, newHeroItems);
                  setUserItems(newUserItems);
                  
                  // Remove from Firebase by setting special slot to null
                  const heroItemsObj: Record<string, any> = {};
                  heroItemsObj[specialSlot.toString()] = null;
                  
                  console.log(`Removed Aghanim's Shard for hero ${hero.id} at minute ${currentMinute}. Syncing to Firebase...`);
                  DotaService.updateSessionItems(sessionId, String(hero.id), heroItemsObj)
                    .then(() => {
                      console.log("Successfully removed shard from Firebase");
                      lastUpdateTimeRef.current = Date.now();
                    })
                    .catch(error => {
                      console.error('Error removing shard from Firebase:', error);
                    });
                }}
              >
                  <img 
                    src="https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/aghanims_shard_lg.png" 
                    alt="Aghanim's Shard" 
                    style={{
                      width: '16px',
                      height: '16px',
                      objectFit: 'contain'
                    }}
              />
            </div>
              )}
              
              {/* Overlay for Aghanim's Blessing */}
              {heroesWithBlessing.has(hero.id) && (
            <div style={{ 
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  background: 'rgba(0, 0, 0, 0.7)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  boxShadow: '0 0 5px #f5a623',
                  cursor: 'pointer'
                }} 
                title="Aghanim's Blessing - Right-click to remove"
                onContextMenu={(e) => {
                  e.preventDefault();
                  console.log("Removing Aghanim's Blessing for hero", hero.id);
                  
                  // Get the current minute for logging
                  const currentMinute = Math.floor(currentTimePoint / 60);
                  
                  // Find the blessing item from original state
                  const specialSlot = 101; // Slot for blessing
                  
                  // Remove the blessing from the set
                  setHeroesWithBlessing(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(hero.id);
                    return newSet;
                  });
                  
                  // Refund the cost (5800 gold for blessing)
                  const currentSpentGold = spentGold.get(hero.id) || 0;
                  const newSpentGold = Math.max(0, currentSpentGold - 5800);
                  
                  console.log(`Refunding Aghanim's Blessing (5800 gold) for hero ${hero.id}. Old spent: ${currentSpentGold}, New spent: ${newSpentGold}`);
                  
                  const newSpentGoldMap = new Map(spentGold);
                  newSpentGoldMap.set(hero.id, newSpentGold);
                  setSpentGold(newSpentGoldMap);
                  
                  // Remove any items from special slots in user items map if they exist
                  const heroItems = userItems.get(hero.id) || new Map<number, Item>();
                  const newHeroItems = new Map(heroItems);
                  
                  // Remove the special slot if it exists
                  if (newHeroItems.has(specialSlot)) {
                    newHeroItems.delete(specialSlot);
                  }
                  
                  // Create a new user items map to ensure React detects the change
                  const newUserItems = new Map(userItems);
                  newUserItems.set(hero.id, newHeroItems);
                  setUserItems(newUserItems);
                  
                  // Remove from Firebase by setting special slot to null
                  const heroItemsObj: Record<string, any> = {};
                  heroItemsObj[specialSlot.toString()] = null;
                  
                  console.log(`Removed Aghanim's Blessing for hero ${hero.id} at minute ${currentMinute}. Syncing to Firebase...`);
                  DotaService.updateSessionItems(sessionId, String(hero.id), heroItemsObj)
                    .then(() => {
                      console.log("Successfully removed blessing from Firebase");
                      lastUpdateTimeRef.current = Date.now();
                    })
                    .catch(error => {
                      console.error('Error removing blessing from Firebase:', error);
                    });
                }}
              >
                  <img 
                    src="https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/ultimate_scepter_2_lg.png" 
                    alt="Aghanim's Blessing" 
                    style={{
                      width: '16px',
                      height: '16px',
                      objectFit: 'contain'
                    }}
                  />
            </div>
              )}
        </div>
        
            <div>
              <div>{hero.name}</div>
        <div style={{ 
                fontSize: '12px', 
                color: '#ffeb3b', 
                fontWeight: 'bold',
          display: 'flex', 
                alignItems: 'center'
              }}>
                {Math.max(0, simulationCurrency).toLocaleString()} <span style={{ fontSize: '10px', marginLeft: '2px' }}>🪙</span>
            </div>
              {/* Add error message directly under hero name if present */}
              {errorMessages.has(hero.id) && (
                <div style={{ 
                  fontSize: '11px',
                  color: '#ff6b6b',
                  marginTop: '2px',
                  fontWeight: 'bold',
                  animation: 'fadeIn 0.3s'
                }}>
                  {errorMessages.get(hero.id)}
                  <style>{`
                    @keyframes fadeIn {
                      from { opacity: 0; transform: translateY(-2px); }
                      to { opacity: 1; transform: translateY(0); }
                    }
                  `}</style>
                </div>
              )}
          </div>
        </div>
        
          {/* Display 6 item slots in a horizontal row */}
          <div style={{ 
            display: 'flex',
            flexWrap: 'nowrap',
            gap: '6px',
            marginLeft: '90px',
            marginRight: 'auto',
            alignItems: 'center',
            justifyContent: 'flex-start',
            width: '324px' // Keep width consistent
          }}>
            {/* Item slots */}
            <div style={{ 
            display: 'flex',
              flexWrap: 'nowrap',
              gap: '6px',
              width: '324px', // 6 items x 48px + 5 gaps x 6px
              height: '48px',
            }}>
              {Array(6).fill(0).map((_, slotIndex) => {
                // Get user-selected items
                const heroItems = userItems.get(hero.id);
                
                // If there's an item in this slot, show it
                if (heroItems && heroItems.has(slotIndex)) {
                  const item = heroItems.get(slotIndex)!;
                  // Calculate if the item was acquired in the past, present or future based on timeline
              const currentMinute = Math.floor(currentTimePoint / 60);
                  const itemAcquiredMinute = item.timestamp || 0;
                  
                  // Determine timing class - but always show the item
                  let timingClass = '';
                  let borderStyle = '1px solid #444';
                  
                  if (itemAcquiredMinute <= currentMinute) {
                    // Item was acquired in the past or at the current time
                    timingClass = 'acquired';
                    borderStyle = '1px solid #4caf50'; // Green border for acquired items
                  } else {
                    // Item will be acquired in the future
                    timingClass = 'planned';
                    borderStyle = '1px dashed #ffeb3b'; // Yellow dashed border for planned items
                  }
                  
                  return (
                    <div 
                      key={`${hero.id}-item-${slotIndex}`}
                      style={{
                        width: '48px',
                        height: '48px',
                        background: '#1a1a1a',
                        borderRadius: '3px',
                        overflow: 'hidden',
                        border: borderStyle,
                        position: 'relative',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}
                      onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                        showItemSelection(event, hero.id, slotIndex);
                      }}
                      onContextMenu={(event: React.MouseEvent<HTMLDivElement>) => {
                        event.preventDefault();
                        removeHeroItem(hero.id, slotIndex);
                      }}
                      title={`${item.name} (${itemAcquiredMinute}:00)${item.cost ? '\nCost: ' + item.cost : ''}\nRight-click to remove`}
                    >
                      <img
                        src={getItemImageUrl(item.name)}
                        alt={item.name}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          opacity: 1 // Always show items at full opacity
                        }}
                        onError={(e) => {
                          const imgElement = e.currentTarget;
                          if (imgElement.src !== item.alternativeImageUrl && item.alternativeImageUrl) {
                            imgElement.src = item.alternativeImageUrl;
                          } else {
                            imgElement.src = 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/blink_lg.png';
                          }
                        }}
                      />
                      {/* Add timestamp indicator */}
                      <div style={{
                        position: 'absolute',
                        bottom: 0,
                        right: 0,
                        background: timingClass === 'acquired' ? 'rgba(0,0,0,0.7)' : 'rgba(255,235,59,0.7)',
                        color: timingClass === 'acquired' ? 'white' : 'black',
                        fontSize: '10px',
                        padding: '1px 3px',
                        borderTopLeftRadius: '2px'
                      }}>
                        {itemAcquiredMinute}m
                      </div>
                    </div>
                  );
                }
                
                // Empty slot - make it clickable too
                return (
                <div 
                  key={`${hero.id}-empty-${slotIndex}`}
                  style={{
                    width: '48px',
                    height: '48px',
                      background: '#1a1a1a',
                      border: '1px solid #333',
                    borderRadius: '3px',
                    cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'center',
                    alignItems: 'center'
                  }}
                  onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                    showItemSelection(event, hero.id, slotIndex);
                  }}
                >
                  <span style={{ color: '#555', fontSize: '24px' }}>+</span>
                </div>
                );
              })}
          </div>
          
            {/* Clear All Items button moved to absolute position on the right */}
            {userItems.get(hero.id) && userItems.get(hero.id)!.size > 0 && (
              <button
                onClick={() => clearAllHeroItems(hero.id)}
                style={{
                  background: '#2a2a2a',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '3px 8px',
                  position: 'absolute',
                  right: '0',
                  cursor: 'pointer',
                  color: '#ff6b6b',
                  fontSize: '12px',
                  fontWeight: 'bold'
                }}
              >
                Clear All
              </button>
            )}
          </div>
        </div>

        {/* Real Match Item Progression */}
        {heroPurchaseHistory.get(hero.id) && heroPurchaseHistory.get(hero.id)!.length > 0 && (() => {
          const isRevealed = revealedFinalBuilds.has(hero.id);
          // Only show items whose purchase time has already passed at the current timeline position.
          // Scrubbing backwards past an item's purchase time removes it again.
          // Items with an unknown purchase time (time === Infinity, couldn't be matched
          // to a purchase_log entry) are still part of the final build - show them
          // whenever revealed rather than hiding them behind a timeline they can't match.
          const itemsSoFar = heroPurchaseHistory.get(hero.id)!.filter(p => p.time === Infinity || p.time <= currentTimePoint);

          return (
            <div style={{
              marginTop: '8px',
              paddingTop: '8px',
              borderTop: '1px solid #333'
            }}>
              <div
                onClick={() => {
                  setRevealedFinalBuilds(prev => {
                    const next = new Set(prev);
                    if (next.has(hero.id)) {
                      next.delete(hero.id);
                    } else {
                      next.add(hero.id);
                    }
                    return next;
                  });
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  cursor: 'pointer',
                  userSelect: 'none',
                  color: '#888',
                  fontSize: '11px'
                }}
                title={isRevealed ? 'Click to hide final build' : 'Click to reveal final build'}
              >
                <span style={{ fontSize: '9px', transform: isRevealed ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}>
                  ▶
                </span>
                <span>Final Build {isRevealed ? `(${itemsSoFar.length}/${heroPurchaseHistory.get(hero.id)!.length})` : '(click to reveal)'}</span>
              </div>

              {isRevealed && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  marginTop: '6px',
                  flexWrap: 'wrap',
                  maxWidth: '100%'
                }}>
                  {itemsSoFar.length === 0 && (
                    <span style={{ fontSize: '11px', color: '#666', fontStyle: 'italic' }}>
                      No items built yet at this time
                    </span>
                  )}
                  {itemsSoFar.map((purchase, idx) => {
                    // Pre-game purchases (wards, tangoes, etc. bought before the horn) have
                    // negative timestamps - clamp to 0m instead of showing e.g. "-2m".
                    // Items with no matched purchase time show "?" instead of a minute.
                    const isUnknownTime = purchase.time === Infinity;
                    const minutes = isUnknownTime ? null : Math.max(0, Math.floor(purchase.time / 60));
                    const timeLabel = isUnknownTime ? '?' : `${minutes}m`;
                    return (
                      <div
                        key={`${hero.id}-actual-${idx}`}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          position: 'relative'
                        }}
                        title={`${purchase.item.name} - ${isUnknownTime ? 'purchase time unknown' : `${minutes}m`}`}
                      >
                        <div style={{
                          width: '32px',
                          height: '32px',
                          background: '#1a1a1a',
                          borderRadius: '3px',
                          overflow: 'hidden',
                          border: '1px solid #444'
                        }}>
                          <img
                            src={purchase.item.imageUrl || getItemImageUrl(purchase.item.name)}
                            alt={purchase.item.name}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'contain'
                            }}
                            onError={(e) => {
                              e.currentTarget.src = 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/blink_lg.png';
                            }}
                          />
                        </div>
                        <span style={{
                          fontSize: '9px',
                          color: '#aaa',
                          marginTop: '2px'
                        }}>
                          {timeLabel}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })()}
      </div>
    );
  };

  // Get sorted net worth data for both teams at current time point
  const getCurrentTeamNetWorths = () => {
    const currentMinute = Math.floor(currentTimePoint / 60);
    
    const getHeroNetWorth = (hero: Hero) => {
      const netWorthData = playerNetWorth.get(hero.id);
      return netWorthData && netWorthData.length > currentMinute 
        ? netWorthData[currentMinute] 
        : 0;
    };
    
    // Get data for both teams
    const radiantData = radiantHeroes
      .map(hero => ({
        hero,
        netWorth: getHeroNetWorth(hero),
        currentGold: playerCurrentGold.get(hero.id)?.[currentMinute] || 0
      }))
      .sort((a, b) => b.netWorth - a.netWorth);
      
    const direData = direHeroes
      .map(hero => ({
        hero,
        netWorth: getHeroNetWorth(hero),
        currentGold: playerCurrentGold.get(hero.id)?.[currentMinute] || 0
      }))
      .sort((a, b) => b.netWorth - a.netWorth);
      
    return { radiantData, direData };
  };
  
  const { radiantData, direData } = getCurrentTeamNetWorths();
  
  // Calculate team totals
  const radiantTotalNetWorth = radiantData.reduce((sum, item) => sum + item.netWorth, 0);
  const direTotalNetWorth = direData.reduce((sum, item) => sum + item.netWorth, 0);

  // Add the helper function to determine if a hero is radiant
  const isHeroRadiant = (hero: Hero): boolean => {
    return radiantHeroes.some(h => h.id === hero.id);
  };

  return (
    <div className="itemization-container" style={{ padding: '20px' }}>
      <div style={{
        background: '#1a1a1a',
        padding: '10px 15px',
        marginBottom: '20px',
        borderRadius: '4px',
        border: '1px solid #333',
        boxShadow: '0 2px 5px rgba(0,0,0,0.3)'
      }}>
        <div style={{ 
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '15px',
          marginBottom: '10px'
        }}>
          <div style={{ 
            flex: '0 0 auto', 
            whiteSpace: 'nowrap', 
            padding: '5px 15px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '4px',
            background: 'rgba(30, 30, 30, 0.5)',
            fontWeight: 'bold',
            fontSize: '18px'
          }}>
            Itemization Simulation
          </div>
          <AdBanner 
            adSlot="6453152319" 
            adFormat="horizontal" 
            style={{ width: '728px', height: '90px', flex: '1 1 auto' }} 
          />
        </div>

        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderTop: '1px solid #333',
          paddingTop: '10px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', flex: '1' }}>
            <span style={{ color: '#dfe6e9', marginRight: '5px' }}>Session ID:</span>
            <div 
              title="Click to copy" 
              onClick={() => {
                navigator.clipboard.writeText(sessionId);
                // Create a temporary element to indicate successful copy
                const notification = document.createElement('div');
                notification.textContent = '✓ Copied!';
                notification.style.position = 'absolute';
                notification.style.left = '50%';
                notification.style.top = '60px';
                notification.style.transform = 'translateX(-50%)';
                notification.style.background = '#00b894';
                notification.style.color = 'white';
                notification.style.padding = '5px 12px';
                notification.style.borderRadius = '4px';
                notification.style.zIndex = '1000';
                notification.style.transition = 'opacity 0.3s ease-in-out';
                document.body.appendChild(notification);
                
                // Remove notification after 2 seconds
                setTimeout(() => {
                  notification.style.opacity = '0';
                  setTimeout(() => document.body.removeChild(notification), 300);
                }, 2000);
              }}
              style={{
                background: '#00b894',
                padding: '4px 8px',
                borderRadius: '4px',
                color: 'white',
                fontWeight: 'bold',
                cursor: 'pointer',
                fontSize: '14px',
                userSelect: 'none',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              {sessionId}
              <span style={{ marginLeft: '5px', fontSize: '12px' }}>📋</span>
            </div>
            
            {/* Live status indicator */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              marginLeft: '15px',
              background: '#34495e',
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '12px',
              color: pendingUpdatesRef.current ? '#f39c12' : '#2ecc71'
            }}>
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: pendingUpdatesRef.current ? '#f39c12' : '#2ecc71',
                marginRight: '5px',
                animation: pendingUpdatesRef.current ? 'pulse 1s infinite' : 'none'
              }}></div>
              <style>
                {`
                  @keyframes pulse {
                    0% { opacity: 0.5; }
                    50% { opacity: 1; }
                    100% { opacity: 0.5; }
                  }
                `}
              </style>
              {pendingUpdatesRef.current ? 'Syncing...' : 'Live'}
            </div>
            
            {/* Clear All Items button for all heroes */}
            <button
              onClick={clearAllItemsForAllHeroes}
              style={{
                background: '#e74c3c',
                border: 'none',
                borderRadius: '4px',
                padding: '4px 10px',
                marginLeft: '15px',
                cursor: 'pointer',
                color: 'white',
                fontSize: '12px',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <span style={{ marginRight: '5px', fontSize: '14px' }}>🗑️</span>
              Clear All Items
            </button>

            {/* Toggle to reveal/hide the Final Build spoiler for every hero at once */}
            <button
              onClick={() => {
                const allHeroIds = [...radiantHeroes, ...direHeroes].map(h => h.id);
                const allRevealed = allHeroIds.length > 0 && allHeroIds.every(id => revealedFinalBuilds.has(id));
                setRevealedFinalBuilds(allRevealed ? new Set() : new Set(allHeroIds));
              }}
              style={{
                background: '#8e44ad',
                border: 'none',
                borderRadius: '4px',
                padding: '4px 10px',
                marginLeft: '8px',
                cursor: 'pointer',
                color: 'white',
                fontSize: '12px',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <span style={{ marginRight: '5px', fontSize: '14px' }}>👁️</span>
              {[...radiantHeroes, ...direHeroes].length > 0 &&
               [...radiantHeroes, ...direHeroes].every(h => revealedFinalBuilds.has(h.id))
                ? 'Hide All Items'
                : 'Show All Items'}
            </button>
          </div>
          
          <div style={{ 
            flex: '1', 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center' 
          }}>
            <div style={{ 
              backgroundColor: '#242424', 
              padding: '4px 12px', 
              borderRadius: '4px', 
              border: '1px solid #333',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span>Current match ID:</span>
              <span style={{ fontWeight: 'bold', color: 'white' }}>{matchId}</span>
            </div>
          </div>
            
          <div style={{ color: '#b2bec3', fontSize: '13px', display: 'flex', alignItems: 'center', flex: '1', justifyContent: 'flex-end' }}>
            {/* New Match ID input and button */}
            <div style={{ display: 'flex', marginLeft: 'auto', marginRight: '10px' }}>
              <input 
                type="text" 
                placeholder="Enter Match ID" 
                id="newMatchId"
                style={{
                  padding: '5px 8px',
                  borderRadius: '4px 0 0 4px',
                  border: '1px solid #444',
                  background: '#222',
                  color: 'white',
                  fontSize: '12px',
                  width: '120px'
                }}
              />
              <button
                onClick={() => {
                  const newMatchId = (document.getElementById('newMatchId') as HTMLInputElement)?.value;
                  if (newMatchId && newMatchId.trim()) {
                    window.location.href = `/?matchId=${newMatchId.trim()}`;
                  }
                }}
                style={{
                  background: '#3498db',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '3px 8px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  whiteSpace: 'nowrap'
                }}
              >
                Go to another match
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Controls section */}
      <div className="controls-section">
        {/* Team Selection Tabs - REMOVED */}
      <div className="team-selector" style={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        background: '#121212',
        padding: '10px 0',
        borderBottom: '1px solid #333'
      }}>
        {/* Draft Sequence Display */}
        <div style={{ 
          padding: '10px 15px',
          backgroundColor: '#171a21', 
          borderRadius: '4px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px'
        }}>
          {/* First team - draft sequence only */}
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              minWidth: '220px',
              color: '#8bc34a',
              fontWeight: 'bold',
              fontSize: '16px',
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }}>
              {radiantTeamName}
            </div>
            
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24].map((num, index) => {
                const isPick = [8, 9, 13, 14, 15, 16, 17, 18, 23, 24].includes(num);
                const hero = draftSequence[num];
                
                // Only show slots that belong to this team
                if (![2, 3, 5, 6, 9, 12, 13, 16, 17, 20, 21, 24].includes(num)) {
                  return null;
                }
                
                return (
                  <div key={num} style={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: '4px',
                    position: 'relative'
                  }}>
                    <div style={{ fontSize: '12px', marginBottom: '2px', color: '#ddd' }}>{num}</div>
                    <div style={{ 
                      width: '45px', 
                      height: '45px', 
                      backgroundColor: '#333',
                      borderRadius: '4px',
                      overflow: 'hidden',
                      position: 'relative'
                    }}>
                      {hero && (
                        <img 
                          src={hero.imageUrl} 
                          alt="Hero" 
                          style={{ 
                            width: '100%', 
                            height: '100%',
                            objectFit: 'cover',
                            filter: isPick ? 'none' : 'brightness(0.4) grayscale(80%)'
                          }} 
                        />
                      )}
                      
                      <div style={{ 
                        position: 'absolute', 
                        bottom: '0', 
                        left: '0', 
                        right: '0',
                        textAlign: 'center',
                        backgroundColor: isPick ? 'rgba(0, 120, 0, 0.7)' : 'rgba(120, 0, 0, 0.7)',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '11px',
                        padding: '2px 0'
                      }}>
                        {isPick ? 'PICK' : 'BAN'}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* Second team - draft sequence only */}
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              minWidth: '220px',
              color: '#ef5350',
              fontWeight: 'bold',
              fontSize: '16px',
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }}>
              {direTeamName}
            </div>
            
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24].map((num, index) => {
                const isPick = [8, 9, 13, 14, 15, 16, 17, 18, 23, 24].includes(num);
                const hero = draftSequence[num];
                
                // Only show slots that belong to this team
                if (![1, 4, 7, 8, 10, 11, 14, 15, 18, 19, 22, 23].includes(num)) {
                  return null;
                }
                
                return (
                  <div key={num} style={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: '4px',
                    position: 'relative'
                  }}>
                    <div style={{ fontSize: '12px', marginBottom: '2px', color: '#ddd' }}>{num}</div>
                    <div style={{ 
                      width: '45px', 
                      height: '45px', 
                      backgroundColor: '#333',
                      borderRadius: '4px',
                      overflow: 'hidden',
                      position: 'relative'
                    }}>
                      {hero && (
                        <img 
                          src={hero.imageUrl} 
                          alt="Hero" 
                          style={{ 
                            width: '100%', 
                            height: '100%',
                            objectFit: 'cover',
                            filter: isPick ? 'none' : 'brightness(0.4) grayscale(80%)'
                          }} 
                        />
                      )}
                      
                      <div style={{ 
                        position: 'absolute', 
                        bottom: '0', 
                        left: '0', 
                        right: '0',
                        textAlign: 'center',
                        backgroundColor: isPick ? 'rgba(0, 120, 0, 0.7)' : 'rgba(120, 0, 0, 0.7)',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '11px',
                        padding: '2px 0'
                      }}>
                        {isPick ? 'PICK' : 'BAN'}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        
        {/* Add Game Events Log beside draft display */}
        <div style={{ 
          marginTop: '15px'
        }}>
          <GameDataLog matchId={matchId} />
        </div>
      </div>
      
      {/* Team Net Worth Display */}
      <div style={{
        display: 'flex',
        gap: '20px',
        margin: '15px 0',
        padding: '15px',
        background: '#1a1a1a',
        borderRadius: '5px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
        position: 'sticky',
        top: '50px',
        zIndex: 90
      }}>
        {/* Radiant Team */}
        <div style={{ flex: 1 }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            marginBottom: '10px',
            alignItems: 'center'
          }}>
            <h3 style={{ margin: 0, color: '#66bb6a', display: 'flex', alignItems: 'center' }}>
                {radiantTeamName}
              <span style={{ marginLeft: '10px', fontSize: '16px', fontWeight: 'bold', color: '#FFD700' }}>
                {radiantTotalNetWorth.toLocaleString()}
              </span>
            </h3>
          </div>
          
          {radiantData.map(({ hero, netWorth }) => {
            const maxNetWorth = Math.max(...radiantData.map(h => h.netWorth), 1);
            const widthPercent = (netWorth / maxNetWorth) * 100;
            
            return (
              <div key={hero.id} style={{ 
                display: 'flex', 
                alignItems: 'center', 
                marginBottom: '5px',
                color: 'white', // Always show in full color
                height: '52px'
              }}>
                {/* Hero icon and items */}
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  width: '450px', // Increased from 400px to accommodate one more item
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    marginRight: '8px',
                    borderRadius: '4px',
                    overflow: 'hidden',
                    background: '#1e1e1e',
                    border: '2px solid #66bb6a', // Green border for Radiant heroes
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexShrink: 0
                  }}>
                    <img 
                      src={hero.imageUrl} 
                      alt={hero.name} 
                      style={{ 
                        width: '44px', 
                        height: '44px',
                        objectFit: 'cover',
                        opacity: 1 // Always show at full opacity
                      }}
                      title={hero.name}
                    />
                  </div>
                  
                  {/* Display hero's current items - moved right after hero icon */}
                  <div style={{ 
                    display: 'flex',
                    gap: '4px',
                    height: '48px',
                    alignItems: 'center',
                    flexGrow: 1,
                    overflow: 'hidden',
                    maxWidth: '380px' // Increased to accommodate one more item slot
                  }}>
                    {(() => {
                      // Get user-selected items up to current time point
                      const heroItems = userItems.get(hero.id);
                      const currentMinute = Math.floor(currentTimePoint / 60);
                      
                      if (heroItems && heroItems.size > 0) {
                          // Show all items, regardless of time
                          const allItems = Array.from(heroItems.entries())
                            .sort((a, b) => {
                              // Sort by timestamp, not by slot index
                              const timestampA = a[1].timestamp || 0;
                              const timestampB = b[1].timestamp || 0;
                              return timestampA - timestampB;
                            });
                          
                          if (allItems.length > 0) {
                            // Show all items
                            return allItems.map(([slotIndex, item]) => {
                              const itemTimestamp = item.timestamp || 0;
                              const isFuture = itemTimestamp > currentMinute;
                              
                              return (
                                <div 
                                  key={`team-${hero.id}-${slotIndex}`}
                              style={{
                                width: '48px',
                                height: '48px',
                                background: '#1a1a1a',
                                borderRadius: '3px',
                                overflow: 'hidden',
                                    border: isFuture ? '1px dashed #ffeb3b' : '1px solid #444',
                                position: 'relative',
                                flexShrink: 0,
                                boxShadow: '0 0 3px rgba(255, 215, 0, 0.5)'
                              }}
                                  title={`${item.name} (${item.timestamp}m)${item.cost ? '\nCost: ' + item.cost : ''}`}
                            >
                              <img
                                    src={getItemImageUrl(item.name)}
                                alt={item.name}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'contain'
                                  }}
                                    // Multiple fallbacks for image loading
                                    onError={(e) => {
                                      const imgElement = e.currentTarget;
                                      if (imgElement.src !== item.alternativeImageUrl && item.alternativeImageUrl) {
                                        // Try the alternative URL first
                                        imgElement.src = item.alternativeImageUrl;
                                      } else {
                                        // If that fails, use a known good fallback
                                        imgElement.src = 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/blink_lg.png';
                                      }
                                }}
                              />
                              <div style={{
                                position: 'absolute',
                                bottom: 0,
                                right: 0,
                                    background: isFuture ? 'rgba(255,235,59,0.7)' : 'rgba(0,0,0,0.7)',
                                    color: isFuture ? 'black' : 'white',
                                fontSize: '12px',
                                padding: '2px 4px',
                                borderTopLeftRadius: '2px'
                              }}>
                                    {item.timestamp}m
                              </div>
                            </div>
                              );
                            });
                        }
                      }
                      
                      return <span style={{ 
                        fontSize: '12px',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        color: '#777',
                        fontStyle: 'italic'
                      }}>No items</span>;
                    })()}
                  </div>
                </div>
                
                {/* Net worth bar for Radiant */}
                <div style={{ flex: 0.5, position: 'relative', height: '20px', marginLeft: '15px' }}>
                  {/* Background bar */}
                  <div style={{ 
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    height: '100%',
                    width: '100%',
                    background: 'rgba(30, 30, 30, 0.5)',
                    borderRadius: '4px',
                    boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.3)'
                  }}></div>
                  
                  {/* Filled bar with gradient */}
                  <div style={{ 
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    height: '100%',
                    width: `${widthPercent}%`,
                    background: 'linear-gradient(90deg, rgba(102, 187, 106, 0.3) 0%, rgba(102, 187, 106, 0.7) 100%)',
                    borderRadius: '4px',
                    transition: 'width 0.5s ease-out',
                    boxShadow: '0 0 10px rgba(102, 187, 106, 0.3)',
                    border: '1px solid rgba(102, 187, 106, 0.5)',
                    animation: 'pulse 2s infinite ease-in-out'
                  }}>
                  </div>
                  
                  {/* Value display */}
                  <div style={{ 
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    width: '100%',
                    padding: '0 8px',
                  }}>
                    <span style={{
                      fontSize: '12px',
                      color: '#FFD700',
                      fontWeight: 'bold',
                      textShadow: '0 0 3px rgba(0,0,0,0.8)'
                    }}>
                      {netWorth.toLocaleString()}
                    </span>
                  </div>
                  
                  {/* Add CSS keyframes for pulsing animation */}
                  <style>{`
                    @keyframes pulse {
                      0% { opacity: 1; }
                      50% { opacity: 0.8; }
                      100% { opacity: 1; }
                    }
                  `}</style>
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Net Worth Difference Indicator */}
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'center', 
          alignItems: 'center', 
          padding: '0 10px' 
        }}>
          <div style={{ 
            fontSize: '14px', 
            marginBottom: '5px', 
            textAlign: 'center', 
            color: '#aaa' 
          }}>
            Advantage
          </div>
          
          {(() => {
            const diff = radiantTotalNetWorth - direTotalNetWorth;
            const absDiff = Math.abs(diff);
            let text = '';
            let color = '';
            
            if (absDiff < 1000) {
              text = 'Even';
              color = '#888';
            } else {
              const advantage = diff > 0 ? radiantTeamName : direTeamName;
              text = `${advantage}`;
              color = diff > 0 ? '#66bb6a' : '#ef5350';
            }
            
            return (
              <div style={{ 
                fontSize: '16px', 
                fontWeight: 'bold', 
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}>
                <span style={{ color: color }}>{text}</span>
                {absDiff >= 1000 && (
                  <span style={{
                    color: '#ffcc00',
                    fontSize: '14px',
                    marginTop: '4px',
                    fontWeight: 'bold'
                  }}>
                    {`${Math.floor(absDiff / 1000)}k+`}
                  </span>
                )}
              </div>
            );
          })()}
        </div>
        
        {/* Dire Team */}
        <div style={{ flex: 1 }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            marginBottom: '10px',
            alignItems: 'center'
          }}>
              <h3 style={{ margin: 0, color: '#ef5350', display: 'flex', alignItems: 'center' }}>
                {direTeamName}
              <span style={{ marginLeft: '10px', fontSize: '16px', fontWeight: 'bold', color: '#FFD700' }}>
                {direTotalNetWorth.toLocaleString()}
              </span>
            </h3>
          </div>
          
          {direData.map(({ hero, netWorth }) => {
            const maxNetWorth = Math.max(...direData.map(h => h.netWorth), 1);
            const widthPercent = (netWorth / maxNetWorth) * 100;
            
            return (
              <div key={hero.id} style={{ 
                display: 'flex', 
                alignItems: 'center', 
                marginBottom: '5px',
                color: 'white', // Always show in full color
                height: '52px'
              }}>
                {/* Hero icon and items */}
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  width: '450px', // Increased from 400px to accommodate one more item
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    marginRight: '8px',
                    borderRadius: '4px',
                    overflow: 'hidden',
                    background: '#1e1e1e',
                    border: '2px solid #ef5350', // Red border for Dire heroes
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexShrink: 0
                  }}>
                    <img 
                      src={hero.imageUrl} 
                      alt={hero.name} 
                      style={{ 
                        width: '44px', 
                        height: '44px',
                        objectFit: 'cover',
                        opacity: 1 // Always show at full opacity
                      }}
                      title={hero.name}
                    />
                  </div>
                  
                  {/* Display hero's current items - moved right after hero icon */}
                  <div style={{ 
                    display: 'flex',
                    gap: '4px',
                    height: '48px',
                    alignItems: 'center',
                    flexGrow: 1,
                    overflow: 'hidden',
                    maxWidth: '380px' // Increased to accommodate one more item slot
                  }}>
                    {(() => {
                      // Get user-selected items up to current time point
                      const heroItems = userItems.get(hero.id);
                      const currentMinute = Math.floor(currentTimePoint / 60);
                      
                      if (heroItems && heroItems.size > 0) {
                          // Show all items, regardless of time
                          const allItems = Array.from(heroItems.entries())
                            .sort((a, b) => {
                              // Sort by timestamp, not by slot index
                              const timestampA = a[1].timestamp || 0;
                              const timestampB = b[1].timestamp || 0;
                              return timestampA - timestampB;
                            });
                          
                          if (allItems.length > 0) {
                            // Show all items
                            return allItems.map(([slotIndex, item]) => {
                              const itemTimestamp = item.timestamp || 0;
                              const isFuture = itemTimestamp > currentMinute;
                              
                              return (
                                <div 
                                  key={`team-${hero.id}-${slotIndex}`}
                              style={{
                                width: '48px',
                                height: '48px',
                                background: '#1a1a1a',
                                borderRadius: '3px',
                                overflow: 'hidden',
                                    border: isFuture ? '1px dashed #ffeb3b' : '1px solid #444',
                                position: 'relative',
                                flexShrink: 0,
                                boxShadow: '0 0 3px rgba(255, 215, 0, 0.5)'
                              }}
                                  title={`${item.name} (${item.timestamp}m)${item.cost ? '\nCost: ' + item.cost : ''}`}
                            >
                              <img
                                    src={getItemImageUrl(item.name)}
                                alt={item.name}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'contain'
                                  }}
                                    // Multiple fallbacks for image loading
                                    onError={(e) => {
                                      const imgElement = e.currentTarget;
                                      if (imgElement.src !== item.alternativeImageUrl && item.alternativeImageUrl) {
                                        // Try the alternative URL first
                                        imgElement.src = item.alternativeImageUrl;
                                      } else {
                                        // If that fails, use a known good fallback
                                        imgElement.src = 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/items/blink_lg.png';
                                      }
                                }}
                              />
                              <div style={{
                                position: 'absolute',
                                bottom: 0,
                                right: 0,
                                    background: isFuture ? 'rgba(255,235,59,0.7)' : 'rgba(0,0,0,0.7)',
                                    color: isFuture ? 'black' : 'white',
                                fontSize: '12px',
                                padding: '2px 4px',
                                borderTopLeftRadius: '2px'
                              }}>
                                    {item.timestamp}m
                              </div>
                            </div>
                              );
                            });
                        }
                      }
                      
                      return <span style={{ 
                        fontSize: '12px',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        color: '#777',
                        fontStyle: 'italic'
                      }}>No items</span>;
                    })()}
                  </div>
                </div>
                
                {/* Net worth bar for Dire */}
                <div style={{ flex: 0.5, position: 'relative', height: '20px', marginLeft: '15px' }}>
                  {/* Background bar */}
                  <div style={{ 
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    height: '100%',
                    width: '100%',
                    background: 'rgba(30, 30, 30, 0.5)',
                    borderRadius: '4px',
                    boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.3)'
                  }}></div>
                  
                  {/* Filled bar with gradient */}
                  <div style={{ 
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    height: '100%',
                    width: `${widthPercent}%`,
                    background: 'linear-gradient(90deg, rgba(239, 83, 80, 0.3) 0%, rgba(239, 83, 80, 0.7) 100%)',
                    borderRadius: '4px',
                    transition: 'width 0.5s ease-out',
                    boxShadow: '0 0 10px rgba(239, 83, 80, 0.3)',
                    border: '1px solid rgba(239, 83, 80, 0.5)',
                    animation: 'pulseDire 2s infinite ease-in-out'
                  }}>
                  </div>
                  
                  {/* Value display */}
                  <div style={{ 
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    width: '100%',
                    padding: '0 8px',
                  }}>
                    <span style={{
                      fontSize: '12px',
                      color: '#FFD700',
                      fontWeight: 'bold',
                      textShadow: '0 0 3px rgba(0,0,0,0.8)'
                    }}>
                      {netWorth.toLocaleString()}
                    </span>
                  </div>
                  
                  {/* Add CSS keyframes for pulsing animation */}
                  <style>{`
                    @keyframes pulseDire {
                      0% { opacity: 1; }
                      50% { opacity: 0.8; }
                      100% { opacity: 1; }
                    }
                  `}</style>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Timeline Slider Controls */}
      <div className="timeline-controls" style={{
        margin: '20px 0',
        padding: '15px',
        background: '#1a1a1a',
        borderRadius: '5px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
        position: 'sticky',
        top: '300px',
        zIndex: 80
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
          <h3 style={{ margin: 0, fontSize: '18px' }}>Game Timeline</h3>
          {/* Play button removed */}
        </div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          margin: '25px 0 15px', // Increased top margin for the labels
          width: '100%'
        }}>
          <span style={{ minWidth: '45px', textAlign: 'center', fontWeight: 'bold' }}>
            {Math.floor(currentTimePoint / 60)}:{String(currentTimePoint % 60).padStart(2, '0')}
          </span>
          
          <div style={{ position: 'relative', flexGrow: 1 }}>
            {/* Add keyframes for pulsing animation */}
            <style>
              {`
                @keyframes pulse {
                  0% { opacity: 0.7; }
                  50% { opacity: 1; }
                  100% { opacity: 0.7; }
                }
              `}
            </style>
          
            {/* Minute markers - one marker for each minute */}
            <div style={{ 
              position: 'absolute', 
              top: '-25px', // Increased space for labels
              left: 0, 
              right: 0, 
              height: '25px', // Increased height for labels
              display: 'flex',
              pointerEvents: 'none' // Don't interfere with the slider
            }}>
              {/* Create an array with the exact number of minutes in the game */}
              {Array.from({ length: Math.ceil(gameDuration / 60) + 1 }).map((_, index) => {
                // Calculate the exact position for this minute marker
                const exactPosition = (index * 60) / gameDuration * 100;
                
                // Check if this minute should be labeled
                const isDivisibleByTwo = index % 2 === 0;
                const isTenMinMark = index >= 15 && (index - 15) % 10 === 0;
                const isSevenMinMark = index >= 7 && (index - 7) % 7 === 0;
                
                // New requirement: specific green minutes
                const greenMinutes = [3, 9, 27, 33, 39, 51, 57, 63, 69];
                const isGreenMinute = greenMinutes.includes(index);
                
                const shouldShowLabel = isDivisibleByTwo || isTenMinMark || isSevenMinMark || isGreenMinute;
                
                // Determine color based on type of marker
                let markerColor = '#777'; // default color
                let labelColor = '#ffcc00'; // default label color
                
                if (isGreenMinute) {
                  markerColor = '#4caf50'; // bright green for specified minutes
                  labelColor = '#4caf50';
                } else if (isTenMinMark) {
                  markerColor = '#f06292'; // pink for 10-minute marks from 15
                  labelColor = '#f06292';
                } else if (isSevenMinMark) {
                  markerColor = '#64b5f6'; // blue for 7-minute marks
                  labelColor = '#64b5f6';
                } else if (isDivisibleByTwo) {
                  markerColor = '#ffcc00'; // gold for minutes divisible by 2
                  labelColor = '#ffcc00';
                }
                
                // Only show markers if they fit within the slider (0% to 100%)
                if (exactPosition <= 100) {
                  return (
                    <div 
                      key={`marker-${index}`}
                      style={{
                        position: 'absolute',
                        left: `${exactPosition}%`,
                        width: shouldShowLabel ? '2px' : '1px',
                        height: isGreenMinute ? '14px' : shouldShowLabel ? '10px' : '6px',
                        background: isTenMinMark ? 
                          'linear-gradient(to bottom, #f06292, #e91e63)' : 
                          (shouldShowLabel ? markerColor : '#777'),
                        transform: 'translateX(-50%)',
                        top: isGreenMinute ? '11px' : '15px', // Position green markers higher
                        boxShadow: isGreenMinute ? '0 0 4px rgba(76, 175, 80, 0.8)' : 
                                  (isTenMinMark ? '0 0 6px rgba(233, 30, 99, 0.6)' : 'none'),
                        animation: isTenMinMark ? 'pulse 2s infinite ease-in-out' : 'none'
                      }}
                    >
                      {shouldShowLabel && (
                        <div style={{
                          position: 'absolute',
                          bottom: '12px',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          fontSize: '10px',
                          color: labelColor,
                          whiteSpace: 'nowrap',
                          fontWeight: 'bold',
                          textShadow: isTenMinMark ? '0 0 4px rgba(233, 30, 99, 0.8)' : 
                                     (isGreenMinute ? '0 0 3px rgba(76, 175, 80, 0.5)' : 'none')
                        }}>
                          {index}
                        </div>
                      )}
                    </div>
                  );
                }
                return null;
              })}
            </div>
            
          <input
            type="range"
            min="0"
            max={gameDuration}
            value={currentTimePoint}
            onChange={(e) => {
                const newTimePoint = parseInt(e.target.value);
                const oldTimePoint = currentTimePoint;
                
                // Only check for future items if moving backward in time
                if (newTimePoint < oldTimePoint) {
                  const currentMinute = Math.floor(newTimePoint / 60);
                  
                  // Check all heroes' items for future timestamps and refund as needed
                  const allHeroes = [...radiantHeroes, ...direHeroes];
                  let updatedUserItems = new Map(userItems);
                  let updatedSpentGold = new Map(spentGold);
                  let changesDetected = false;
                  
                  allHeroes.forEach(hero => {
                    const heroItems = userItems.get(hero.id);
                    if (!heroItems || heroItems.size === 0) return;
                    
                    // Check if any items were purchased after the current time
                    const futureItems = Array.from(heroItems.entries()).filter(([_, item]) => {
                      const itemTimestamp = item.timestamp || 0;
                      return itemTimestamp > currentMinute;
                    });
                    
                    if (futureItems.length > 0) {
                      changesDetected = true;
                      const newHeroItems = new Map(heroItems);
                      let refundAmount = 0;
                      
                      // Remove future items and calculate refund
                      futureItems.forEach(([slotIndex, item]) => {
                        newHeroItems.delete(slotIndex);
                        refundAmount += item.cost || 0;
                      });
                      
                      // Update hero items
                      updatedUserItems.set(hero.id, newHeroItems);
                      
                      // Update spent gold
                      const heroSpentGold = spentGold.get(hero.id) || 0;
                      const newSpentGold = Math.max(0, heroSpentGold - refundAmount);
                      updatedSpentGold.set(hero.id, newSpentGold);
                    }
                  });
                  
                  // Only update state if changes were detected
                  if (changesDetected) {
                    setUserItems(updatedUserItems);
                    setSpentGold(updatedSpentGold);
                  }
                }
                
                // Update the time point
                setCurrentTimePoint(newTimePoint);
            }}
            style={{
                width: '100%',
              height: '12px',
                borderRadius: '6px',
                marginTop: '5px', // Add some space between markers and slider
                zIndex: 5 // Keep slider above markers
            }}
          />
          </div>
          
          <span style={{ minWidth: '45px', textAlign: 'center' }}>
            {Math.floor(gameDuration / 60)}:{String(gameDuration % 60).padStart(2, '0')}
          </span>
          
          {/* Add minute control buttons */}
          <div style={{ display: 'flex', gap: '5px', marginLeft: '10px' }}>
            <button
              onClick={() => {
                // Calculate new time point (subtract 60 seconds = 1 minute)
                const newTimePoint = Math.max(0, currentTimePoint - 60);
                
                if (newTimePoint < currentTimePoint) {
                  const currentMinute = Math.floor(newTimePoint / 60);
                  
                  // Check all heroes' items for future timestamps and refund as needed
                  const allHeroes = [...radiantHeroes, ...direHeroes];
                  let updatedUserItems = new Map(userItems);
                  let updatedSpentGold = new Map(spentGold);
                  let changesDetected = false;
                  
                  allHeroes.forEach(hero => {
                    const heroItems = userItems.get(hero.id);
                    if (!heroItems || heroItems.size === 0) return;
                    
                    // Check if any items were purchased after the current time
                    const futureItems = Array.from(heroItems.entries()).filter(([_, item]) => {
                      const itemTimestamp = item.timestamp || 0;
                      return itemTimestamp > currentMinute;
                    });
                    
                    if (futureItems.length > 0) {
                      changesDetected = true;
                      const newHeroItems = new Map(heroItems);
                      let refundAmount = 0;
                      
                      // Remove future items and calculate refund
                      futureItems.forEach(([slotIndex, item]) => {
                        newHeroItems.delete(slotIndex);
                        refundAmount += item.cost || 0;
                      });
                      
                      // Update hero items
                      updatedUserItems.set(hero.id, newHeroItems);
                      
                      // Update spent gold
                      const heroSpentGold = spentGold.get(hero.id) || 0;
                      const newSpentGold = Math.max(0, heroSpentGold - refundAmount);
                      updatedSpentGold.set(hero.id, newSpentGold);
                    }
                  });
                  
                  // Only update state if changes were detected
                  if (changesDetected) {
                    setUserItems(updatedUserItems);
                    setSpentGold(updatedSpentGold);
                  }
                }
                
                // Update time point
                setCurrentTimePoint(newTimePoint);
                
                // Pause playback
                if (isPlaying) setIsPlaying(false);
              }}
              title="Go back 1 minute"
                style={{
                background: '#1a1a1a',
                border: '1px solid #444',
                borderRadius: '4px',
                color: 'white',
                padding: '3px 8px',
                cursor: 'pointer',
                fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 20C16.4183 20 20 16.4183 20 12C20 7.58172 16.4183 4 12 4C7.58172 4 4 7.58172 4 12C4 16.4183 7.58172 20 12 20Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 8L8 12L12 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M16 12H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span style={{ fontSize: '11px', marginLeft: '2px', userSelect: 'none' }}>1m</span>
            </button>
            <button
              onClick={() => {
                // Calculate new time point (add 60 seconds = 1 minute)
                const newTimePoint = Math.min(gameDuration, currentTimePoint + 60);
                
                // Update time point
                setCurrentTimePoint(newTimePoint);
                
                // Pause playback
                if (isPlaying) setIsPlaying(false);
              }}
              title="Advance 1 minute"
              style={{
                background: '#1a1a1a',
                border: '1px solid #444',
                borderRadius: '4px',
                color: 'white',
                padding: '3px 8px',
                  cursor: 'pointer',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 20C16.4183 20 20 16.4183 20 12C20 7.58172 16.4183 4 12 4C7.58172 4 4 7.58172 4 12C4 16.4183 7.58172 20 12 20Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 16L16 12L12 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M8 12H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span style={{ fontSize: '11px', marginLeft: '2px', userSelect: 'none' }}>1m</span>
            </button>
              </div>
        </div>
        
        {/* Game Objectives Timeline - REMOVED */}
      </div>

      {/* Map Component - inserted after timeline */}
      {mapComponent && (
        <div style={{ margin: '20px 0' }}>
          {mapComponent}
        </div>
      )}

      <div className="heroes-timelines" style={{ 
        display: 'flex', 
        flexDirection: 'row', 
        gap: '20px',
        justifyContent: 'space-between'
      }}>
        {/* Radiant Heroes Column */}
        <div className="radiant-heroes" style={{ flex: 1 }}>
          <h3 style={{ color: '#66bb6a', borderBottom: '1px solid #333', paddingBottom: '8px' }}>
            {radiantTeamName}
          </h3>
          {radiantHeroes.map(renderHeroTimeline)}
      </div>
        
        {/* Dire Heroes Column */}
        <div className="dire-heroes" style={{ flex: 1 }}>
          <h3 style={{ color: '#ff5252', borderBottom: '1px solid #333', paddingBottom: '8px' }}>
            {direTeamName}
          </h3>
          {direHeroes.map(renderHeroTimeline)}
      </div>
          </div>
      </div>

      {/* Bottom banner ad */}
      <div style={{ 
        margin: '20px 0 0 0', 
        width: '100%',
        background: '#1a1a1a',
        padding: '20px 0',
        borderTop: '1px solid #333',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <AdBanner 
          adSlot="7581740096" 
          adFormat="horizontal" 
          style={{ 
            width: '728px',
            height: '90px',
            margin: '0 auto',
            display: 'block'
          }} 
        />
      </div>
    </div>
  );
};

export default ItemizationSimulation;